import axios from 'axios'

// Configuración base de axios
const api = axios.create({
  baseURL: '/api', // Proxy configurado en vite.config.ts
  timeout: 600000, // 10 minutos para operaciones AI complejas (generación de fichas extensas + imágenes)
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Interceptor para añadir token de autenticación y logging
api.interceptors.request.use(
  (config) => {
    // Añadir token de autenticación si existe
    const token = localStorage.getItem('jvs_auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`)
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    console.error('[API Error]', error.response?.data || error.message)

    // Si el error es 401 (no autorizado), limpiar tokens y redirigir al login
    if (error.response?.status === 401) {
      localStorage.removeItem('jvs_auth_token')
      localStorage.removeItem('jvs_auth_user')

      // Redirigir al login si no estamos ya en /login
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

// ============================================================================
// TYPES
// ============================================================================

export interface Product {
  id?: number | null
  code: string
  reference: string
  name: string | Record<string, string>  // string from Factusol, Record from PrestaShop
  price: number
  sale_price?: number | null
  active: boolean
  quantity: number
  family_id?: string | null
  category_id?: number
  manufacturer_id?: number
  ean13?: string | null
  images: string[]
}

export interface Customer {
  id: number
  email: string
  firstname: string
  lastname: string
  active: boolean
  newsletter: boolean
  total_orders: number
  total_spent: number
  last_order_date?: string
  rfm_segment?: string
  rfm_score?: string
}

// ============================================================================
// CUSTOMER 360° PROFILE TYPES
// ============================================================================

export interface CustomerAddress {
  id: number
  alias: string
  company?: string | null
  address1: string
  address2?: string | null
  postcode: string
  city: string
  country: string
  phone?: string | null
  phone_mobile?: string | null
  vat_number?: string | null
  dni?: string | null
}

export interface CustomerOrder {
  id: number
  reference: string
  date: string
  status: string
  status_color: string
  total: number
  products_count: number
  payment: string
}

export interface CustomerProduct {
  id: number
  reference: string
  name: string
  image_url?: string | null
  times_bought: number
  total_quantity: number
  total_spent: number
  last_bought: string
}

export interface CustomerNote {
  id: number
  note_text: string
  created_by: string
  created_at: string
  pinned: boolean
}

export interface Customer360Profile {
  id: number
  email: string
  firstname: string
  lastname: string
  company?: string | null
  active: boolean
  newsletter: boolean
  date_add: string
  birthday?: string | null
  group_id: number
  group_name: string
  addresses: CustomerAddress[]
  rfm_segment?: string | null
  rfm_segment_description?: string | null
  recency_days?: number | null
  frequency?: number | null
  monetary_total?: number | null
  spending_trend?: number | null
  total_orders: number
  total_spent: number
  average_order: number
  first_order_date?: string | null
  last_order_date?: string | null
  days_since_last_order?: number | null
}

export interface CustomerOrdersResponse {
  orders: CustomerOrder[]
  total: number
  total_spent: number
}

export interface CustomerProductsResponse {
  products: CustomerProduct[]
  total: number
}

export interface CustomerNotesResponse {
  notes: CustomerNote[]
  total: number
}

export interface CarrierStatistics {
  orders_previous_business_day: number
  avg_orders_last_week: number
  avg_orders_per_business_day_last_month: number
}

export interface Carrier {
  id: number
  name: string
  active: boolean
  delay: string
  schedule?: CarrierSchedule
  is_auto_managed: boolean
  statistics: CarrierStatistics
}

export interface CarrierSchedule {
  inactive_start_time: string
  inactive_end_time: string
  always_active: boolean
}

export interface ProductCreateRequest {
  urls: string[]
  text_content: string
  category_id?: number
  reference?: string
  cost_price?: number
  primary_language?: string
  preview_only: boolean
  warranty_duration?: string
  images?: File[]
  image_urls?: string[] // URLs de imágenes del scraping

  // Factusol fields
  factusol_create?: boolean
  factusol_family?: string
  factusol_supplier?: string
  factusol_supplier_ref?: string

  // WebSocket client ID for progress tracking
  client_id?: string

  // OpenAI model selection (gpt-4o-mini or gpt-4o)
  ai_model?: string

  // Force create even if reference already exists
  force_create?: boolean
}

export interface OpenAIUsage {
  num_calls: number
  total_prompt_tokens: number
  total_completion_tokens: number
  total_tokens: number
  total_cost_usd: number
  total_cost_eur: number
  calls?: Array<{
    prompt_tokens: number
    completion_tokens: number
    total_tokens: number
    model: string
    cost_usd: number
    cost_eur: number
  }>
}

export interface ExistingProductInfo {
  id_product: number
  active: boolean
  reference: string
  name: string
}

export interface ProductCreateResponse {
  status: string  // "success", "preview", "error", or "duplicate_warning"
  product_id?: number
  reference?: string
  preview?: any
  extracted_data?: any
  warnings: string[]
  message?: string  // Error or warning message
  execution_time: number
  timestamp: string
  factusol_created?: boolean
  openai_usage?: OpenAIUsage
  existing_product?: ExistingProductInfo  // Info when status=duplicate_warning
  ai_readiness?: AiReadiness | null  // score de la capa AI-ready (GEO + SEO)
}

// ============================================================================
// PRODUCT AI API
// ============================================================================

export const productAI = {
  /**
   * Crea un producto con IA
   * Siempre usa FormData para compatibilidad con el backend
   */
  createProduct: async (data: ProductCreateRequest): Promise<ProductCreateResponse> => {
    const formData = new FormData()

    // URLs - enviar como JSON string para que el backend pueda parsear múltiples
    formData.append('urls', JSON.stringify(data.urls))
    formData.append('text_content', data.text_content || '')

    if (data.category_id) formData.append('category_id', data.category_id.toString())
    if (data.reference) formData.append('reference', data.reference)
    if (data.cost_price) formData.append('cost_price', data.cost_price.toString())
    formData.append('primary_language', data.primary_language || 'es')
    formData.append('preview_only', data.preview_only.toString())
    if (data.warranty_duration) formData.append('warranty_duration', data.warranty_duration)

    // Factusol fields
    formData.append('factusol_create', data.factusol_create ? 'true' : 'false')
    if (data.factusol_family) formData.append('factusol_family', data.factusol_family)
    if (data.factusol_supplier) formData.append('factusol_supplier', data.factusol_supplier)
    if (data.factusol_supplier_ref) formData.append('factusol_supplier_ref', data.factusol_supplier_ref)

    // WebSocket client ID for real-time progress
    if (data.client_id) formData.append('client_id', data.client_id)

    // OpenAI model selection
    if (data.ai_model) formData.append('ai_model', data.ai_model)

    // Force create even if reference exists
    formData.append('force_create', data.force_create ? 'true' : 'false')

    // Añadir imágenes subidas si existen
    if (data.images && data.images.length > 0) {
      data.images.forEach((image) => {
        formData.append('images', image)
      })
    }

    // Añadir URLs de imágenes del scraping seleccionadas
    if (data.image_urls && data.image_urls.length > 0) {
      formData.append('image_urls', JSON.stringify(data.image_urls))
    }

    const response = await api.post('/product-ai/create', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  /**
   * Health check del sistema AI
   */
  health: async () => {
    const response = await api.get('/product-ai/health')
    return response.data
  },

  /**
   * Lista modelos disponibles
   */
  listModels: async () => {
    const response = await api.get('/product-ai/models')
    return response.data
  },

  /**
   * Obtiene familias de Factusol
   */
  getFactusolFamilies: async () => {
    const response = await api.get('/product-ai/factusol/families')
    return response.data
  },

  /**
   * Obtiene proveedores de Factusol
   */
  getFactusolSuppliers: async () => {
    const response = await api.get('/product-ai/factusol/suppliers')
    return response.data
  },

  /**
   * Obtiene la siguiente referencia secuencial para un prefijo dado.
   * Si el prefijo es "BAT-" y existen BAT-001, BAT-015, devolverá BAT-016.
   */
  getNextSequentialReference: async (prefix: string): Promise<{
    next_reference: string
    highest_found: string | null
    count: number
  }> => {
    const response = await api.get('/product-ai/factusol/next-reference', {
      params: { prefix }
    })
    return response.data
  },

  /**
   * Añade un trabajo a la cola de creación de productos
   */
  addToQueue: async (data: {
    name: string
    reference?: string
    cost_price?: number
    text_content?: string
    urls?: string[]
    image_urls?: string[]
    category_id?: number
    warranty_duration?: string
    factusol_family?: string
    factusol_supplier?: string
    factusol_supplier_ref?: string
    supplier_catalog_id?: number
    supplier_product_id?: number
    auto_approve?: boolean
    ai_model?: string
  }): Promise<{ success: boolean; job_id: number; message: string }> => {
    const response = await api.post('/product-ai/queue/add', data)
    return response.data
  },

  /**
   * Obtiene estadísticas de la cola
   */
  getQueueStats: async (): Promise<{
    pending: number
    processing: number
    completed: number
    failed: number
    total: number
  }> => {
    const response = await api.get('/product-ai/queue/stats')
    return response.data
  },

  /**
   * Obtiene trabajos de la cola
   */
  getQueueJobs: async (limit: number = 50): Promise<{ jobs: any[] }> => {
    const response = await api.get('/product-ai/queue/jobs', { params: { limit } })
    return response.data
  },

  /**
   * Obtiene un trabajo específico
   */
  getQueueJob: async (jobId: number): Promise<any> => {
    const response = await api.get(`/product-ai/queue/job/${jobId}`)
    return response.data
  },

  /**
   * Elimina un trabajo de la cola
   */
  deleteQueueJob: async (jobId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/product-ai/queue/job/${jobId}`)
    return response.data
  },

  /**
   * Reintenta un trabajo fallido
   */
  retryQueueJob: async (jobId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/product-ai/queue/job/${jobId}/retry`)
    return response.data
  },

  /**
   * Pausa el procesamiento de la cola
   */
  pauseQueue: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/product-ai/queue/pause')
    return response.data
  },

  /**
   * Reanuda el procesamiento de la cola
   */
  resumeQueue: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/product-ai/queue/resume')
    return response.data
  },

  /**
   * Obtiene el estado de pausa de la cola
   */
  getQueuePauseStatus: async (): Promise<{ is_paused: boolean; paused_at: string | null }> => {
    const response = await api.get('/product-ai/queue/pause-status')
    return response.data
  },

  /**
   * Añade múltiples productos a la cola de creación
   */
  addBatchToQueue: async (data: {
    supplier_product_ids: number[]
    category_id: number
    reference_prefix: string
    warranty_duration?: string
    ai_model?: string
    auto_approve?: boolean
    factusol_create?: boolean
    factusol_family?: string
    factusol_supplier?: string
  }): Promise<{
    success: boolean
    jobs_created: number
    jobs_skipped: number
    jobs: Array<{
      job_id: number
      reference: string
      name: string
      supplier_product_id: number
    }>
    skipped: Array<{
      supplier_product_id: number
      name?: string
      reason: string
    }>
    message: string
  }> => {
    const response = await api.post('/product-ai/queue/add-batch', data)
    return response.data
  },
}

// ============================================================================
// PRODUCTS API
// ============================================================================

export const products = {
  /**
   * Lista productos con paginación y filtros
   */
  list: async (params: {
    page?: number
    page_size?: number
    search?: string
    active?: boolean
    category_id?: number
    low_stock?: boolean
  }) => {
    const response = await api.get('/products/', { params })
    return response.data
  },

  /**
   * Obtiene un producto por ID
   */
  get: async (id: number): Promise<Product> => {
    const response = await api.get(`/products/${id}`)
    return response.data
  },

  /**
   * Sincroniza productos desde PrestaShop
   */
  sync: async (force_full_sync: boolean = false) => {
    const response = await api.post('/products/sync', { force_full_sync })
    return response.data
  },

  /**
   * Actualiza un producto
   */
  update: async (id: number, data: Partial<Product>) => {
    const response = await api.patch(`/products/${id}`, data)
    return response.data
  },

  /**
   * Elimina un producto
   */
  delete: async (id: number) => {
    const response = await api.delete(`/products/${id}`)
    return response.data
  },

  /**
   * Lista categorías (API REST - deprecated)
   */
  listCategories: async () => {
    const response = await api.get('/products/categories/list')
    return response.data
  },

  /**
   * Lista categorías desde MySQL directamente (más fiable)
   */
  listCategoriesFromDB: async (): Promise<{
    categories: Array<{
      id: number
      id_parent: number
      level_depth: number
      active: boolean
      name: string
      display_name: string
      link_rewrite: string
    }>
    total: number
  }> => {
    const response = await api.get('/products/categories/db')
    return response.data
  },

  /**
   * Lista fabricantes
   */
  listManufacturers: async () => {
    const response = await api.get('/products/manufacturers/list')
    return response.data
  },
}

// ============================================================================
// CUSTOMERS API
// ============================================================================

export const customers = {
  /**
   * Lista clientes con paginación y filtros
   */
  list: async (params: {
    page?: number
    page_size?: number
    search?: string
    active?: boolean
    newsletter?: boolean
    rfm_segment?: string
  }) => {
    const response = await api.get('/customers/', { params })
    return response.data
  },

  /**
   * Obtiene un cliente por ID
   */
  get: async (id: number): Promise<Customer> => {
    const response = await api.get(`/customers/${id}`)
    return response.data
  },

  /**
   * Ejecuta análisis RFM
   */
  analyzeRFM: async (force_refresh: boolean = false, customer_group_id?: number) => {
    const response = await api.post('/customers/rfm/analyze', { force_refresh, customer_group_id })
    return response.data
  },

  /**
   * Obtiene segmentos RFM
   */
  getRFMSegments: async (customer_group_id?: number) => {
    const response = await api.get('/customers/rfm/segments', {
      params: customer_group_id ? { customer_group_id } : {}
    })
    return response.data
  },

  /**
   * Envía campaña de email
   */
  sendCampaign: async (data: {
    segment: string
    subject: string
    html_content: string
    preview_only: boolean
  }) => {
    const response = await api.post('/customers/campaigns/send', data)
    return response.data
  },

  /**
   * Obtiene historial de campañas
   */
  getCampaignHistory: async (limit: number = 20) => {
    const response = await api.get('/customers/campaigns/history', { params: { limit } })
    return response.data
  },

  /**
   * Obtiene estadísticas de clientes
   */
  getStats: async () => {
    const response = await api.get('/customers/stats/summary')
    return response.data
  },

  /**
   * Obtiene estadísticas del dashboard de clientes
   */
  getDashboardStats: async (periodDays: number = 30): Promise<{
    clientes_total: number
    clientes_nuevos_mes: number
    clientes_nuevos_semana: number
    clv_medio: number
    tasa_retencion: number
    clientes_en_riesgo: number
    clientes_perdidos: number
    revenue_total: number
    revenue_por_segmento: Record<string, number>
    pedidos_mes: number
    ticket_medio: number
  }> => {
    const response = await api.get('/customers/stats/dashboard', { params: { period_days: periodDays } })
    return response.data
  },

  /**
   * Obtiene tendencias mensuales de clientes y ventas
   */
  getTrends: async (months: number = 12): Promise<Array<{
    mes: string
    clientes_nuevos: number
    clientes_perdidos: number
    revenue: number
    pedidos: number
  }>> => {
    const response = await api.get('/customers/stats/trends', { params: { months } })
    return response.data
  },

  // ============================================================================
  // FICHA CLIENTE 360°
  // ============================================================================

  /**
   * Obtiene el perfil completo 360° de un cliente
   */
  getProfile360: async (email: string): Promise<Customer360Profile> => {
    const response = await api.get(`/customers/profile/${encodeURIComponent(email)}/360`)
    return response.data
  },

  /**
   * Obtiene el historial de pedidos de un cliente
   */
  getCustomerOrders: async (email: string, limit: number = 50, offset: number = 0): Promise<CustomerOrdersResponse> => {
    const response = await api.get(`/customers/profile/${encodeURIComponent(email)}/orders`, {
      params: { limit, offset }
    })
    return response.data
  },

  /**
   * Obtiene los productos más comprados por un cliente
   */
  getCustomerProducts: async (email: string, limit: number = 20): Promise<CustomerProductsResponse> => {
    const response = await api.get(`/customers/profile/${encodeURIComponent(email)}/products`, {
      params: { limit }
    })
    return response.data
  },

  /**
   * Obtiene las notas de un cliente
   */
  getCustomerNotes: async (email: string): Promise<CustomerNotesResponse> => {
    const response = await api.get(`/customers/profile/${encodeURIComponent(email)}/notes`)
    return response.data
  },

  /**
   * Crea una nueva nota para un cliente
   */
  createCustomerNote: async (email: string, data: { note_text: string; created_by?: string; pinned?: boolean }): Promise<CustomerNote> => {
    const response = await api.post(`/customers/profile/${encodeURIComponent(email)}/notes`, data)
    return response.data
  },

  /**
   * Elimina una nota de un cliente
   */
  deleteCustomerNote: async (email: string, noteId: number): Promise<void> => {
    await api.delete(`/customers/profile/${encodeURIComponent(email)}/notes/${noteId}`)
  },

  /**
   * Cambia el estado de fijado de una nota
   */
  toggleNotePin: async (email: string, noteId: number): Promise<{ success: boolean; pinned: boolean }> => {
    const response = await api.put(`/customers/profile/${encodeURIComponent(email)}/notes/${noteId}/pin`)
    return response.data
  },
}

// ============================================================================
// CUSTOMER ALERTS API
// ============================================================================

export interface CustomerAlert {
  id: number
  customer_email: string
  customer_name: string | null
  customer_group: string | null
  alert_type: 'vip_inactive' | 'spending_decline' | 'churn_risk'
  severity: 'high' | 'medium' | 'low'
  title: string
  description: string | null
  metrics: Record<string, any> | null
  status: 'pending' | 'reviewed' | 'actioned' | 'dismissed'
  created_at: string
  reviewed_at: string | null
  reviewed_by: string | null
  action_taken: string | null
}

export interface AlertStats {
  total: number
  pending: number
  reviewed: number
  actioned: number
  dismissed: number
  by_type: Record<string, number>
  by_severity: Record<string, number>
}

export const customerAlerts = {
  /**
   * Lista alertas con filtros
   */
  list: async (params?: {
    status?: string
    alert_type?: string
    severity?: string
    limit?: number
  }): Promise<{ alerts: CustomerAlert[]; total: number }> => {
    const response = await api.get('/customer-alerts/', { params })
    return response.data
  },

  /**
   * Obtiene estadísticas de alertas
   */
  getStats: async (): Promise<AlertStats> => {
    const response = await api.get('/customer-alerts/stats')
    return response.data
  },

  /**
   * Ejecuta detección de alertas
   */
  runDetection: async (): Promise<{
    total_alerts: number
    new_alerts: number
    vip_inactive_count: number
    spending_decline_count: number
    churn_risk_count: number
    execution_time: number
  }> => {
    const response = await api.post('/customer-alerts/detect')
    return response.data
  },

  /**
   * Actualiza estado de una alerta
   */
  update: async (alertId: number, data: {
    status: string
    reviewed_by?: string
    action_taken?: string
  }): Promise<{ success: boolean; message: string }> => {
    const response = await api.put(`/customer-alerts/${alertId}`, data)
    return response.data
  },

  /**
   * Descarta una alerta
   */
  dismiss: async (alertId: number, reviewedBy: string = 'user'): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/customer-alerts/${alertId}/dismiss`, null, {
      params: { reviewed_by: reviewedBy }
    })
    return response.data
  },

  /**
   * Marca alerta como accionada
   */
  markActioned: async (alertId: number, action: string, reviewedBy: string = 'user'): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/customer-alerts/${alertId}/action`, null, {
      params: { action, reviewed_by: reviewedBy }
    })
    return response.data
  },
}

// ============================================================================
// GROUP PROMOTIONS API
// ============================================================================

export interface PromotionRule {
  id: number
  name: string
  description?: string | null
  from_group_id: number
  from_group_name: string
  to_group_id: number
  to_group_name: string
  min_orders: number
  min_revenue: number
  min_revenue_period_months: number
  min_avg_order_value: number
  auto_apply: boolean
  notify_customer: boolean
  active: boolean
  created_at: string
  updated_at?: string | null
}

export interface PromotionCandidate {
  email: string
  name: string
  company?: string | null
  current_group_id: number
  current_group_name: string
  total_orders: number
  total_revenue: number
  avg_order_value: number
  last_order_date?: string | null
  rule_id: number
  rule_name: string
  target_group_id: number
  target_group_name: string
}

export interface PromotionHistory {
  id: number
  customer_email: string
  customer_name?: string | null
  rule_id?: number | null
  rule_name?: string | null
  from_group_id: number
  from_group_name: string
  to_group_id: number
  to_group_name: string
  reason?: string | null
  applied_at: string
  applied_by: string
}

export const groupPromotions = {
  /**
   * Lista reglas de promoción
   */
  getRules: async (activeOnly: boolean = true): Promise<PromotionRule[]> => {
    const response = await api.get('/group-promotions/rules', { params: { active_only: activeOnly } })
    return response.data
  },

  /**
   * Obtiene una regla por ID
   */
  getRule: async (ruleId: number): Promise<PromotionRule> => {
    const response = await api.get(`/group-promotions/rules/${ruleId}`)
    return response.data
  },

  /**
   * Crea una nueva regla
   */
  createRule: async (data: Partial<PromotionRule>): Promise<{ success: boolean; rule_id: number; message: string }> => {
    const response = await api.post('/group-promotions/rules', data)
    return response.data
  },

  /**
   * Actualiza una regla
   */
  updateRule: async (ruleId: number, data: Partial<PromotionRule>): Promise<{ success: boolean; message: string }> => {
    const response = await api.put(`/group-promotions/rules/${ruleId}`, data)
    return response.data
  },

  /**
   * Elimina una regla
   */
  deleteRule: async (ruleId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/group-promotions/rules/${ruleId}`)
    return response.data
  },

  /**
   * Lista candidatos a promoción
   */
  getCandidates: async (ruleId?: number): Promise<PromotionCandidate[]> => {
    const response = await api.get('/group-promotions/candidates', { params: ruleId ? { rule_id: ruleId } : {} })
    return response.data
  },

  /**
   * Aplica una promoción
   */
  applyPromotion: async (email: string, ruleId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/group-promotions/apply', { email, rule_id: ruleId })
    return response.data
  },

  /**
   * Ejecuta promociones automáticas
   */
  runAutoPromotions: async (): Promise<{ success: boolean; promotions_applied: number; details: any[] }> => {
    const response = await api.post('/group-promotions/apply/auto')
    return response.data
  },

  /**
   * Obtiene historial de promociones
   */
  getHistory: async (email?: string, limit: number = 50): Promise<PromotionHistory[]> => {
    const response = await api.get('/group-promotions/history', { params: { email, limit } })
    return response.data
  },

  /**
   * Lista grupos disponibles
   */
  getGroups: async (): Promise<{ groups: Array<{ id: number; name: string; label: string }> }> => {
    const response = await api.get('/group-promotions/groups')
    return response.data
  },
}

// ============================================================================
// ADVANCED SEARCH API
// ============================================================================

export interface AdvancedSearchFilters {
  countries?: string[]
  min_revenue?: number
  max_revenue?: number
  min_orders?: number
  max_orders?: number
  last_order_days_min?: number
  last_order_days_max?: number
  group_ids?: number[]
  rfm_segments?: string[]
  active?: boolean
  newsletter?: boolean
  limit?: number
}

export interface AdvancedSearchResult {
  email: string
  name: string
  company?: string | null
  country?: string | null
  group_id: number
  group_name: string
  total_orders: number
  total_revenue: number
  avg_order_value: number
  last_order_date?: string | null
  days_since_last_order?: number | null
  rfm_segment?: string | null
}

export const advancedSearch = {
  /**
   * Búsqueda avanzada de clientes
   */
  search: async (filters: AdvancedSearchFilters): Promise<{
    customers: AdvancedSearchResult[]
    total: number
    filters_applied: Record<string, any>
  }> => {
    const response = await api.post('/customers/search/advanced', filters)
    return response.data
  },
}

// ============================================================================
// CARRIERS API
// ============================================================================

export const carriers = {
  /**
   * Lista transportistas
   * @param includeStats - Si true, incluye estadísticas (más lento). Default: true para compatibilidad
   */
  list: async (includeStats: boolean = true) => {
    const response = await api.get('/carriers/', { params: { include_stats: includeStats } })
    return response.data
  },

  /**
   * Obtiene un transportista por ID
   */
  get: async (id: number): Promise<Carrier> => {
    const response = await api.get(`/carriers/${id}`)
    return response.data
  },

  /**
   * Actualiza un transportista
   */
  update: async (id: number, data: Partial<Carrier>) => {
    const response = await api.patch(`/carriers/${id}`, data)
    return response.data
  },

  /**
   * Crea un horario para transportista
   */
  createSchedule: async (data: {
    carrier_id: number
    day_of_week: number
    start_time: string
    end_time: string
  }) => {
    const response = await api.post('/carriers/schedules', data)
    return response.data
  },

  /**
   * Elimina un horario
   */
  deleteSchedule: async (id: number) => {
    const response = await api.delete(`/carriers/schedules/${id}`)
    return response.data
  },

  /**
   * Ejecuta activación automática
   */
  runAutoActivation: async () => {
    const response = await api.post('/carriers/auto-activation/run')
    return response.data
  },

  /**
   * Obtiene estado de activación automática
   */
  getAutoActivationStatus: async () => {
    const response = await api.get('/carriers/auto-activation/status')
    return response.data
  },

  /**
   * Obtiene logs de activación
   */
  getActivationLogs: async (limit: number = 50) => {
    const response = await api.get('/carriers/auto-activation/logs', { params: { limit } })
    return response.data
  },

  /**
   * Habilita activación automática
   */
  enableAutoActivation: async () => {
    const response = await api.post('/carriers/auto-activation/enable')
    return response.data
  },

  /**
   * Deshabilita activación automática
   */
  disableAutoActivation: async () => {
    const response = await api.post('/carriers/auto-activation/disable')
    return response.data
  },

  /**
   * Actualiza el flag de gestión automática de un transportista
   */
  updateAutoManaged: async (id: number, isAutoManaged: boolean) => {
    const response = await api.patch(`/carriers/${id}/auto-managed`, null, {
      params: { is_auto_managed: isAutoManaged }
    })
    return response.data
  },

  /**
   * Actualiza el horario de un transportista
   */
  updateSchedule: async (id: number, data: {
    inactive_start_time?: string
    inactive_end_time?: string
    always_active?: boolean
  }) => {
    const response = await api.put(`/carriers/${id}/schedule`, data)
    return response.data
  },

  /**
   * Obtiene estadísticas de todos los transportistas
   * Diseñado para carga asíncrona en segundo plano
   */
  getStatistics: async (): Promise<{ statistics: Record<number, CarrierStatistics> }> => {
    const response = await api.get('/carriers/statistics')
    return response.data
  },
}

export default api
// ============================================================================
// FACTUSOL API
// ============================================================================

export interface FactusolStatus {
  connected: boolean;
  token?: string;
  error?: string;
}

export interface FactusolSyncResponse {
  status: string;
  reference: string;
  synced_data: {
    wholesale_price: number;
    price: number;
    quantity: number;
    vat_price: number;
  };
}

export const checkFactusolStatus = async (): Promise<FactusolStatus> => {
  const response = await api.get('/factusol/status');
  return response.data;
};

export const syncFactusolProduct = async (reference: string): Promise<FactusolSyncResponse> => {
  const response = await api.post(`/factusol/sync/${reference}`);
  return response.data;
};

export interface PaymentMethod {
  code: string;
  name: string;
}

export const getFactusolPaymentMethods = async (): Promise<PaymentMethod[]> => {
  const response = await api.get('/factusol/payment-methods');
  return response.data.payment_methods;
};

// ============================================================================
// SYNC API - Factusol <-> PrestaShop Synchronization
// ============================================================================

export interface TariffMapping {
  tariff_1: { group_id: number | null; is_principal: boolean; name: string };
  tariff_2: { group_id: number | null; is_principal: boolean; name: string };
  tariff_3: { group_id: number | null; is_principal: boolean; name: string };
}

export interface CustomerGroup {
  id: number;
  name: string;
}

export interface FactusolFamily {
  code: string;
  name: string;
}

export interface FamilySyncConfig {
  [familyCode: string]: {
    sync_stock: boolean;
    sync_prices: boolean;
  };
}

export interface SyncProgress {
  total: number;
  processed: number;
  success: number;
  errors: number;
  current_reference: string;
  error_details: { reference: string; error: string }[];
  status: 'idle' | 'running' | 'completed' | 'cancelled' | 'error';
  percent: number;
  start_time: string | null;
  end_time: string | null;
}

export interface ProductComparison {
  reference: string;
  found_in_factusol: boolean;
  found_in_prestashop: boolean;
  factusol?: {
    name: string;
    stock: number;
    tariffs: Record<string, number>;
  };
  prestashop?: {
    products_count: number;
    products: any[];
    prices: any;
  };
  differences: {
    field: string;
    name?: string;
    group_id?: number;
    prestashop: number;
    factusol: number;
    action: string;
  }[];
}

export interface SyncHistoryItem {
  id: number;
  sync_type: string;
  reference: string;
  status: string;
  message: string;
  created_at: string;
}

export const syncApi = {
  // Configuration
  getTariffMapping: async (): Promise<{ success: boolean; mapping: TariffMapping }> => {
    const response = await api.get('/sync/config/tariffs');
    return response.data;
  },

  saveTariffMapping: async (mapping: TariffMapping): Promise<{ success: boolean }> => {
    const response = await api.post('/sync/config/tariffs', mapping);
    return response.data;
  },

  getCustomerGroups: async (): Promise<{ success: boolean; groups: CustomerGroup[] }> => {
    const response = await api.get('/sync/config/groups');
    return response.data;
  },

  getFamilySyncConfig: async (): Promise<{ success: boolean; config: FamilySyncConfig }> => {
    const response = await api.get('/sync/config/families');
    return response.data;
  },

  saveFamilySyncConfig: async (config: FamilySyncConfig): Promise<{ success: boolean }> => {
    const response = await api.post('/sync/config/families', config);
    return response.data;
  },

  // Factusol data
  getFamilies: async (): Promise<{ success: boolean; families: FactusolFamily[] }> => {
    const response = await api.get('/sync/families');
    return response.data;
  },

  // Search & Compare
  searchProducts: async (q: string): Promise<{ success: boolean; products: { reference: string; name: string }[] }> => {
    const response = await api.get('/sync/search', { params: { q } });
    return response.data;
  },

  getComparison: async (reference: string): Promise<{ success: boolean; comparison: ProductComparison }> => {
    const response = await api.get(`/sync/compare/${reference}`);
    return response.data;
  },

  // Sync operations
  syncProduct: async (reference: string, options?: { sync_stock?: boolean; sync_prices?: boolean }): Promise<any> => {
    const response = await api.post(`/sync/product/${reference}`, options || {});
    return response.data;
  },

  syncByFamily: async (family_code: string, sync_stock?: boolean, sync_prices?: boolean): Promise<any> => {
    const response = await api.post('/sync/family', { family_code, sync_stock, sync_prices });
    return response.data;
  },

  syncAll: async (sync_stock?: boolean, sync_prices?: boolean): Promise<any> => {
    const response = await api.post('/sync/all', { sync_stock, sync_prices });
    return response.data;
  },

  // Progress & Control
  getProgress: async (): Promise<{ success: boolean; progress: SyncProgress }> => {
    const response = await api.get('/sync/progress');
    return response.data;
  },

  cancelSync: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/sync/cancel');
    return response.data;
  },

  // History
  getSyncHistory: async (limit?: number): Promise<{ success: boolean; history: SyncHistoryItem[] }> => {
    const response = await api.get('/sync/history', { params: { limit } });
    return response.data;
  },
};

// ============================================================================
// INVOICES API - Factusol PDF Synchronization
// ============================================================================

export interface InvoiceStats {
  year: number;
  total_invoices: number;
  published: number;
  pending: number;
  emails_sent: number;
  publish_rate: number;
  pending_files?: number;
}

export interface PendingInvoice {
  serie: string;
  codigo: string;
  reffac: string;
  order_id: number | null;
  fecha: string | null;
  total: number;
}

export interface PublishedInvoice {
  serie: string;
  codigo: string;
  order_id: number;
  filename: string;
  email_sent: boolean;
  email_sent_date: string | null;
  published_date: string | null;
  invoice_date: string | null;
  total: number;
}

export interface PendingFile {
  filename: string;
  serie: string | null;
  codigo: string | null;
  error?: string;
}

export interface InvoiceSyncHistoryItem {
  id: number;
  action: string;
  serie: string;
  codigo: string;
  order_id: number;
  status: string;
  message: string;
  created_at: string;
}

// Email configuration types
export interface EmailSettings {
  attach_pdf: boolean;
  from_email: string;
  from_name: string;
}

export interface EmailTemplate {
  lang_iso: string;
  lang_name: string;
  subject: string;
  body: string;
  updated_at?: string;
}

export interface EmailVariable {
  name: string;
  description: string;
}

export interface AvailableLanguage {
  id: number;
  name: string;
  iso_code: string;
}

export interface EmailConfig {
  settings: EmailSettings;
  templates: EmailTemplate[];
  available_languages: AvailableLanguage[];
  available_variables: EmailVariable[];
}

export const invoicesApi = {
  // Statistics
  getStats: async (): Promise<{ success: boolean; stats: InvoiceStats }> => {
    const response = await api.get('/invoices/stats');
    return response.data;
  },

  // Lists
  getPendingInvoices: async (limit?: number): Promise<{ success: boolean; invoices: PendingInvoice[] }> => {
    const response = await api.get('/invoices/pending', { params: { limit } });
    return response.data;
  },

  getPendingFiles: async (): Promise<{ success: boolean; files: PendingFile[]; total: number }> => {
    const response = await api.get('/invoices/pending-files');
    return response.data;
  },

  getPublishedInvoices: async (limit?: number): Promise<{ success: boolean; invoices: PublishedInvoice[] }> => {
    const response = await api.get('/invoices/published', { params: { limit } });
    return response.data;
  },

  getHistory: async (limit?: number): Promise<{ success: boolean; history: InvoiceSyncHistoryItem[] }> => {
    const response = await api.get('/invoices/history', { params: { limit } });
    return response.data;
  },

  // Email Configuration
  getEmailConfig: async (): Promise<{ success: boolean; config: EmailConfig }> => {
    const response = await api.get('/invoices/email-config');
    return response.data;
  },

  updateEmailSettings: async (settings: EmailSettings): Promise<{ success: boolean; settings: EmailSettings }> => {
    const response = await api.put('/invoices/email-config/settings', settings);
    return response.data;
  },

  updateEmailTemplate: async (langIso: string, template: { subject: string; body: string }): Promise<{ success: boolean; template: EmailTemplate }> => {
    const response = await api.put(`/invoices/email-config/template/${langIso}`, template);
    return response.data;
  },

  resetEmailTemplate: async (langIso: string): Promise<{ success: boolean; template: EmailTemplate }> => {
    const response = await api.post(`/invoices/email-config/template/${langIso}/reset`);
    return response.data;
  },

  // Check pending invoices from Factusol
  checkPendingInvoices: async (fullSync: boolean = false): Promise<{ success: boolean; message: string; status: string; mode: string }> => {
    const response = await api.post('/invoices/check-pending', { full_sync: fullSync });
    return response.data;
  },

  getCheckPendingStatus: async (): Promise<{
    success: boolean;
    in_progress: boolean;
    last_sync_time: string | null;
    stats: {
      total_fetched: number;
      inserted: number;
      updated: number;
      errors: number;
      last_error: string | null;
      start_time?: string;
      end_time?: string;
      mode?: string;
      min_codfac?: number;
    };
  }> => {
    const response = await api.get('/invoices/check-pending/status');
    return response.data;
  },

  // Upload single PDF (designed for sequential uploads) - LEGACY
  uploadSinglePdf: async (file: File): Promise<{
    success: boolean;
    filename: string;
    serie?: string;
    codigo?: string;
    size?: number;
    reason?: string;
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/invoices/upload-pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000, // 1 minuto por archivo individual
    });
    return response.data;
  },

  // Upload and process PDF in one step (NEW - recommended)
  uploadAndProcessPdf: async (file: File): Promise<{
    success: boolean;
    filename: string;
    serie?: string;
    codigo?: string;
    order_id?: number;
    email_sent?: boolean;
    email_to?: string;
    error?: string;
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/invoices/upload-and-process', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000, // 60 segundos (SFTP + email)
    });
    return response.data;
  },
};

// ============================================================================
// ALERTS API - System Alerts
// ============================================================================

export interface AlertDetail {
  serie: string;
  codigo: string;
  order_id: number | null;
  order_reference: string | null;
  fecha: string | null;
  days_old: number;
  total: number;
}

export interface Alert {
  id: string;
  type: 'warning' | 'error' | 'info';
  severity: 'high' | 'medium' | 'low';
  title: string;
  message: string;
  count?: number;
  details?: AlertDetail[];
  link?: string;
  link_text?: string;
}

export const alertsApi = {
  getAlerts: async (): Promise<{ success: boolean; alerts: Alert[]; count: number }> => {
    const response = await api.get('/alerts/');
    return response.data;
  },
};

// ============================================================================
// SCHEDULER API - Scheduled Tasks Management
// ============================================================================

export interface ScheduledTask {
  task_id: string;
  name: string;
  description: string;
  enabled: boolean;
  execution_times: string[];  // Array of "HH:MM" in Madrid timezone
  executions_per_day: number;
  last_run: string | null;
  last_status: string | null;
  last_message: string | null;
  next_run?: string;
  interval_minutes?: number | null;  // If set, task runs on interval instead of fixed times
  day_of_week?: string;  // e.g., "mon,tue,wed", "sun", "*" for all days
  category?: string;  // Category ID for grouping
}

export interface TaskExecutionLog {
  id: number;
  task_id: string;
  task_name: string;
  started_at: string;
  finished_at: string | null;
  duration_seconds: number | null;
  status: string;
  message: string | null;
}

export interface TaskUpdateRequest {
  enabled: boolean;
  execution_times: string[];
  day_of_week?: string;  // e.g., "mon,wed,fri" or "*" for all days
  name?: string;  // Custom task name
  description?: string;  // Custom task description
}

export interface TaskCategory {
  name: string;
  description: string;
  icon: string;
  color: string;
}

export interface TaskSummary {
  total: number;
  enabled: number;
  disabled: number;
  success: number;
  error: number;
  never_run: number;
  by_category: Record<string, number>;
  errors_by_category: Record<string, number>;
}

export interface GroupedTasks {
  [categoryId: string]: {
    info: TaskCategory;
    tasks: ScheduledTask[];
  };
}

export const schedulerApi = {
  getTasks: async (): Promise<{ success: boolean; tasks: ScheduledTask[]; timezone: string }> => {
    const response = await api.get('/scheduler/');
    return response.data;
  },

  getTask: async (taskId: string): Promise<{ success: boolean; task: ScheduledTask; timezone: string }> => {
    const response = await api.get(`/scheduler/${taskId}`);
    return response.data;
  },

  updateTask: async (taskId: string, data: TaskUpdateRequest): Promise<{ success: boolean; task: ScheduledTask; message: string }> => {
    const response = await api.put(`/scheduler/${taskId}`, data);
    return response.data;
  },

  runTask: async (taskId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/scheduler/${taskId}/run`);
    return response.data;
  },

  getHistory: async (limit?: number): Promise<{ success: boolean; history: TaskExecutionLog[]; timezone: string }> => {
    const response = await api.get('/scheduler/history', { params: { limit } });
    return response.data;
  },

  getTaskHistory: async (taskId: string, limit?: number): Promise<{ success: boolean; task_id: string; history: TaskExecutionLog[]; timezone: string }> => {
    const response = await api.get(`/scheduler/history/${taskId}`, { params: { limit } });
    return response.data;
  },

  getCategories: async (): Promise<{ success: boolean; categories: Record<string, TaskCategory> }> => {
    const response = await api.get('/scheduler/categories');
    return response.data;
  },

  getSummary: async (): Promise<{ success: boolean } & TaskSummary> => {
    const response = await api.get('/scheduler/summary');
    return response.data;
  },

  getGroupedTasks: async (): Promise<{ success: boolean; grouped: GroupedTasks; timezone: string }> => {
    const response = await api.get('/scheduler/grouped');
    return response.data;
  },
};

// ============================================================================
// PRODUCT STATS API - Factusol Historical Data
// ============================================================================

export interface PriceHistoryItem {
  ejercicio: string;
  cost_price: number | null;
  sale_price_1: number | null;
  sale_price_2: number | null;
  sale_price_3: number | null;
  margin_percent: number | null;
}

export interface SalesYearItem {
  ejercicio: string;
  total_quantity: number;
  total_revenue: number;
  total_cost: number;
  total_profit: number;
  avg_sale_price: number | null;
  num_invoices: number;
  first_sale: string | null;
  last_sale: string | null;
}

export interface SalesMonthItem {
  year_month: string;
  quantity: number;
  revenue: number;
  avg_price: number | null;
  num_invoices: number;
}

export interface SalesByClientTypeItem {
  client_type: string;
  client_type_name: string;
  total_quantity: number;
  total_revenue: number;
  total_cost: number;
  total_profit: number;
  num_invoices: number;
}

export interface ProductStatsSummary {
  total_units_sold: number;
  total_revenue: number;
  total_profit: number;
  years_with_sales: number;
  avg_units_per_year: number;
}

export interface ProductStats {
  reference: string;
  price_history: PriceHistoryItem[];
  sales_by_year: SalesYearItem[];
  sales_monthly: SalesMonthItem[];
  sales_by_client_type: SalesByClientTypeItem[];
  summary: ProductStatsSummary;
}

export interface ProductStatsSyncStatus {
  recent_syncs: Array<{
    id: number;
    sync_type: string;
    ejercicio: string | null;
    status: string;
    started_at: string;
    finished_at: string | null;
    products_processed: number;
    sales_records: number;
    error_message: string | null;
  }>;
  products_with_prices: number;
  products_with_sales: number;
  ejercicios_extracted: number;
}

export interface ProductStatsSyncResult {
  ejercicios_processed: string[];
  total_products: number;
  total_sales_records: number;
  errors: string[];
}

export interface TopProductItem {
  reference: string;
  ejercicio?: string;
  total_quantity: number;
  total_revenue: number;
  total_profit: number;
  avg_sale_price: number | null;
  num_invoices: number;
  cost_price?: number | null;
  sale_price_1?: number | null;
}

export const productStatsApi = {
  // Get complete stats for a product
  getProductStats: async (reference: string): Promise<ProductStats> => {
    const response = await api.get(`/product-stats/${encodeURIComponent(reference)}`);
    return response.data;
  },

  // Get sync status
  getSyncStatus: async (): Promise<ProductStatsSyncStatus> => {
    const response = await api.get('/product-stats/sync/status');
    return response.data;
  },

  // Run initial full extraction
  runInitialSync: async (ejercicios?: string[]): Promise<ProductStatsSyncResult> => {
    const response = await api.post('/product-stats/sync/initial', null, {
      params: ejercicios ? { ejercicios } : undefined
    });
    return response.data;
  },

  // Run daily incremental sync
  runDailySync: async (): Promise<ProductStatsSyncResult> => {
    const response = await api.post('/product-stats/sync/daily');
    return response.data;
  },

  // Get top selling products
  getTopProducts: async (options?: {
    ejercicio?: string;
    limit?: number;
    order_by?: 'revenue' | 'quantity' | 'profit';
  }): Promise<TopProductItem[]> => {
    const response = await api.get('/product-stats/top/products', { params: options });
    return response.data;
  },

  // Get available fiscal years
  getAvailableEjercicios: async (): Promise<{ ejercicios: string[] }> => {
    const response = await api.get('/product-stats/ejercicios/available');
    return response.data;
  },
};

// ============================================================================
// API CLIENTS - External catalog access management
// ============================================================================

export interface ApiClient {
  id: number;
  nombre: string;
  email: string | null;
  token: string;
  activo: boolean;
  fecha_expiracion: string | null;
  limite_descargas_dia: number;
  ips_permitidas: string[];
  tarifa: number;  // 1=PVP, 2=PVD (Distribuidores), 3=VIP
  incluir_iva: boolean;
  familias: string[];
  created_at: string;
  updated_at: string;
  accesos_hoy?: number;
}

export interface ApiClientCreate {
  nombre: string;
  email?: string | null;
  activo?: boolean;
  fecha_expiracion?: string | null;
  limite_descargas_dia?: number;
  ips_permitidas?: string[] | null;
  tarifa?: number;  // 1=PVP (default), 2=PVD, 3=VIP
  incluir_iva?: boolean;  // default: true
  familias?: string[] | null;
}

export interface ApiClientUpdate {
  nombre: string;
  email?: string | null;
  activo?: boolean;
  fecha_expiracion?: string | null;
  limite_descargas_dia?: number;
  ips_permitidas?: string[] | null;
  tarifa?: number;
  incluir_iva?: boolean;
  familias?: string[] | null;
}

export interface FactusolFamily {
  code: string;
  name: string;
}

export interface ApiAccessLog {
  id: number;
  cliente_id: number;
  timestamp: string;
  ip: string;
  user_agent: string;
  status: string;
  response_time_ms: number;
  cliente_nombre?: string;
}

export interface ApiClientStats {
  total_clients: number;
  active_clients: number;
  today_accesses: number;
  today_successful: number;
}

export const apiClientsApi = {
  // Get all clients
  getAll: async (): Promise<{ success: boolean; clients: ApiClient[] }> => {
    const response = await api.get('/api-clients');
    return response.data;
  },

  // Get single client
  get: async (id: number): Promise<{ success: boolean; client: ApiClient }> => {
    const response = await api.get(`/api-clients/${id}`);
    return response.data;
  },

  // Get stats
  getStats: async (): Promise<{ success: boolean; stats: ApiClientStats }> => {
    const response = await api.get('/api-clients/stats');
    return response.data;
  },

  // Get all logs
  getAllLogs: async (limit?: number): Promise<{ success: boolean; logs: ApiAccessLog[] }> => {
    const response = await api.get('/api-clients/logs', { params: { limit } });
    return response.data;
  },

  // Create client
  create: async (data: ApiClientCreate): Promise<{ success: boolean; client: ApiClient }> => {
    const response = await api.post('/api-clients', data);
    return response.data;
  },

  // Update client
  update: async (id: number, data: ApiClientUpdate): Promise<{ success: boolean; client: ApiClient }> => {
    const response = await api.put(`/api-clients/${id}`, data);
    return response.data;
  },

  // Delete client
  delete: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/api-clients/${id}`);
    return response.data;
  },

  // Regenerate token
  regenerateToken: async (id: number): Promise<{ success: boolean; token: string }> => {
    const response = await api.post(`/api-clients/${id}/regenerate-token`);
    return response.data;
  },

  // Get client logs
  getClientLogs: async (id: number, limit?: number): Promise<{ success: boolean; logs: ApiAccessLog[]; client: ApiClient }> => {
    const response = await api.get(`/api-clients/${id}/logs`, { params: { limit } });
    return response.data;
  },

  // Get Factusol families
  getFamilies: async (): Promise<{ success: boolean; families: FactusolFamily[] }> => {
    const response = await api.get('/api-clients/families/list');
    return response.data;
  },

  // Get export URL (helper function)
  getExportUrl: (token: string): string => {
    // Return the full public URL for the catalog export
    return `https://api.jvs-app.es/api/export/catalog.csv?token=${token}`;
  },
};

// ============================================================================
// PRODUCT OPTIMIZER API - Optimize existing product pages
// ============================================================================

export interface OptimizerProductSearchResult {
  id_product: number;
  reference: string;
  name: string;
  active: boolean;
}

export interface OptimizerProductLanguageContent {
  id_lang: number;
  name: string;
  description: string;
  description_short: string;
  meta_title: string;
  meta_description: string;
  link_rewrite: string;
}

export interface OptimizerProductDetail {
  id_product: number;
  reference: string;
  price: number;
  wholesale_price: number;
  id_category_default: number;
  active: boolean;
  date_add?: string;
  date_upd?: string;
  languages: Record<number, OptimizerProductLanguageContent>;
}

export interface OptimizeRequest {
  product_id: number;
  instructions?: string;
  warranty_duration?: string;
  client_id?: string;
  model?: string;  // OpenAI model (gpt-4o-mini or gpt-4o)
}

export interface OptimizedTranslation {
  name: string;
  description: string;
  description_short: string;
  meta_title: string;
  meta_description: string;
  link_rewrite: string;
}

// Capa AI-ready (GEO + SEO) — score 0-100 devuelto por el backend
export interface AiReadinessCheck {
  key: string;
  weight: number;
  points: number;
  passed: boolean;
  detail?: string;
}

export interface AiReadiness {
  score: number;
  checks: AiReadinessCheck[];
  blocking: string[];
  warnings: string[];
  lang?: string;
  multilang?: MultilangCoherence;
}

export interface MultilangCoherence {
  score: number;
  passed: boolean;
  details: string[];
}

export interface ProductFaq {
  question: string;
  answer: string;
}

export interface OptimizePreviewResponse {
  product_id: number;
  reference: string;
  current: {
    name: string;
    description_html: string;
    description_short?: string;
    meta_title?: string;
    meta_description: string;
    meta_keywords?: string;
    link_rewrite?: string;
    mpn?: string;
  };
  optimized: {
    name: string;
    description_html: string;
    description_short: string;
    meta_title: string;
    meta_description: string;
    meta_keywords?: string;
    mpn?: string;
    link_rewrite: string;
    // Capa GEO (editable en el preview)
    direct_answer?: string;
    faqs?: ProductFaq[];
    tags?: string[];
    features_ps?: Record<string, string>;
    image_alt?: string;
    translations: Record<number, OptimizedTranslation>;
  };
  json_data?: any;
  ai_readiness?: AiReadiness | null;
  openai_usage?: OpenAIUsage;
  original_link_rewrites?: Record<number, string>;  // For translate-and-apply flow
}

export interface ApplyChangesRequest {
  product_id: number;
  optimized_data: Record<number, Record<string, any>>;
  mpn?: string;
  update_all_languages?: boolean;
  force?: boolean;  // aplicar aunque el score AI-ready sea < 60
}

export interface TranslateAndApplyRequest {
  product_id: number;
  spanish_data: {
    name: string;
    description_html: string;
    description_short: string;
    meta_title?: string;
    meta_description: string;
    link_rewrite: string;
    // Capa GEO editada por el usuario (opcional)
    direct_answer?: string;
    faqs?: ProductFaq[];
    tags?: string[];
    image_alt?: string;
    features_ps?: Record<string, string>;
  };
  json_data: any;
  original_link_rewrites: Record<number, string>;
  mpn?: string;
  warranty_duration?: string;
  model?: string;
  force?: boolean;  // aplicar aunque el score AI-ready sea < 60
}

export interface ApplyChangesResponse {
  success: boolean;
  product_id: number;
  backup_id: number;
  message: string;
  updated_languages: number[];
  openai_usage?: OpenAIUsage;
  ai_readiness?: AiReadiness | null;
  multilang?: MultilangCoherence | null;
  cache_purged?: boolean;
  hooks_triggered?: boolean;
}

export interface RollbackResponse {
  success: boolean;
  product_id: number;
  message: string;
}

// Job-based async generation types
export interface JobStartResponse {
  job_id: string;
  status: string;
  message: string;
}

export interface JobStatusResponse {
  job_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress?: {
    step: number;
    total: number;
    message: string;
    percentage: number;
  };
  result?: OptimizePreviewResponse;
  error?: string;
}

export interface ProductBackup {
  id: number;
  product_id: number;
  reference: string;
  optimized_at: string;
  status: string;
}

// Batch optimization types
export interface BatchOptimizeRequest {
  product_ids: number[];
  instructions?: string;
  warranty_duration?: string;
  auto_apply?: boolean;
  model?: string;  // OpenAI model (gpt-4o-mini or gpt-4o)
  min_score?: number;  // score AI-ready mínimo para auto-aplicar (0 = sin gate)
  translate?: boolean;  // traducir y aplicar en los 5 idiomas
}

export interface BatchProductStatus {
  product_id: number;
  reference: string;
  name: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped';
  error?: string | null;
  ai_readiness?: number | null;
}

export interface BatchJobStartResponse {
  job_id: string;
  status: string;
  total_products: number;
  message: string;
}

export interface BatchJobStatus {
  job_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  total_products: number;
  processed: number;
  succeeded: number;
  failed: number;
  current_product?: string | null;
  products: BatchProductStatus[];
  started_at?: string | null;
  completed_at?: string | null;
}

// ============================================================================
// FAMILY INSTRUCTIONS API - AI instructions per product family
// ============================================================================

export interface FamilyInstruction {
  id: number;
  family_code: string;
  family_name: string | null;
  instructions: string;
  created_at: string;
  updated_at: string;
}

export interface FamilyWithInstructions {
  code: string;
  name: string;
  has_instructions: boolean;
  instructions?: string;
  instruction_id?: number;
}

export const familyInstructionsApi = {
  // Get all family instructions
  getAll: async (): Promise<{ success: boolean; instructions: FamilyInstruction[] }> => {
    const response = await api.get('/family-instructions/');
    return response.data;
  },

  // Get families with their instruction status
  getFamiliesWithInstructions: async (): Promise<{ success: boolean; families: FamilyWithInstructions[] }> => {
    const response = await api.get('/family-instructions/families');
    return response.data;
  },

  // Get instructions for a specific family
  getByFamily: async (familyCode: string): Promise<{ success: boolean; instruction: FamilyInstruction | null }> => {
    const response = await api.get(`/family-instructions/${familyCode}`);
    return response.data;
  },

  // Create or update instructions for a family
  save: async (data: { family_code: string; family_name: string; instructions: string }): Promise<{ success: boolean; instruction: FamilyInstruction }> => {
    const response = await api.post('/family-instructions/', data);
    return response.data;
  },

  // Delete instructions for a family
  delete: async (familyCode: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/family-instructions/${familyCode}`);
    return response.data;
  },
};

export const productOptimizerApi = {
  // Search products by ID or reference (no limit when searching by reference)
  search: async (q: string, searchBy: 'id' | 'reference' = 'id'): Promise<OptimizerProductSearchResult[]> => {
    const response = await api.get('/product-optimizer/search', {
      params: { q, search_by: searchBy }
    });
    return response.data;
  },

  // Get full product details
  getProduct: async (productId: number): Promise<OptimizerProductDetail> => {
    const response = await api.get(`/product-optimizer/product/${productId}`);
    return response.data;
  },

  // Generate optimized preview (legacy synchronous - may timeout)
  generatePreview: async (data: OptimizeRequest): Promise<OptimizePreviewResponse> => {
    const response = await api.post('/product-optimizer/preview', data);
    return response.data;
  },

  // Start async preview generation job (returns immediately with job_id)
  startPreview: async (data: OptimizeRequest): Promise<JobStartResponse> => {
    const response = await api.post('/product-optimizer/preview/start', data);
    return response.data;
  },

  // Check status of preview generation job
  getPreviewStatus: async (jobId: string): Promise<JobStatusResponse> => {
    const response = await api.get(`/product-optimizer/preview/status/${jobId}`);
    return response.data;
  },

  // Apply changes to product
  applyChanges: async (data: ApplyChangesRequest): Promise<ApplyChangesResponse> => {
    const response = await api.post('/product-optimizer/apply', data);
    return response.data;
  },

  // Rollback to previous version
  rollback: async (backupId: number): Promise<RollbackResponse> => {
    const response = await api.post(`/product-optimizer/rollback/${backupId}`);
    return response.data;
  },

  // Get product backups
  getBackups: async (productId: number, limit?: number): Promise<{ backups: ProductBackup[] }> => {
    const response = await api.get(`/product-optimizer/backups/${productId}`, {
      params: { limit }
    });
    return response.data;
  },

  // Batch optimization - start batch job
  startBatch: async (data: BatchOptimizeRequest): Promise<BatchJobStartResponse> => {
    const response = await api.post('/product-optimizer/batch/start', data);
    return response.data;
  },

  // Batch optimization - get job status
  getBatchStatus: async (jobId: string): Promise<BatchJobStatus> => {
    const response = await api.get(`/product-optimizer/batch/status/${jobId}`);
    return response.data;
  },

  // Batch optimization - cancel job
  cancelBatch: async (jobId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/product-optimizer/batch/cancel/${jobId}`);
    return response.data;
  },

  // Translate Spanish content and apply changes (Option E flow)
  translateAndApply: async (data: TranslateAndApplyRequest): Promise<ApplyChangesResponse> => {
    const response = await api.post('/product-optimizer/translate-and-apply', data);
    return response.data;
  },
};

// ============================================================================
// SUPPLIER CATALOGS API - Import and manage supplier product catalogs
// ============================================================================

export interface Supplier {
  id: number;
  code: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  csv_delimiter: string;
  csv_encoding: string;
  csv_has_header: boolean;
  column_mapping?: Record<string, number> | null;
  catalog_url?: string | null;
  import_frequency_days: number;
  last_auto_import?: string | null;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface SupplierCreate {
  code: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  csv_delimiter?: string;
  csv_encoding?: string;
  csv_has_header?: boolean;
  column_mapping?: Record<string, number> | null;
  catalog_url?: string | null;
  import_frequency_days?: number;
}

export interface SupplierUpdate {
  code?: string;
  name?: string;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  csv_delimiter?: string;
  csv_encoding?: string;
  csv_has_header?: boolean;
  column_mapping?: Record<string, number> | null;
  catalog_url?: string | null;
  import_frequency_days?: number;
  active?: boolean;
}

export interface SupplierProduct {
  id: number;
  supplier_id: number;
  supplier_reference: string;
  our_reference?: string | null;
  ean?: string | null;
  name: string;
  description?: string | null;
  brand?: string | null;
  category?: string | null;
  cost_price: number;
  rrp_price?: number | null;
  stock: number;
  stock_status?: string | null;
  published_prestashop: boolean;
  prestashop_id?: number | null;
  published_factusol: boolean;
  factusol_reference?: string | null;
  mapped_factusol_reference?: string | null;
  extra_data?: string | null;  // JSON con datos adicionales (specifications, images, etc.)
  last_seen?: string | null;
  created_at?: string;
}

export interface SupplierProductUpdate {
  our_reference?: string;
  name?: string;
  description?: string;
  cost_price?: number;
  rrp_price?: number;
  // Campos para vincular con producto creado
  prestashop_id?: number;
  factusol_reference?: string;
  published_prestashop?: boolean;
  published_factusol?: boolean;
}

export interface PriceHistoryEntry {
  date: string;
  cost_price: number;
  rrp_price?: number | null;
  stock: number;
}

export interface ImportPreview {
  success: boolean;
  headers?: string[] | null;
  rows?: string[][] | null;
  total_columns: number;
  error?: string | null;
}

export interface ImportResult {
  success: boolean;
  import_id?: number;
  total_rows: number;
  products_created: number;
  products_updated: number;
  errors: number;
  error?: string | null;
}

export interface CatalogImportRecord {
  id: number;
  supplier_id: number;
  supplier_name: string;
  filename: string;
  import_date: string;
  total_rows: number;
  products_created: number;
  products_updated: number;
  errors: number;
  error_log?: any;
  status: string;
}

export interface SupplierStats {
  total_products: number;
  published_prestashop: number;
  published_factusol: number;
  with_stock: number;
  unpublished: number;
  last_import?: {
    import_date: string;
    total_rows: number;
    products_created: number;
    products_updated: number;
  } | null;
}

export interface GlobalSupplierStats {
  total_suppliers: number;
  total_products: number;
  total_published: number;
  pending_publish: number;
  imports_today: number;
}

export interface ReferenceMapping {
  id: number;
  factusol_reference: string;
  supplier_id: number;
  supplier_product_id: number;
  notes?: string | null;
  supplier_name?: string;
  supplier_code?: string;
  supplier_reference?: string;
  product_name?: string;
  cost_price?: number;
  rrp_price?: number;
  stock?: number;
  created_at?: string;
  updated_at?: string;
}

export interface MappingStats {
  total_products?: number;
  mapped_products?: number;
  unmapped_products?: number;
  mapping_percentage?: number;
  total_mappings?: number;
  unique_factusol_references?: number;
  suppliers_with_mappings?: number;
}

export interface SupplierProductForMapping {
  id: number;
  supplier_reference: string;
  name: string;
  ean?: string | null;
  cost_price: number;
  rrp_price?: number | null;
  stock: number;
  mapped_to?: string | null;
}

export const supplierCatalogsApi = {
  // =========================================================================
  // SUPPLIERS
  // =========================================================================

  // List all suppliers (portal_only: true=portal asiático, false=catálogo europeo, undefined=todos)
  list: async (opts: { active_only?: boolean; portal_only?: boolean } = {}): Promise<Supplier[]> => {
    const params: Record<string, any> = { active_only: opts.active_only ?? false };
    if (opts.portal_only !== undefined) params.portal_only = opts.portal_only;
    const response = await api.get('/supplier-catalogs/', { params });
    return response.data;
  },

  // Get single supplier
  get: async (id: number): Promise<Supplier> => {
    const response = await api.get(`/supplier-catalogs/${id}`);
    return response.data;
  },

  // Create supplier
  create: async (data: SupplierCreate): Promise<Supplier> => {
    const response = await api.post('/supplier-catalogs/', data);
    return response.data;
  },

  // Update supplier
  update: async (id: number, data: SupplierUpdate): Promise<Supplier> => {
    const response = await api.patch(`/supplier-catalogs/${id}`, data);
    return response.data;
  },

  // Delete supplier
  delete: async (id: number): Promise<{ status: string; message: string }> => {
    const response = await api.delete(`/supplier-catalogs/${id}`);
    return response.data;
  },

  // Get supplier stats
  getStats: async (id: number): Promise<SupplierStats> => {
    const response = await api.get(`/supplier-catalogs/${id}/stats`);
    return response.data;
  },

  // Get global stats
  getGlobalStats: async (): Promise<GlobalSupplierStats> => {
    const response = await api.get('/supplier-catalogs/stats');
    return response.data;
  },

  // =========================================================================
  // CSV IMPORT
  // =========================================================================

  // Preview CSV file
  previewCsv: async (
    supplierId: number,
    file: File,
    delimiter: string = ';',
    encoding: string = 'utf-8',
    hasHeader: boolean = true
  ): Promise<ImportPreview> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(
      `/supplier-catalogs/${supplierId}/import/preview`,
      formData,
      {
        params: { delimiter, encoding, has_header: hasHeader },
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    );
    return response.data;
  },

  // Preview CSV from URL
  previewCsvFromUrl: async (
    supplierId: number,
    url: string,
    delimiter: string = ';',
    encoding: string = 'utf-8',
    hasHeader: boolean = true
  ): Promise<ImportPreview> => {
    const response = await api.post(
      `/supplier-catalogs/${supplierId}/import/url/preview`,
      null,
      { params: { url, delimiter, encoding, has_header: hasHeader } }
    );
    return response.data;
  },

  // Import CSV file
  importCsv: async (
    supplierId: number,
    file: File,
    columnMapping: Record<string, number>
  ): Promise<ImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('column_mapping', JSON.stringify(columnMapping));

    const response = await api.post(
      `/supplier-catalogs/${supplierId}/import`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  // Import CSV from URL
  importCsvFromUrl: async (
    supplierId: number,
    url: string,
    columnMapping: Record<string, number>
  ): Promise<ImportResult> => {
    const response = await api.post(
      `/supplier-catalogs/${supplierId}/import/url`,
      null,
      {
        params: {
          url,
          column_mapping: JSON.stringify(columnMapping)
        }
      }
    );
    return response.data;
  },

  // Get import history
  getImportHistory: async (supplierId: number, limit: number = 20): Promise<{ imports: CatalogImportRecord[]; total: number }> => {
    const response = await api.get(`/supplier-catalogs/${supplierId}/imports`, {
      params: { limit }
    });
    return response.data;
  },

  // =========================================================================
  // PRODUCTS
  // =========================================================================

  // List products
  listProducts: async (
    supplierId: number,
    options?: {
      page?: number;
      page_size?: number;
      search?: string;
      exclude?: string;
      reference_filter?: string;
      category_filter?: string;
      published_only?: boolean;
      unpublished_only?: boolean;
      hide_mapped?: boolean;
      sort_by?: string;
      sort_order?: string;
    }
  ): Promise<{
    products: SupplierProduct[];
    total: number;
    page: number;
    page_size: number;
  }> => {
    const response = await api.get(`/supplier-catalogs/${supplierId}/products`, {
      params: options
    });
    return response.data;
  },

  // Get single product
  getProduct: async (supplierId: number, productId: number): Promise<SupplierProduct> => {
    const response = await api.get(`/supplier-catalogs/${supplierId}/products/${productId}`);
    return response.data;
  },

  // Update product
  updateProduct: async (
    supplierId: number,
    productId: number,
    data: SupplierProductUpdate
  ): Promise<SupplierProduct> => {
    const response = await api.patch(`/supplier-catalogs/${supplierId}/products/${productId}`, data);
    return response.data;
  },

  // Get price history
  getPriceHistory: async (
    supplierId: number,
    productId: number,
    limit: number = 90
  ): Promise<PriceHistoryEntry[]> => {
    const response = await api.get(
      `/supplier-catalogs/${supplierId}/products/${productId}/price-history`,
      { params: { limit } }
    );
    return response.data;
  },

  // =========================================================================
  // PUBLISHING
  // =========================================================================

  // Publish to PrestaShop
  publishToPrestaShop: async (
    supplierId: number,
    productId: number
  ): Promise<{ status: string; message: string; product_id: number }> => {
    const response = await api.post(
      `/supplier-catalogs/${supplierId}/products/${productId}/publish/prestashop`
    );
    return response.data;
  },

  // Publish to Factusol
  publishToFactusol: async (
    supplierId: number,
    productId: number
  ): Promise<{ status: string; message: string; product_id: number }> => {
    const response = await api.post(
      `/supplier-catalogs/${supplierId}/products/${productId}/publish/factusol`
    );
    return response.data;
  },

  // Publish to both platforms
  publishToAll: async (
    supplierId: number,
    productId: number
  ): Promise<{
    status: string;
    message: string;
    results: { prestashop: string | null; factusol: string | null };
  }> => {
    const response = await api.post(
      `/supplier-catalogs/${supplierId}/products/${productId}/publish/all`
    );
    return response.data;
  },

  // Unpublish from PrestaShop (solo marca en BD local, no elimina de PrestaShop)
  unpublishFromPrestaShop: async (
    supplierId: number,
    productId: number
  ): Promise<{ status: string; message: string }> => {
    const response = await api.delete(
      `/supplier-catalogs/${supplierId}/products/${productId}/publish/prestashop`
    );
    return response.data;
  },

  // Unpublish from Factusol (solo marca en BD local, no elimina de Factusol)
  unpublishFromFactusol: async (
    supplierId: number,
    productId: number
  ): Promise<{ status: string; message: string }> => {
    const response = await api.delete(
      `/supplier-catalogs/${supplierId}/products/${productId}/publish/factusol`
    );
    return response.data;
  },

  // =========================================================================
  // REFERENCE MAPPINGS (Factusol <-> Proveedores)
  // =========================================================================

  // Create a reference mapping
  createMapping: async (data: {
    factusol_reference: string;
    supplier_id: number;
    supplier_product_id: number;
    notes?: string;
  }): Promise<ReferenceMapping> => {
    const response = await api.post('/supplier-catalogs/mappings', data);
    return response.data;
  },

  // Get mappings for a Factusol reference
  getMappingsByFactusolRef: async (factusolReference: string): Promise<{
    mappings: ReferenceMapping[];
    total: number;
  }> => {
    const response = await api.get(`/supplier-catalogs/mappings/by-factusol/${encodeURIComponent(factusolReference)}`);
    return response.data;
  },

  // Get mappings for a supplier product
  getMappingsBySupplierProduct: async (supplierProductId: number): Promise<{
    mappings: ReferenceMapping[];
    total: number;
  }> => {
    const response = await api.get(`/supplier-catalogs/mappings/by-supplier-product/${supplierProductId}`);
    return response.data;
  },

  // Get all mappings for a supplier
  getSupplierMappings: async (supplierId: number): Promise<{
    mappings: ReferenceMapping[];
    total: number;
    stats: MappingStats;
  }> => {
    const response = await api.get(`/supplier-catalogs/${supplierId}/mappings`);
    return response.data;
  },

  // Delete a mapping
  deleteMapping: async (mappingId: number): Promise<{ status: string; message: string }> => {
    const response = await api.delete(`/supplier-catalogs/mappings/${mappingId}`);
    return response.data;
  },

  // Get mapping stats
  getMappingStats: async (supplierId?: number): Promise<MappingStats> => {
    const response = await api.get('/supplier-catalogs/mappings/stats', {
      params: supplierId ? { supplier_id: supplierId } : undefined
    });
    return response.data;
  },

  // Get products for mapping
  getProductsForMapping: async (
    supplierId: number,
    search: string = '',
    unmappedOnly: boolean = false,
    limit: number = 50
  ): Promise<{ products: SupplierProductForMapping[]; total: number }> => {
    const response = await api.get(`/supplier-catalogs/${supplierId}/products/for-mapping`, {
      params: { search, unmapped_only: unmappedOnly, limit }
    });
    return response.data;
  },

  // Auto-map by EAN
  autoMapByEan: async (
    supplierId: number,
    factusolProducts: { reference: string; ean: string }[]
  ): Promise<{
    mapped: number;
    skipped: number;
    errors: number;
    error_details: { reference: string; error: string }[];
  }> => {
    const response = await api.post(`/supplier-catalogs/${supplierId}/mappings/auto-ean`, factusolProducts);
    return response.data;
  },

  // =========================================================================
  // IMPORT LOCAL FILE
  // =========================================================================

  // Import catalog from local file (JSON, CSV, XLSX)
  importLocalFile: async (
    supplierId: number,
    filePath: string,
    columnMapping?: Record<string, number>
  ): Promise<ImportResult> => {
    const response = await api.post(
      `/supplier-catalogs/${supplierId}/import/local`,
      {
        file_path: filePath,
        column_mapping: columnMapping
      }
    );
    return response.data;
  },

  // =========================================================================
  // PRODUCT IMAGES - Imágenes locales del proveedor
  // =========================================================================

  // Get product images from local storage
  getProductImages: async (
    supplierId: number,
    productId: number
  ): Promise<{
    images: Array<{
      index: number;
      base64: string;
      filename: string;
      has_edited_version: boolean;
    }>;
    base_path: string;
  }> => {
    const response = await api.get(
      `/supplier-catalogs/${supplierId}/products/${productId}/images`
    );
    return response.data;
  },

  // Process a single image with AI
  processProductImage: async (
    supplierId: number,
    productId: number,
    imageIndex: number,
    instructions: string
  ): Promise<{
    success: boolean;
    processed_image?: string;
    new_file_path?: string;
    cost_usd: number;
    cost_eur: number;
    error?: string;
  }> => {
    const response = await api.post(
      `/supplier-catalogs/${supplierId}/products/${productId}/images/${imageIndex}/process`,
      { instructions }
    );
    return response.data;
  },
};

// ============================================================================
// FREE SHIPPING - Envío gratuito condicional para pedidos agrupados
// ============================================================================

export interface FreeShippingConfig {
  id_config?: number;
  enabled: boolean;
  id_carrier_free: number;
  carrier_name?: string;
  time_window_start: string;
  time_window_end: string;
  valid_order_states: number[];
  show_message: boolean;
  message_template: string;
}

export interface FreeShippingCarrier {
  id_carrier: number;
  name: string;
  active: boolean;
  is_free: boolean;
}

export interface FreeShippingOrderState {
  id_order_state: number;
  name: string;
  color: string;
}

export interface FreeShippingStats {
  total_applied_today: number;
  total_applied_week: number;
  total_applied_month: number;
  estimated_savings: number;
  recent_applications: {
    customer_name: string;
    order_reference: string;
    related_order: number;
    date: string;
    total_paid: number;
  }[];
}

export interface FreeShippingLanguage {
  id_lang: number;
  name: string;
  iso_code: string;
  active: boolean;
}

export const freeShipping = {
  // Get current configuration
  getConfig: async (): Promise<FreeShippingConfig | null> => {
    const response = await api.get('/free-shipping/config');
    return response.data.config;
  },

  // Update configuration
  updateConfig: async (config: Omit<FreeShippingConfig, 'id_config' | 'carrier_name'>): Promise<FreeShippingConfig> => {
    const response = await api.put('/free-shipping/config', config);
    return response.data.config;
  },

  // Get available carriers
  getCarriers: async (): Promise<FreeShippingCarrier[]> => {
    const response = await api.get('/free-shipping/carriers');
    return response.data.carriers;
  },

  // Get order states
  getOrderStates: async (): Promise<FreeShippingOrderState[]> => {
    const response = await api.get('/free-shipping/order-states');
    return response.data.states;
  },

  // Get usage statistics
  getStats: async (): Promise<FreeShippingStats> => {
    const response = await api.get('/free-shipping/stats');
    return response.data.stats;
  },

  // Ensure tables exist
  ensureTables: async (): Promise<void> => {
    await api.post('/free-shipping/ensure-tables');
  },

  // Get available languages
  getLanguages: async (): Promise<FreeShippingLanguage[]> => {
    const response = await api.get('/free-shipping/languages');
    return response.data.languages;
  },

  // Get message translations
  getTranslations: async (idConfig: number): Promise<Record<number, string>> => {
    const response = await api.get(`/free-shipping/translations/${idConfig}`);
    return response.data.translations;
  },

  // Update message translations
  updateTranslations: async (idConfig: number, translations: Record<number, string>): Promise<void> => {
    await api.put(`/free-shipping/translations/${idConfig}`, translations);
  },
};

// ============================================================================
// CATEGORY MANAGER API
// ============================================================================

export interface CategoryListItem {
  id: number;
  id_parent: number;
  level_depth: number;
  active: boolean;
  position: number;
  name: string;
  display_name: string;
  description: string;
  link_rewrite: string;
  meta_title: string;
  meta_description: string;
}

export interface CategoryDetail {
  id_category: number;
  id_parent: number;
  level_depth: number;
  active: boolean;
  position: number;
  date_add?: string;
  date_upd?: string;
  languages: Record<number, {
    id_lang: number;
    name: string;
    description: string;
    link_rewrite: string;
    meta_title: string;
    meta_description: string;
    meta_keywords: string;
  }>;
}

export interface CategorySearchResult {
  id_category: number;
  name: string;
  id_parent: number;
  level_depth: number;
  active: boolean;
}

export interface CategoryCreateRequest {
  name: string;
  description: string;
  instructions?: string;
  id_parent: number;
  client_id?: string;
  model?: string;
  generate_image?: boolean;
}

export interface CategoryOptimizeRequest {
  category_id: number;
  instructions?: string;
  client_id?: string;
  model?: string;
  generate_image?: boolean;
}

export interface CategoryJobStartResponse {
  job_id: string;
  status: string;
  message: string;
}

export interface CategoryJobStatusResponse {
  job_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress?: {
    type: string;
    step: number;
    total: number;
    message: string;
    percentage: number;
  };
  result?: {
    category_id?: number;
    id_parent: number;
    current?: Record<string, string>;
    optimized: Record<string, string>;
    json_data?: any;
    openai_usage?: OpenAIUsage;
    image_url?: string;
    original_link_rewrites?: Record<number, string>;
  };
  error?: string;
}

export interface TranslateAndApplyCategoryRequest {
  category_id?: number;  // null for new categories
  id_parent: number;
  spanish_data: {
    name: string;
    description: string;
    link_rewrite: string;
    meta_title: string;
    meta_description: string;
  };
  json_data: any;
  model?: string;
  image_url?: string;  // Optional DALL-E generated image URL
}

export interface ApplyCategoryResponse {
  success: boolean;
  category_id: number;
  backup_id?: number;
  message: string;
  updated_languages: number[];
}

export const categoryManagerApi = {
  // List all categories
  listCategories: async (): Promise<{ categories: CategoryListItem[]; total: number }> => {
    const response = await api.get('/category-manager/list');
    return response.data;
  },

  // Search categories
  searchCategories: async (query: string, limit: number = 20): Promise<CategorySearchResult[]> => {
    const response = await api.get('/category-manager/search', { params: { q: query, limit } });
    return response.data;
  },

  // Get category details
  getCategory: async (categoryId: number): Promise<CategoryDetail> => {
    const response = await api.get(`/category-manager/${categoryId}`);
    return response.data;
  },

  // Start category creation with AI
  startCreate: async (data: CategoryCreateRequest): Promise<CategoryJobStartResponse> => {
    const response = await api.post('/category-manager/create/start', data);
    return response.data;
  },

  // Get creation job status
  getCreateStatus: async (jobId: string): Promise<CategoryJobStatusResponse> => {
    const response = await api.get(`/category-manager/create/status/${jobId}`);
    return response.data;
  },

  // Start category optimization with AI
  startOptimize: async (data: CategoryOptimizeRequest): Promise<CategoryJobStartResponse> => {
    const response = await api.post('/category-manager/optimize/start', data);
    return response.data;
  },

  // Get optimization job status
  getOptimizeStatus: async (jobId: string): Promise<CategoryJobStatusResponse> => {
    const response = await api.get(`/category-manager/optimize/status/${jobId}`);
    return response.data;
  },

  // Translate and apply changes (for both create and optimize)
  translateAndApply: async (data: TranslateAndApplyCategoryRequest): Promise<ApplyCategoryResponse> => {
    const response = await api.post('/category-manager/translate-and-apply', data);
    return response.data;
  },

  // Rollback category changes
  rollback: async (backupId: number): Promise<{ success: boolean; category_id: number; message: string }> => {
    const response = await api.post(`/category-manager/rollback/${backupId}`);
    return response.data;
  },
};

// ============================================================================
// BREVO API - Email Marketing Integration
// ============================================================================

export interface BrevoStatus {
  connected: boolean;
  email?: string;
  company_name?: string;
  plan?: { type: string; credits: number }[];
  error?: string;
}

export interface BrevoList {
  id: number;
  name: string;
  total_subscribers: number;
  folder_id?: number;
}

export interface SegmentMapping {
  segment_name: string;
  brevo_list_id: number | null;
  brevo_list_name: string | null;
  auto_sync: boolean;
  last_sync_at: string | null;
  last_sync_count: number;
}

export interface BrevoConfig {
  is_configured: boolean;
  api_key_set: boolean;
  api_key_preview: string | null;
  mappings: SegmentMapping[];
  rfm_segments: string[];
  customer_groups: { id: number; name: string }[];
}

export interface CampaignHistory {
  id: number;
  brevo_campaign_id: number | null;
  name: string;
  subject: string;
  segment_name: string | null;
  list_id: number | null;
  recipients_count: number;
  status: string;
  sent_at: string | null;
  stats: {
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    unsubscribed: number;
  };
  created_at: string;
}

export interface SyncResult {
  success: boolean;
  segment_name: string;
  list_id: number | null;
  contacts_synced: number;
  message: string;
}

// Tipos adicionales para Brevo
export interface BrevoContact {
  email: string;
  id?: number;
  emailBlacklisted?: boolean;
  smsBlacklisted?: boolean;
  createdAt?: string;
  modifiedAt?: string;
  attributes?: Record<string, any>;
  listIds?: number[];
  statistics?: {
    opened?: number;
    clicked?: number;
    messagesSent?: number;
  };
}

export interface BrevoTemplate {
  id: number;
  name: string;
  subject?: string;
  isActive: boolean;
  createdAt?: string;
  modifiedAt?: string;
  htmlContent?: string;
  tag?: string;
  sender?: { name: string; email: string };
}

export interface BrevoSender {
  id: number;
  name: string;
  email: string;
  active: boolean;
  ips?: Array<{ ip: string; domain: string }>;
}

export interface BrevoAttribute {
  name: string;
  category: string;
  type?: string;
  enumeration?: Array<{ label: string; value: number }>;
}

export interface BrevoFolder {
  id: number;
  name: string;
  totalSubscribers?: number;
  totalBlacklisted?: number;
  uniqueSubscribers?: number;
}

export interface BrevoWebhook {
  id: number;
  url: string;
  description?: string;
  events: string[];
  type: string;
  createdAt?: string;
  modifiedAt?: string;
}

export interface BrevoSegment {
  id: number;
  segmentName: string;
  createdAt?: string;
  modifiedAt?: string;
}

export const brevoApi = {
  // =========================================================================
  // STATUS & CONFIG
  // =========================================================================

  getStatus: async (): Promise<BrevoStatus> => {
    const response = await api.get('/brevo/status');
    return response.data;
  },

  getConfig: async (): Promise<BrevoConfig> => {
    const response = await api.get('/brevo/config');
    return response.data;
  },

  setApiKey: async (apiKey: string): Promise<{ success: boolean; message: string; account?: { email: string; company_name: string } }> => {
    const response = await api.post('/brevo/config/api-key', { api_key: apiKey });
    return response.data;
  },

  // =========================================================================
  // ACCOUNT INFO
  // =========================================================================

  getSenders: async (): Promise<{ senders: BrevoSender[] }> => {
    const response = await api.get('/brevo/senders');
    return response.data;
  },

  getAttributes: async (): Promise<{ attributes: BrevoAttribute[] }> => {
    const response = await api.get('/brevo/attributes');
    return response.data;
  },

  getFolders: async (): Promise<{ folders: BrevoFolder[] }> => {
    const response = await api.get('/brevo/folders');
    return response.data;
  },

  createFolder: async (name: string): Promise<{ success: boolean; folder_id: number; name: string }> => {
    const response = await api.post('/brevo/folders', null, { params: { name } });
    return response.data;
  },

  getBrevoSegments: async (): Promise<{ segments: BrevoSegment[] }> => {
    const response = await api.get('/brevo/brevo-segments');
    return response.data;
  },

  // =========================================================================
  // CONTACTS
  // =========================================================================

  getContacts: async (limit: number = 50, offset: number = 0, sort: string = 'desc'): Promise<{
    contacts: BrevoContact[];
    count: number;
  }> => {
    const response = await api.get('/brevo/contacts', { params: { limit, offset, sort } });
    return response.data;
  },

  getContactByEmail: async (email: string): Promise<BrevoContact> => {
    const response = await api.get(`/brevo/contacts/${encodeURIComponent(email)}`);
    return response.data;
  },

  createContact: async (data: {
    email: string;
    attributes?: Record<string, any>;
    list_ids?: number[];
  }): Promise<{ success: boolean; contact_id?: number }> => {
    const response = await api.post('/brevo/contacts', data);
    return response.data;
  },

  updateContact: async (email: string, data: {
    attributes?: Record<string, any>;
    list_ids?: number[];
    unlink_list_ids?: number[];
    email_blacklisted?: boolean;
  }): Promise<{ success: boolean }> => {
    const response = await api.put(`/brevo/contacts/${encodeURIComponent(email)}`, data);
    return response.data;
  },

  deleteContact: async (email: string): Promise<{ success: boolean }> => {
    const response = await api.delete(`/brevo/contacts/${encodeURIComponent(email)}`);
    return response.data;
  },

  importContacts: async (data: {
    contacts: Array<{ email: string; attributes?: Record<string, any> }>;
    list_ids?: number[];
    update_existing?: boolean;
  }): Promise<{ success: boolean; process_id?: number }> => {
    const response = await api.post('/brevo/contacts/import', data);
    return response.data;
  },

  // =========================================================================
  // LISTS
  // =========================================================================

  getLists: async (): Promise<BrevoList[]> => {
    const response = await api.get('/brevo/lists');
    return response.data;
  },

  createList: async (name: string): Promise<BrevoList> => {
    const response = await api.post('/brevo/lists', { name });
    return response.data;
  },

  getListContacts: async (listId: number, limit: number = 500, offset: number = 0): Promise<{
    contacts: BrevoContact[];
    count: number;
  }> => {
    const response = await api.get(`/brevo/lists/${listId}/contacts`, { params: { limit, offset } });
    return response.data;
  },

  addContactsToList: async (listId: number, emails: string[]): Promise<{
    success: boolean;
    added: number;
    failed: number;
  }> => {
    const response = await api.post(`/brevo/lists/${listId}/contacts`, { emails });
    return response.data;
  },

  removeContactsFromList: async (listId: number, emails: string[]): Promise<{
    success: boolean;
    removed: number;
    total: number;
    results: Array<{ email: string; success: boolean }>;
  }> => {
    const response = await api.delete(`/brevo/lists/${listId}/contacts`, { data: { emails } });
    return response.data;
  },

  // =========================================================================
  // SEGMENT MAPPINGS (RFM)
  // =========================================================================

  getMappings: async (): Promise<SegmentMapping[]> => {
    const response = await api.get('/brevo/config/mappings');
    return response.data;
  },

  setMapping: async (data: {
    segment_name: string;
    brevo_list_id: number;
    brevo_list_name: string;
    auto_sync?: boolean;
  }): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/brevo/config/mappings', data);
    return response.data;
  },

  clearMapping: async (segmentName: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/brevo/config/mappings/${encodeURIComponent(segmentName)}`);
    return response.data;
  },

  // =========================================================================
  // SYNC
  // =========================================================================

  syncSegment: async (segmentName: string, customerGroupId?: number): Promise<SyncResult> => {
    const response = await api.post(
      `/brevo/sync/segment/${encodeURIComponent(segmentName)}`,
      null,
      { params: customerGroupId ? { customer_group_id: customerGroupId } : {} }
    );
    return response.data;
  },

  syncAll: async (customerGroupId?: number): Promise<{
    success: boolean;
    message: string;
    results: SyncResult[];
  }> => {
    const response = await api.post(
      '/brevo/sync/all',
      null,
      { params: customerGroupId ? { customer_group_id: customerGroupId } : {} }
    );
    return response.data;
  },

  // =========================================================================
  // CAMPAIGNS
  // =========================================================================

  sendCampaign: async (data: {
    name: string;
    subject: string;
    html_content: string;
    segment_name?: string;
    list_ids: number[];
    schedule_at?: string;
  }): Promise<{ success: boolean; campaign_id?: number; name: string; status: string; error?: string }> => {
    const response = await api.post('/brevo/campaign/send', data);
    return response.data;
  },

  getCampaigns: async (status: string = 'sent', limit: number = 50): Promise<{
    campaigns: Array<{
      id: number;
      name: string;
      subject: string;
      status: string;
      sent_date: string | null;
      statistics: {
        sent: number;
        delivered: number;
        opened: number;
        clicked: number;
        unsubscribed: number;
      } | null;
    }>;
    count: number;
  }> => {
    const response = await api.get('/brevo/campaigns', { params: { status, limit } });
    return response.data;
  },

  getCampaignHistory: async (limit: number = 50): Promise<CampaignHistory[]> => {
    const response = await api.get('/brevo/campaign/history', { params: { limit } });
    return response.data;
  },

  getCampaignStats: async (campaignId: number): Promise<any> => {
    const response = await api.get(`/brevo/campaign/${campaignId}/stats`);
    return response.data;
  },

  sendCampaignTest: async (campaignId: number, emails: string[]): Promise<{ success: boolean }> => {
    const response = await api.post(`/brevo/campaign/${campaignId}/test`, { emails });
    return response.data;
  },

  // =========================================================================
  // TEMPLATES
  // =========================================================================

  getTemplates: async (limit: number = 50, offset: number = 0): Promise<{
    templates: BrevoTemplate[];
    count: number;
  }> => {
    const response = await api.get('/brevo/templates', { params: { limit, offset } });
    return response.data;
  },

  getTemplate: async (templateId: number): Promise<BrevoTemplate> => {
    const response = await api.get(`/brevo/templates/${templateId}`);
    return response.data;
  },

  sendTemplateTest: async (templateId: number, emails: string[]): Promise<{ success: boolean }> => {
    const response = await api.post(`/brevo/templates/${templateId}/test`, { emails });
    return response.data;
  },

  // =========================================================================
  // WEBHOOKS
  // =========================================================================

  getWebhooks: async (type: string = 'marketing'): Promise<{ webhooks: BrevoWebhook[] }> => {
    const response = await api.get('/brevo/webhooks', { params: { webhook_type: type } });
    return response.data;
  },

  createWebhook: async (data: {
    url: string;
    events: string[];
    description?: string;
    webhook_type?: string;
  }): Promise<{ success: boolean; webhook_id: number }> => {
    const response = await api.post('/brevo/webhooks', data);
    return response.data;
  },

  deleteWebhook: async (webhookId: number): Promise<{ success: boolean }> => {
    const response = await api.delete(`/brevo/webhooks/${webhookId}`);
    return response.data;
  },

  // =========================================================================
  // STATISTICS
  // =========================================================================

  getStatistics: async (startDate: string, endDate: string, options?: {
    days?: number;
    aggregate?: string;
    tag?: string;
  }): Promise<any> => {
    const response = await api.get('/brevo/statistics', {
      params: { start_date: startDate, end_date: endDate, ...options }
    });
    return response.data;
  },

  // =========================================================================
  // TRANSACTIONAL EMAIL
  // =========================================================================

  sendTransactionalEmail: async (data: {
    to_email: string;
    to_name?: string;
    subject: string;
    html_content?: string;
    text_content?: string;
    template_id?: number;
    params?: Record<string, any>;
    attachment_url?: string;
    tags?: string;
  }): Promise<{ success: boolean; message_id?: string }> => {
    const params = new URLSearchParams();
    params.append('to_email', data.to_email);
    params.append('subject', data.subject);
    if (data.to_name) params.append('to_name', data.to_name);
    if (data.html_content) params.append('html_content', data.html_content);
    if (data.text_content) params.append('text_content', data.text_content);
    if (data.template_id) params.append('template_id', data.template_id.toString());
    if (data.params) params.append('params', JSON.stringify(data.params));
    if (data.attachment_url) params.append('attachment_url', data.attachment_url);
    if (data.tags) params.append('tags', data.tags);

    const response = await api.post(`/brevo/transactional/send?${params.toString()}`);
    return response.data;
  },

  // =========================================================================
  // REFERENCE DATA
  // =========================================================================

  getSegments: async (): Promise<{ segments: string[] }> => {
    const response = await api.get('/brevo/segments');
    return response.data;
  },

  getCustomerGroups: async (): Promise<{ groups: { id: number; name: string }[] }> => {
    const response = await api.get('/brevo/customer-groups');
    return response.data;
  },
};

// ============================================================================
// GOOGLE ADS TYPES
// ============================================================================

export interface GoogleAdsStatus {
  connected: boolean;
  configured: boolean;
  oauth_completed: boolean;
  customer_id?: string;
  account_name?: string;
  currency?: string;
  timezone?: string;
  error?: string;
}

export interface GoogleAdsConfig {
  is_configured: boolean;
  oauth_completed: boolean;
  credentials: {
    client_id_set: boolean;
    client_secret_set: boolean;
    developer_token_set: boolean;
    customer_id: string | null;
    login_customer_id: string | null;
    refresh_token_set: boolean;
  };
  audience_mappings: GoogleAdsAudienceMapping[];
  rfm_segments: string[];
  campaign_types: { code: string; name: string; description: string }[];
  adscore_thresholds: Record<string, { min: number; max: number; description: string }>;
  default_settings: {
    min_margin_percent: number;
    min_stock: number;
    target_roas: number;
    default_conversion_rate: number;
  };
}

export interface GoogleAdsAudienceMapping {
  rfm_segment: string;
  google_audience_id: number | null;
  audience_name: string | null;
  bid_modifier: number;
  auto_sync: boolean;
  last_sync_at: string | null;
  last_sync_count: number;
}

export interface GoogleAdsCampaign {
  google_campaign_id: number;
  name: string;
  status: string;
  campaign_type: string;
  campaign_subtype?: string;
  daily_budget: number;
  target_roas?: number;
  metrics?: {
    impressions: number;
    clicks: number;
    cost: number;
    conversions: number;
    conversion_value: number;
    roas?: number;
  };
}

export interface GoogleAdsPerformance {
  period: {
    start_date: string;
    end_date: string;
  };
  metrics: {
    impressions: number;
    clicks: number;
    cost: number;
    conversions: number;
    conversion_value: number;
    ctr: number;
    cpc: number;
    roas: number;
    conv_rate: number;
  };
}

export interface GoogleAdsProductScore {
  id: number;
  id_product: number;
  reference: string;
  product_name: string;
  id_category_default: number;
  ad_score: number;
  margin_score: number;
  volume_score: number;
  trend_score: number;
  stock_score: number;
  conversion_score: number;
  margin_percent: number;
  stock_shared: number;
  sales_30d: number;
  sales_trend: number;
  cost_price: number;
  sale_price: number;
  recommendation: 'HIGHLY_RECOMMENDED' | 'RECOMMENDED' | 'NEUTRAL' | 'NOT_RECOMMENDED' | 'EXCLUDE';
  block_reason: string | null;
  suggested_cpc: number | null;
  suggested_daily_budget: number | null;
  calculated_at: string;
  // GMC (Google Merchant Center) status
  gmc_available: boolean | null;
  gmc_status: 'AVAILABLE' | 'INACTIVE' | 'NO_STOCK' | 'LOW_PRICE' | 'NO_CATEGORY' | 'EXCLUDED' | null;
  gmc_reason: string | null;
}

export interface GoogleAdsAlert {
  id: number;
  alert_type: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  title: string;
  message: string | null;
  campaign_id: number | null;
  product_reference: string | null;
  recommended_action: string | null;
  status: 'ACTIVE' | 'ACKNOWLEDGED' | 'RESOLVED';
  created_at: string;
}

// ============================================================================
// GOOGLE ADS API
// ============================================================================

export const googleAdsApi = {
  // =========================================================================
  // STATUS & CONFIG
  // =========================================================================

  getStatus: async (): Promise<GoogleAdsStatus> => {
    const response = await api.get('/google-ads/status');
    return response.data;
  },

  getConfig: async (): Promise<GoogleAdsConfig> => {
    const response = await api.get('/google-ads/config');
    return response.data;
  },

  setCredentials: async (credentials: {
    client_id: string;
    client_secret: string;
    developer_token: string;
    customer_id: string;
    login_customer_id?: string;
  }): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/google-ads/config/credentials', credentials);
    return response.data;
  },

  getOAuthUrl: async (redirectUri: string): Promise<{ authorization_url: string; state: string }> => {
    const response = await api.get('/google-ads/config/oauth-url', {
      params: { redirect_uri: redirectUri }
    });
    return response.data;
  },

  completeOAuth: async (code: string, redirectUri: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/google-ads/config/oauth-complete', {
      code,
      redirect_uri: redirectUri
    });
    return response.data;
  },

  // =========================================================================
  // CAMPAIGNS
  // =========================================================================

  getCampaigns: async (status?: string): Promise<GoogleAdsCampaign[]> => {
    const response = await api.get('/google-ads/campaigns', {
      params: status ? { status } : undefined
    });
    return response.data;
  },

  getLocalCampaigns: async (): Promise<any[]> => {
    const response = await api.get('/google-ads/campaigns/local');
    return response.data;
  },

  pauseCampaign: async (campaignId: number): Promise<{ success: boolean; campaign_id: number; status: string }> => {
    const response = await api.post(`/google-ads/campaigns/${campaignId}/pause`);
    return response.data;
  },

  enableCampaign: async (campaignId: number): Promise<{ success: boolean; campaign_id: number; status: string }> => {
    const response = await api.post(`/google-ads/campaigns/${campaignId}/enable`);
    return response.data;
  },

  // =========================================================================
  // PERFORMANCE
  // =========================================================================

  getPerformanceOverview: async (startDate?: string, endDate?: string): Promise<GoogleAdsPerformance> => {
    const response = await api.get('/google-ads/performance/overview', {
      params: { start_date: startDate, end_date: endDate }
    });
    return response.data;
  },

  getDailyPerformance: async (
    startDate: string,
    endDate: string,
    campaignId?: number
  ): Promise<{ daily_data: any[]; count: number }> => {
    const response = await api.get('/google-ads/performance/daily', {
      params: { start_date: startDate, end_date: endDate, campaign_id: campaignId }
    });
    return response.data;
  },

  // =========================================================================
  // PRODUCTS & ADSCORE
  // =========================================================================

  getProductScores: async (options?: {
    recommendation?: string;
    gmc_status?: string;
    min_score?: number;
    category_id?: number;
    limit?: number;
    offset?: number;
  }): Promise<{ products: GoogleAdsProductScore[]; total: number }> => {
    const response = await api.get('/google-ads/products/scores', { params: options });
    return response.data;
  },

  recalculateScores: async (): Promise<{ success: boolean; message: string; status: string }> => {
    const response = await api.post('/google-ads/products/recalculate');
    return response.data;
  },

  updateGmcStatus: async (): Promise<{
    success: boolean;
    message: string;
    products_checked: number;
    available: number;
    unavailable: number;
    by_status: Record<string, number>;
  }> => {
    const response = await api.post('/google-ads/products/update-gmc-status');
    return response.data;
  },

  getProductRecommendations: async (
    budget: number,
    campaignType?: string,
    limit?: number
  ): Promise<{ budget: number; campaign_type?: string; products: any[]; total_suggested_budget: number }> => {
    const response = await api.get('/google-ads/products/recommendations', {
      params: { budget, campaign_type: campaignType, limit }
    });
    return response.data;
  },

  // =========================================================================
  // AUDIENCES (RFM REMARKETING)
  // =========================================================================

  getAudienceMappings: async (): Promise<{ mappings: GoogleAdsAudienceMapping[]; rfm_segments: string[] }> => {
    const response = await api.get('/google-ads/audiences/mappings');
    return response.data;
  },

  setAudienceMapping: async (mapping: {
    rfm_segment: string;
    google_audience_id: number;
    audience_name: string;
    bid_modifier?: number;
    auto_sync?: boolean;
  }): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/google-ads/audiences/mappings', mapping);
    return response.data;
  },

  // =========================================================================
  // ALERTS
  // =========================================================================

  getAlerts: async (options?: {
    status?: string;
    severity?: string;
    limit?: number;
  }): Promise<GoogleAdsAlert[]> => {
    const response = await api.get('/google-ads/alerts', { params: options });
    return response.data;
  },

  acknowledgeAlert: async (alertId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/google-ads/alerts/${alertId}/acknowledge`);
    return response.data;
  },

  resolveAlert: async (alertId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/google-ads/alerts/${alertId}/resolve`);
    return response.data;
  },

  // =========================================================================
  // REFERENCE DATA
  // =========================================================================

  getCampaignTypes: async (): Promise<{ campaign_types: { code: string; name: string; description: string }[] }> => {
    const response = await api.get('/google-ads/campaign-types');
    return response.data;
  },

  getAdscoreThresholds: async (): Promise<{ thresholds: Record<string, { min: number; max: number; description: string }> }> => {
    const response = await api.get('/google-ads/adscore-thresholds');
    return response.data;
  },

  // =========================================================================
  // BUDGET OPTIMIZATION
  // =========================================================================

  getBudgetRecommendations: async (budget: number): Promise<any> => {
    const response = await api.get('/google-ads/budget/recommendations', {
      params: { budget }
    });
    return response.data;
  },

  calculateMaxCpc: async (salePrice: number, costPrice: number, conversionRate?: number, targetRoas?: number): Promise<{
    sale_price: number;
    cost_price: number;
    profit: number;
    margin_percent: number;
    conversion_rate: number;
    target_roas: number;
    max_cpc: number;
    cpc_cap: number;
    suggested_cpc: number;
  }> => {
    const response = await api.post('/google-ads/budget/calculate-cpc', null, {
      params: {
        sale_price: salePrice,
        cost_price: costPrice,
        conversion_rate: conversionRate,
        target_roas: targetRoas
      }
    });
    return response.data;
  },

  getBudgetDistribution: async (budget: number, maxProducts?: number): Promise<any> => {
    const response = await api.get('/google-ads/budget/distribution', {
      params: { budget, max_products: maxProducts }
    });
    return response.data;
  },

  // =========================================================================
  // REMARKETING / AUDIENCES (Extended)
  // =========================================================================

  getRfmSegments: async (): Promise<{
    total_segments: number;
    mapped_segments: number;
    unmapped_segments: number;
    pending_sync: number;
    total_members_synced: number;
    segments: GoogleAdsRfmSegment[];
  }> => {
    const response = await api.get('/google-ads/audiences/segments');
    return response.data;
  },

  getSegmentDetails: async (segmentName: string): Promise<{
    segment: string;
    config: any;
    status: any;
  }> => {
    const response = await api.get(`/google-ads/audiences/segments/${encodeURIComponent(segmentName)}`);
    return response.data;
  },

  createCustomerMatchList: async (segmentName: string, description?: string): Promise<{
    success: boolean;
    segment: string;
    google_list_id: number;
    message: string;
  }> => {
    const response = await api.post(`/google-ads/audiences/segments/${encodeURIComponent(segmentName)}/create`, null, {
      params: { description }
    });
    return response.data;
  },

  syncSegment: async (segmentName: string, customerGroupId?: number, createIfMissing: boolean = true): Promise<{
    success: boolean;
    segment: string;
    audience_id: number | null;
    customers_synced: number;
    message: string;
    synced_at: string;
  }> => {
    const response = await api.post(`/google-ads/audiences/segments/${encodeURIComponent(segmentName)}/sync`, null, {
      params: { customer_group_id: customerGroupId, create_if_missing: createIfMissing }
    });
    return response.data;
  },

  syncAllSegments: async (customerGroupId?: number, createIfMissing: boolean = false): Promise<{
    success: boolean;
    total_segments: number;
    segments_synced: number;
    total_customers: number;
    started_at: string;
    finished_at: string;
    segment_results: any[];
  }> => {
    const response = await api.post('/google-ads/audiences/sync-all', null, {
      params: { customer_group_id: customerGroupId, create_if_missing: createIfMissing }
    });
    return response.data;
  },

  updateBidModifier: async (segmentName: string, bidModifier: number): Promise<{
    success: boolean;
    segment: string;
    new_bid_modifier: number;
    display: string;
  }> => {
    const response = await api.put(`/google-ads/audiences/segments/${encodeURIComponent(segmentName)}/bid-modifier`, null, {
      params: { bid_modifier: bidModifier }
    });
    return response.data;
  },

  applyAudiencesToCampaign: async (campaignId: number, segments?: string[]): Promise<{
    success: boolean;
    campaign_id: number;
    modifiers_applied: any[];
    errors: string[];
  }> => {
    const response = await api.post(`/google-ads/audiences/apply-to-campaign/${campaignId}`, null, {
      params: { segments }
    });
    return response.data;
  },

  // =========================================================================
  // ALERTS (Extended)
  // =========================================================================

  getAlertsSummary: async (): Promise<{
    by_severity: Record<string, number>;
    by_status: Record<string, number>;
    critical_active: any[];
    total_active: number;
  }> => {
    const response = await api.get('/google-ads/alerts/summary');
    return response.data;
  },

  checkAlerts: async (): Promise<{
    checked_at: string;
    alerts_created: number;
    alerts_resolved: number;
    checks_performed: string[];
  }> => {
    const response = await api.post('/google-ads/alerts/check');
    return response.data;
  },

  dismissAlert: async (alertId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/google-ads/alerts/${alertId}/dismiss`);
    return response.data;
  },

  executeAlertAction: async (alertId: number): Promise<{
    success: boolean;
    action_taken: string;
    result: any;
  }> => {
    const response = await api.post(`/google-ads/alerts/${alertId}/execute-action`);
    return response.data;
  },

  // =========================================================================
  // AD TEXT GENERATION (AI)
  // =========================================================================

  generateAdTexts: async (params: {
    product_name: string;
    product_reference: string;
    category?: string;
    brand?: string;
    price?: number;
    features?: string[];
    stock_status?: string;
    shipping?: string;
    model?: string;
  }): Promise<GoogleAdsGeneratedTexts> => {
    const response = await api.post('/google-ads/ads/generate-texts', params);
    return response.data;
  },

  regenerateText: async (params: {
    original_text: string;
    text_type: string;
    max_chars: number;
    focus: string;
    product_context: string;
    model?: string;
  }): Promise<GoogleAdsGeneratedText> => {
    const response = await api.post('/google-ads/ads/regenerate-text', params);
    return response.data;
  },

  getTextLimits: async (): Promise<{
    short_title: number;
    long_title: number;
    description: number;
    display_path: number;
    business_name: number;
  }> => {
    const response = await api.get('/google-ads/ads/text-limits');
    return response.data;
  },

  validateAdText: async (text: string, textType: string, maxChars: number): Promise<{
    text: string;
    char_count: number;
    max_chars: number;
    valid: boolean;
    errors: string[];
    warnings: string[];
  }> => {
    const response = await api.post('/google-ads/ads/validate-text', null, {
      params: { text, text_type: textType, max_chars: maxChars }
    });
    return response.data;
  },

  // =====================================================================
  // Campaign Optimizer
  // =====================================================================

  analyzeOptimizations: async (days: number = 30): Promise<GoogleAdsOptimizationAnalysis> => {
    const response = await api.get('/google-ads/optimizer/analyze', {
      params: { days }
    });
    return response.data;
  },

  getOptimizationRecommendations: async (options?: {
    status?: 'pending' | 'approved' | 'rejected' | 'applied';
    priority?: 1 | 2 | 3 | 4;
    recommendation_type?: string;
    limit?: number;
  }): Promise<{
    count: number;
    recommendations: GoogleAdsRecommendation[];
  }> => {
    const response = await api.get('/google-ads/optimizer/recommendations', { params: options });
    return response.data;
  },

  getOptimizationSummary: async (): Promise<GoogleAdsOptimizationSummary> => {
    const response = await api.get('/google-ads/optimizer/summary');
    return response.data;
  },

  approveRecommendation: async (recommendationId: number): Promise<{ success: boolean; id: number; status: string }> => {
    const response = await api.post(`/google-ads/optimizer/recommendations/${recommendationId}/approve`);
    return response.data;
  },

  rejectRecommendation: async (recommendationId: number): Promise<{ success: boolean; id: number; status: string }> => {
    const response = await api.post(`/google-ads/optimizer/recommendations/${recommendationId}/reject`);
    return response.data;
  },

  executeRecommendation: async (recommendationId: number): Promise<{
    success: boolean;
    action?: string;
    error?: string;
  }> => {
    const response = await api.post(`/google-ads/optimizer/recommendations/${recommendationId}/execute`);
    return response.data;
  },
};

// Types for new features
export interface GoogleAdsRfmSegment {
  name: string;
  description: string;
  bid_modifier: number;
  bid_modifier_display: string;
  strategy: string;
  priority: number;
  audience_name_template: string;
  google_audience_id: number | null;
  audience_name: string | null;
  is_mapped: boolean;
  auto_sync: boolean;
  last_sync_at: string | null;
  member_count: number;
}

// Ad Text Generation Types
export interface GoogleAdsGeneratedText {
  text: string;
  char_count: number;
  max_chars: number;
  focus: string;
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export interface GoogleAdsGeneratedTexts {
  short_titles: GoogleAdsGeneratedText[];
  long_titles: GoogleAdsGeneratedText[];
  descriptions: GoogleAdsGeneratedText[];
  product_name: string;
  product_reference: string;
  generation_cost_usd: number;
  summary: {
    total_generated: number;
    valid_count: number;
    invalid_count: number;
  };
}

// Campaign Optimizer Types
export interface GoogleAdsRecommendation {
  id: number;
  type: string;
  priority: number;
  priority_label: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  title: string;
  description: string;
  estimated_impact_monthly: number;
  estimated_roas_change: number;
  confidence_score: number;
  campaign_id: number | null;
  campaign_name: string | null;
  product_id: number | null;
  product_reference: string | null;
  current_value: string | number;
  recommended_value: string | number;
  supporting_data: Record<string, unknown>;
  action_code: string;
  status: 'pending' | 'approved' | 'rejected' | 'applied' | 'failed';
  created_at: string;
  applied_at: string | null;
}

export interface GoogleAdsOptimizationAnalysis {
  analysis_date: string;
  period_days: number;
  campaigns_analyzed: number;
  products_analyzed: number;
  total_recommendations: number;
  estimated_monthly_impact: number;
  recommendations_by_priority: {
    CRITICAL: number;
    HIGH: number;
    MEDIUM: number;
    LOW: number;
  };
  recommendations_by_type: Record<string, number>;
  recommendations: GoogleAdsRecommendation[];
  error?: string;
}

export interface GoogleAdsOptimizationSummary {
  total: number;
  pending: number;
  applied: number;
  rejected: number;
  potential_monthly_impact: number;
  realized_monthly_impact: number;
  by_priority: Record<number, number>;
  by_type: Record<string, number>;
}

// ============================================================================
// ITSYS IMAGES API - Imágenes de productos ITSys
// ============================================================================

export interface ItsysImageInfo {
  filename: string;
  path: string;
  size: string;
  width: number;
  height: number;
  is_main: boolean;
  index: number | null;
  variant: string | null;
  url: string;
}

export interface ItsysProductImagesResponse {
  found: boolean;
  reference: string;
  category: string | null;
  folder_name: string | null;
  images: ItsysImageInfo[];
  thumbnail_url: string | null;
  best_quality_url: string | null;
  unique_images_count: number;
}

export interface ItsysBestImagesResponse {
  found: boolean;
  reference: string;
  category?: string;
  images: {
    url: string;
    path: string;
    width: number;
    height: number;
    is_main: boolean;
    index: number | null;
  }[];
}

export interface ItsysStatsResponse {
  total_products: number;
  total_images: number;
  categories: Record<string, number>;
  base_path: string | null;
}

export const itsysImagesApi = {
  /**
   * Busca imágenes disponibles para una referencia de producto ITSys
   */
  async search(reference: string): Promise<ItsysProductImagesResponse> {
    const response = await api.get(`/itsys-images/search/${encodeURIComponent(reference)}`);
    return response.data;
  },

  /**
   * Busca múltiples productos que coincidan con el término
   */
  async searchMultiple(query: string, limit: number = 5): Promise<ItsysProductImagesResponse[]> {
    const response = await api.get('/itsys-images/search', {
      params: { q: query, limit }
    });
    return response.data;
  },

  /**
   * Obtiene las mejores imágenes de un producto para subir
   */
  async getBestImages(reference: string, maxImages: number = 5): Promise<ItsysBestImagesResponse> {
    const response = await api.get(`/itsys-images/best/${encodeURIComponent(reference)}`, {
      params: { max_images: maxImages }
    });
    return response.data;
  },

  /**
   * Obtiene estadísticas del índice de imágenes
   */
  async getStats(): Promise<ItsysStatsResponse> {
    const response = await api.get('/itsys-images/stats');
    return response.data;
  },

  /**
   * Reconstruye el índice de imágenes
   */
  async rebuildIndex(): Promise<{ success: boolean; products_indexed: number; message: string }> {
    const response = await api.post('/itsys-images/rebuild-index');
    return response.data;
  },

  /**
   * Genera la URL completa para una imagen
   */
  getImageUrl(path: string): string {
    return `/api/itsys-images/file/${path}`;
  }
};

// ============================================================================
// WHATSAPP API TYPES
// ============================================================================

export interface WhatsAppStatus {
  configured: boolean;
  connected: boolean;
  whatsapp_from: string | null;
  account_sid: string | null;
  error?: string;
}

export interface WhatsAppConfig {
  is_configured: boolean;
  whatsapp_from: string | null;
  invoice_notifications_enabled: boolean;
  order_tracking_enabled: boolean;
  marketing_enabled: boolean;
  total_contacts: number;
  consented_contacts: number;
}

export interface WhatsAppContact {
  id: number;
  customer_id: number | null;
  phone: string;
  phone_normalized: string;
  customer_name: string | null;
  customer_email: string | null;
  customer_type: string | null;
  consent: number;
  consent_date: string | null;
  consent_source: string | null;
  valid_whatsapp: number | null;
  validation_date: string | null;
  last_message_at: string | null;
  opt_out: number;
  opt_out_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface WhatsAppTemplate {
  id: number;
  template_sid: string | null;
  name: string;
  description: string | null;
  template_type: string;
  category: string | null;
  language: string;
  body_template: string;
  variables: string[] | null;
  is_approved: number;
  active: number;
  created_at: string;
  updated_at: string;
}

export interface WhatsAppCampaign {
  id: number;
  name: string;
  description: string | null;
  template_id: number | null;
  target_segment: string | null;
  target_customer_type: string | null;
  status: string;
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  total_recipients: number;
  sent_count: number;
  delivered_count: number;
  read_count: number;
  failed_count: number;
  created_at: string;
  updated_at: string;
}

export interface WhatsAppMessageStats {
  total: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  delivery_rate: number;
  read_rate: number;
}

// ============================================================================
// WHATSAPP API
// ============================================================================

export const whatsappApi = {
  // =========================================================================
  // STATUS & CONFIG
  // =========================================================================

  getStatus: async (): Promise<WhatsAppStatus> => {
    const response = await api.get('/whatsapp/status');
    return response.data;
  },

  getConfig: async (): Promise<WhatsAppConfig> => {
    const response = await api.get('/whatsapp/config');
    return response.data;
  },

  setCredentials: async (credentials: {
    account_sid: string;
    auth_token: string;
    whatsapp_from: string;
  }): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/whatsapp/config/credentials', credentials);
    return response.data;
  },

  updateSettings: async (settings: {
    invoice_notifications?: boolean;
    order_tracking?: boolean;
  }): Promise<{ success: boolean; invoice_notifications_enabled: boolean; order_tracking_enabled: boolean; marketing_enabled: boolean }> => {
    const response = await api.post('/whatsapp/config/settings', settings);
    return response.data;
  },

  // =========================================================================
  // CONTACTS
  // =========================================================================

  getContacts: async (options?: {
    consent?: boolean;
    customer_type?: string;
    valid_whatsapp?: boolean;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ contacts: WhatsAppContact[]; total: number }> => {
    const response = await api.get('/whatsapp/contacts', { params: options });
    return response.data;
  },

  createContact: async (contact: {
    phone: string;
    customer_name?: string;
    customer_id?: number;
    customer_email?: string;
    customer_type?: string;
    consent?: boolean;
    consent_source?: string;
  }): Promise<{ success: boolean; contact_id: number }> => {
    const response = await api.post('/whatsapp/contacts', contact);
    return response.data;
  },

  updateConsent: async (contactId: number, consent: boolean, source?: string): Promise<{ success: boolean }> => {
    const response = await api.put(`/whatsapp/contacts/${contactId}/consent`, null, {
      params: { consent, source }
    });
    return response.data;
  },

  validateContact: async (contactId: number): Promise<{ success: boolean; valid: boolean; phone: string }> => {
    const response = await api.post(`/whatsapp/contacts/${contactId}/validate`);
    return response.data;
  },

  importContacts: async (contacts: Array<{
    phone: string;
    customer_name?: string;
    customer_id?: number;
    customer_email?: string;
    customer_type?: string;
    consent?: boolean;
    consent_source?: string;
  }>, defaultConsent: boolean = true): Promise<{ success: boolean; imported: number; skipped: number; errors: string[] }> => {
    const response = await api.post('/whatsapp/contacts/import', {
      contacts,
      default_consent: defaultConsent
    });
    return response.data;
  },

  importFromPrestaShop: async (customerType?: string, withConsent?: boolean): Promise<{
    success: boolean;
    imported: number;
    skipped: number;
    total_found: number;
    errors: string[];
  }> => {
    const response = await api.post('/whatsapp/contacts/import-from-prestashop', null, {
      params: { customer_type: customerType, with_consent: withConsent }
    });
    return response.data;
  },

  // =========================================================================
  // TEMPLATES
  // =========================================================================

  getTemplates: async (templateType?: string): Promise<WhatsAppTemplate[]> => {
    const response = await api.get('/whatsapp/templates', {
      params: templateType ? { template_type: templateType } : undefined
    });
    return response.data;
  },

  getTemplate: async (templateId: number): Promise<WhatsAppTemplate> => {
    const response = await api.get(`/whatsapp/templates/${templateId}`);
    return response.data;
  },

  createTemplate: async (template: {
    name: string;
    template_type: string;
    body_template: string;
    category?: string;
    variables?: string[];
    description?: string;
    language?: string;
  }): Promise<{ success: boolean; template_id: number }> => {
    const response = await api.post('/whatsapp/templates', template);
    return response.data;
  },

  updateTemplate: async (templateId: number, updates: {
    name?: string;
    template_type?: string;
    body_template?: string;
    category?: string;
    variables?: string[];
    description?: string;
  }): Promise<{ success: boolean }> => {
    const response = await api.put(`/whatsapp/templates/${templateId}`, updates);
    return response.data;
  },

  deleteTemplate: async (templateId: number): Promise<{ success: boolean }> => {
    const response = await api.delete(`/whatsapp/templates/${templateId}`);
    return response.data;
  },

  // =========================================================================
  // CAMPAIGNS
  // =========================================================================

  getCampaigns: async (status?: string, limit?: number): Promise<WhatsAppCampaign[]> => {
    const response = await api.get('/whatsapp/campaigns', {
      params: { status, limit }
    });
    return response.data;
  },

  getCampaign: async (campaignId: number): Promise<WhatsAppCampaign & { stats: WhatsAppMessageStats }> => {
    const response = await api.get(`/whatsapp/campaigns/${campaignId}`);
    return response.data;
  },

  createCampaign: async (campaign: {
    name: string;
    template_id: number;
    target_segment?: string;
    target_customer_type?: string;
    description?: string;
    scheduled_at?: string;
  }): Promise<{ success: boolean; campaign_id: number }> => {
    const response = await api.post('/whatsapp/campaigns', campaign);
    return response.data;
  },

  sendCampaign: async (campaignId: number, templateParams?: Record<string, string>): Promise<{
    success: boolean;
    campaign_id: number;
    total_recipients: number;
    sent_count: number;
    failed_count: number;
  }> => {
    const response = await api.post(`/whatsapp/campaigns/${campaignId}/send`, { template_params: templateParams });
    return response.data;
  },

  // =========================================================================
  // MESSAGES
  // =========================================================================

  sendMessage: async (message: {
    to_phone: string;
    template_name?: string;
    body?: string;
    template_params?: Record<string, string>;
    media_url?: string;
  }): Promise<{ success: boolean; message_sid: string }> => {
    const response = await api.post('/whatsapp/messages/send', message);
    return response.data;
  },

  sendTestMessage: async (toPhone: string, body?: string): Promise<{ success: boolean; message_sid: string }> => {
    const response = await api.post('/whatsapp/messages/test', null, {
      params: { to_phone: toPhone, body }
    });
    return response.data;
  },

  getMessageStats: async (campaignId?: number, days?: number): Promise<WhatsAppMessageStats> => {
    const response = await api.get('/whatsapp/stats', {
      params: { campaign_id: campaignId, days }
    });
    return response.data;
  },
};

// ============================================================================
// SEO ANALYTICS
// ============================================================================

export interface SeoStatus {
  tables_exist: boolean;
  last_sync: {
    status: string;
    started_at: string;
    finished_at: string;
    records_processed: number;
  } | null;
  data_range: {
    from: string;
    to: string;
  } | null;
  record_counts: Record<string, number>;
}

export interface BotSummary {
  by_bot: Array<{
    bot_type: string;
    bot_name: string;
    total_hits: number;
    errors_404: number;
    errors_500: number;
    active_days: number;
  }>;
  by_type: Array<{
    bot_type: string;
    total_hits: number;
    total_errors: number;
  }>;
  trend: {
    this_week: number;
    last_week: number;
    change_percent: number;
  };
  period_days: number;
}

export interface BotActivity {
  date: string;
  bot_name: string;
  bot_type: string;
  hits: number;
}

export interface CrawlError {
  date: string;
  bot_name: string;
  url_path: string;
  url_type: string;
  status_code: number;
  hit_count: number;
  last_hit_at: string;
}

export interface TrafficOverview {
  by_source: Array<{
    source: string;
    visits: number;
    unique_visitors: number;
  }>;
  totals: {
    total_visits: number;
    total_visitors: number;
  };
  trend: {
    this_week: number;
    last_week: number;
    change_percent: number;
  };
  period_days: number;
}

export interface ConversionSummary {
  by_source: Array<{
    source: string;
    sessions: number;
    conversions: number;
    conversion_rate: number;
  }>;
  totals: {
    total_sessions: number;
    total_conversions: number;
    overall_rate: number;
  };
  period_days: number;
}

export interface AttributionSourceDef {
  category: string;
  label: string;
  color: string;
}

export interface AttributionSummary {
  by_source: Array<{
    source: string;
    label: string;
    color: string;
    category: string;
    orders: number;
    revenue: number;
    avg_order: number;
    confidence: number;
  }>;
  by_category: Array<{
    category: string;
    label: string;
    icon: string;
    orders: number;
    revenue: number;
    percentage: number;
  }>;
  totals: {
    orders: number;
    revenue: number;
    avg_order: number;
  };
  period_days: number;
}

export interface TopCategoryItem {
  prestashop_id: number;
  category_name: string;
  url_path: string;
  visits: number;
  unique_visitors: number;
  sessions: number;
  conversions: number;
  conversion_rate: number;
  revenue: number;
  avg_order_value: number;
}

export interface TopCategoriesResponse {
  categories: TopCategoryItem[];
  totals: {
    visits: number;
    unique_visitors: number;
    sessions: number;
    conversions: number;
    conversion_rate: number;
    revenue: number;
    avg_order_value: number;
  };
  period_days: number;
}

export interface TopProductItem {
  prestashop_id: number;
  reference: string;
  product_name: string;
  url_path: string;
  visits: number;
  unique_visitors: number;
  sessions: number;
  conversions: number;
  conversion_rate: number;
  revenue: number;
  avg_order_value: number;
}

export interface TopProductsResponse {
  products: TopProductItem[];
  totals: {
    visits: number;
    unique_visitors: number;
    sessions: number;
    conversions: number;
    conversion_rate: number;
    revenue: number;
    avg_order_value: number;
  };
  period_days: number;
}

export const seoAnalytics = {
  // Status
  getStatus: async (): Promise<{ success: boolean; data: SeoStatus }> => {
    const response = await api.get('/seo-analytics/status');
    return response.data;
  },

  // Sync
  triggerLogSync: async (daysBack: number = 1): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/seo-analytics/sync/logs', { days_back: daysBack });
    return response.data;
  },

  getSyncStatus: async (): Promise<{ success: boolean; data: any }> => {
    const response = await api.get('/seo-analytics/sync/status');
    return response.data;
  },

  // Bots
  getBotList: async (): Promise<{ success: boolean; data: Array<{ id: number; bot_name: string; bot_type: string; total_hits: number }> }> => {
    const response = await api.get('/seo-analytics/bots/list');
    return response.data;
  },

  getBotSummary: async (days: number = 30, section?: string): Promise<{ success: boolean; data: BotSummary }> => {
    const response = await api.get('/seo-analytics/bots/summary', { params: { days, section } });
    return response.data;
  },

  getBotActivity: async (params: {
    start_date?: string;
    end_date?: string;
    days?: number;
    bot_type?: string;
    bot_id?: number;
    section?: string;
  }): Promise<{ success: boolean; data: BotActivity[] }> => {
    const response = await api.get('/seo-analytics/bots/activity', { params });
    return response.data;
  },

  getCrawlErrors: async (days: number = 7, limit: number = 100, section?: string): Promise<{ success: boolean; data: CrawlError[]; count: number }> => {
    const response = await api.get('/seo-analytics/bots/errors', { params: { days, limit, section } });
    return response.data;
  },

  getTopCrawledUrls: async (days: number = 7, limit: number = 50, section?: string): Promise<{ success: boolean; data: any[]; count: number }> => {
    const response = await api.get('/seo-analytics/bots/top-urls', { params: { days, limit, section } });
    return response.data;
  },

  getHourlyDistribution: async (targetDate?: string, botId?: number, section?: string): Promise<{ success: boolean; data: Array<{ hour: number; hits: number }> }> => {
    const response = await api.get('/seo-analytics/bots/hourly', { params: { target_date: targetDate, bot_id: botId, section } });
    return response.data;
  },

  // Traffic
  getTrafficOverview: async (days: number = 30, section?: string): Promise<{ success: boolean; data: TrafficOverview }> => {
    const response = await api.get('/seo-analytics/traffic/overview', { params: { days, section } });
    return response.data;
  },

  getTrafficBySource: async (params: {
    start_date?: string;
    end_date?: string;
    days?: number;
    source?: string;
    section?: string;
  }): Promise<{ success: boolean; data: any[] }> => {
    const response = await api.get('/seo-analytics/traffic/by-source', { params });
    return response.data;
  },

  getTopLandingPages: async (days: number = 30, source?: string, limit: number = 50, section?: string): Promise<{ success: boolean; data: any[]; count: number }> => {
    const response = await api.get('/seo-analytics/traffic/top-pages', { params: { days, source, limit, section } });
    return response.data;
  },

  getTrafficByType: async (days: number = 30, source?: string, section?: string): Promise<{ success: boolean; data: any[] }> => {
    const response = await api.get('/seo-analytics/traffic/by-type', { params: { days, source, section } });
    return response.data;
  },

  getTopCategories: async (days: number = 30, source?: string, section?: string, limit: number = 20): Promise<{ success: boolean; data: TopCategoriesResponse }> => {
    const response = await api.get('/seo-analytics/traffic/top-categories', { params: { days, source, section, limit } });
    return response.data;
  },

  getTopProducts: async (days: number = 30, source?: string, section?: string, limit: number = 50): Promise<{ success: boolean; data: TopProductsResponse }> => {
    const response = await api.get('/seo-analytics/traffic/top-products', { params: { days, source, section, limit } });
    return response.data;
  },

  // Google Ads
  getAdsCampaigns: async (days: number = 30, limit: number = 50, section?: string): Promise<{ success: boolean; data: any[]; count: number }> => {
    const response = await api.get('/seo-analytics/ads/campaigns', { params: { days, limit, section } });
    return response.data;
  },

  getAdsLandingPages: async (days: number = 30, limit: number = 50, section?: string): Promise<{ success: boolean; data: any[]; count: number }> => {
    const response = await api.get('/seo-analytics/ads/landing-pages', { params: { days, limit, section } });
    return response.data;
  },

  // Conversions
  getConversionSummary: async (days: number = 30, section?: string): Promise<{ success: boolean; data: ConversionSummary }> => {
    const response = await api.get('/seo-analytics/conversions/summary', { params: { days, section } });
    return response.data;
  },

  getConversionsByLanding: async (days: number = 30, limit: number = 50, section?: string): Promise<{ success: boolean; data: any[]; count: number }> => {
    const response = await api.get('/seo-analytics/conversions/by-landing', { params: { days, limit, section } });
    return response.data;
  },

  getConversionsByProduct: async (days: number = 30, limit: number = 50, section?: string): Promise<{ success: boolean; data: any[]; count: number }> => {
    const response = await api.get('/seo-analytics/conversions/by-product', { params: { days, limit, section } });
    return response.data;
  },

  getConversionsByCategory: async (days: number = 30, limit: number = 50, section?: string): Promise<{ success: boolean; data: any[]; count: number }> => {
    const response = await api.get('/seo-analytics/conversions/by-category', { params: { days, limit, section } });
    return response.data;
  },

  getAdsConversions: async (days: number = 30, section?: string): Promise<{ success: boolean; data: any }> => {
    const response = await api.get('/seo-analytics/conversions/ads', { params: { days, section } });
    return response.data;
  },

  getConversionsByDate: async (params: {
    start_date?: string;
    end_date?: string;
    days?: number;
    source?: string;
    section?: string;
  }): Promise<{ success: boolean; data: any[] }> => {
    const response = await api.get('/seo-analytics/conversions/by-date', { params });
    return response.data;
  },

  triggerConversionSync: async (daysBack: number = 30): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/seo-analytics/sync/conversions', { days_back: daysBack });
    return response.data;
  },

  // Attribution (unified from multiple sources)
  getAttributionSummary: async (days: number = 30, section?: string): Promise<{ success: boolean; data: AttributionSummary }> => {
    const response = await api.get('/seo-analytics/attribution/summary', { params: { days, section } });
    return response.data;
  },

  getAttributionSources: async (): Promise<{ success: boolean; data: Record<string, AttributionSourceDef> }> => {
    const response = await api.get('/seo-analytics/attribution/sources');
    return response.data;
  },

  triggerAttributionSync: async (daysBack: number = 30): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/seo-analytics/sync/attribution', { days_back: daysBack });
    return response.data;
  },

  // Google Search Console
  getGscStatus: async (): Promise<{ success: boolean; data: { configured: boolean; has_credentials: boolean; has_token: boolean; site_url: string | null; last_sync: string | null } }> => {
    const response = await api.get('/seo-analytics/gsc/status');
    return response.data;
  },

  setGscCredentials: async (credentials: Record<string, any>, siteUrl?: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/seo-analytics/gsc/credentials', { credentials, site_url: siteUrl });
    return response.data;
  },

  getGscOAuthUrl: async (redirectUri: string): Promise<{ success: boolean; auth_url: string }> => {
    const response = await api.post('/seo-analytics/gsc/oauth', { redirect_uri: redirectUri });
    return response.data;
  },

  completeGscOAuth: async (code: string, redirectUri: string): Promise<{ success: boolean; message: string; has_token: boolean }> => {
    const response = await api.post('/seo-analytics/gsc/oauth', { code, redirect_uri: redirectUri });
    return response.data;
  },

  getGscSites: async (): Promise<{ success: boolean; data: string[] }> => {
    const response = await api.get('/seo-analytics/gsc/sites');
    return response.data;
  },

  setGscSite: async (siteUrl: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/seo-analytics/gsc/site', { site_url: siteUrl });
    return response.data;
  },

  getGscSummary: async (days: number = 30): Promise<{ success: boolean; data: any }> => {
    const response = await api.get('/seo-analytics/gsc/summary', { params: { days } });
    return response.data;
  },

  getGscQueries: async (days: number = 30, limit: number = 50): Promise<{ success: boolean; data: any[]; count: number }> => {
    const response = await api.get('/seo-analytics/gsc/queries', { params: { days, limit } });
    return response.data;
  },

  getGscPages: async (days: number = 30, limit: number = 50): Promise<{ success: boolean; data: any[]; count: number }> => {
    const response = await api.get('/seo-analytics/gsc/pages', { params: { days, limit } });
    return response.data;
  },

  getGscPerformance: async (params: {
    start_date?: string;
    end_date?: string;
    days?: number;
  }): Promise<{ success: boolean; data: any[] }> => {
    const response = await api.get('/seo-analytics/gsc/performance', { params });
    return response.data;
  },

  triggerGscSync: async (daysBack: number = 30): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/seo-analytics/sync/gsc', { days_back: daysBack });
    return response.data;
  },

  // Alerts
  getAlerts: async (status?: string, alertType?: string, limit: number = 50): Promise<{ success: boolean; data: any[]; count: number }> => {
    const response = await api.get('/seo-analytics/alerts', { params: { status, alert_type: alertType, limit } });
    return response.data;
  },

  getAlertCount: async (): Promise<{ success: boolean; data: { info: number; warning: number; error: number; critical: number; total: number } }> => {
    const response = await api.get('/seo-analytics/alerts/count');
    return response.data;
  },

  acknowledgeAlert: async (alertId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/seo-analytics/alerts/${alertId}/acknowledge`);
    return response.data;
  },

  resolveAlert: async (alertId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/seo-analytics/alerts/${alertId}/resolve`);
    return response.data;
  },

  dismissAlert: async (alertId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/seo-analytics/alerts/${alertId}/dismiss`);
    return response.data;
  },

  runAlertChecks: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/seo-analytics/alerts/check');
    return response.data;
  },

  getAlertConfig: async (): Promise<{ success: boolean; data: any[] }> => {
    const response = await api.get('/seo-analytics/alerts/config');
    return response.data;
  },

  updateAlertConfig: async (alertType: string, config: { enabled?: boolean; threshold?: number }): Promise<{ success: boolean; message: string }> => {
    const response = await api.patch(`/seo-analytics/alerts/config/${alertType}`, config);
    return response.data;
  },
};

// ============================================================================
// EMAIL PROMOS API - Gestión de productos promocionados en emails transaccionales
// ============================================================================

export interface PromoProduct {
  id: number;
  prestashop_id: number;
  reference: string;
  name?: string;
  price?: number;
  image_url?: string;
  is_active: boolean;
  priority: number;
  show_in_invoice: boolean;
  show_in_shipping: boolean;
  show_in_welcome: boolean;
  created_at: string;
  updated_at?: string;
  stats?: {
    impressions: number;
    clicks: number;
    ctr: number;
    revenue: number;
  };
}

export interface PromoConfig {
  products_per_email: number;
  attribution_window_days: number;
  tracking_enabled: boolean;
  promo_section_title_es: string;
  promo_section_title_en: string;
  promo_section_title_pt: string;
  promo_section_title_fr: string;
  promo_section_title_de: string;
}

export interface PromoStats {
  period: { from: string; to: string };
  impressions: number;
  clicks: number;
  unique_clicks: number;
  ctr: number;
  conversions: number;
  revenue: number;
  conversion_rate: number;
}

export interface SearchProduct {
  id_product: number;
  reference: string;
  name: string;
  price: number;
  active: boolean;
  image_url: string;
  already_promoted: boolean;
}

export const emailPromosApi = {
  // Products
  getProducts: async (includeInactive: boolean = false): Promise<{ success: boolean; products: PromoProduct[]; total: number }> => {
    const response = await api.get('/email-promos/products', { params: { include_inactive: includeInactive } });
    return response.data;
  },

  addProduct: async (data: { prestashop_id?: number; reference?: string }): Promise<{ success: boolean; id?: number; error?: string }> => {
    const response = await api.post('/email-promos/products', data);
    return response.data;
  },

  updateProduct: async (productId: number, data: {
    is_active?: boolean;
    show_in_invoice?: boolean;
    show_in_shipping?: boolean;
    show_in_welcome?: boolean;
  }): Promise<{ success: boolean }> => {
    const response = await api.put(`/email-promos/products/${productId}`, data);
    return response.data;
  },

  removeProduct: async (productId: number): Promise<{ success: boolean }> => {
    const response = await api.delete(`/email-promos/products/${productId}`);
    return response.data;
  },

  reorderProducts: async (productIds: number[]): Promise<{ success: boolean }> => {
    const response = await api.post('/email-promos/products/reorder', { product_ids: productIds });
    return response.data;
  },

  // Search for products to add
  searchProducts: async (query: string): Promise<{ success: boolean; products: SearchProduct[] }> => {
    const response = await api.get('/email-promos/search-products', { params: { q: query } });
    return response.data;
  },

  // Stats
  getStats: async (fromDate?: string, toDate?: string): Promise<{ success: boolean; summary: PromoStats; by_product: any[] }> => {
    const response = await api.get('/email-promos/stats', { params: { from_date: fromDate, to_date: toDate } });
    return response.data;
  },

  getRecentConversions: async (limit: number = 20): Promise<{ success: boolean; conversions: any[] }> => {
    const response = await api.get('/email-promos/stats/conversions', { params: { limit } });
    return response.data;
  },

  // Config
  getConfig: async (): Promise<{ success: boolean; config: PromoConfig }> => {
    const response = await api.get('/email-promos/config');
    return response.data;
  },

  updateConfig: async (data: Partial<PromoConfig>): Promise<{ success: boolean }> => {
    const response = await api.put('/email-promos/config', data);
    return response.data;
  },

  // Preview & Test
  getPreview: async (customerId?: number, lang: string = 'es'): Promise<{
    success: boolean;
    subject: string;
    html: string;
    products_count: number;
    note: string;
  }> => {
    const response = await api.get('/email-promos/preview', {
      params: { customer_id: customerId, lang }
    });
    return response.data;
  },

  sendTestEmail: async (toEmail: string, customerId?: number, lang: string = 'es'): Promise<{
    success: boolean;
    message: string;
    message_id?: string;
  }> => {
    const response = await api.post('/email-promos/send-test', null, {
      params: { to_email: toEmail, customer_id: customerId, lang }
    });
    return response.data;
  },
};

// ============================================================================
// EMAIL MANAGEMENT - Centro de gestión de emails transaccionales
// ============================================================================

export interface EmailTypeInfo {
  id: string;
  name: string;
  name_en: string;
  description: string;
  description_en: string;
  icon: string;
  color: string;
  is_active: boolean;
  has_promos: boolean;
  has_attachment: boolean;
  languages: string[];
}

export interface EmailPreviewResponse {
  success: boolean;
  email_type: string;
  subject: string;
  html: string;
  promo_products_count: number;
  note: string;
  error?: string;
}

export interface EmailTypeStats {
  sent: number;
  delivered: number;
  opens: number;
  unique_opens: number;
  clicks: number;
  unique_clicks: number;
  hard_bounces: number;
  soft_bounces: number;
  spam_reports: number;
  unsubscribed: number;
  delivery_rate: number;
  open_rate: number;
  click_rate: number;
  bounce_rate?: number;
  blocked?: number;
  invalid?: number;
}

export interface EmailStatsSummaryResponse {
  success: boolean;
  period_days: number;
  start_date: string;
  end_date: string;
  by_type: Record<string, EmailTypeStats>;
  totals: EmailTypeStats;
}

export interface EmailTypeStatsResponse {
  success: boolean;
  email_type: string;
  email_name: string;
  period_days: number;
  start_date: string;
  end_date: string;
  statistics: EmailTypeStats;
}

export interface WelcomeLocalStatsResponse {
  success: boolean;
  period_days: number;
  statistics: {
    total_processed: number;
    sent_successfully: number;
    failed: number;
    pending_last_24h: number;
  };
  pending_customers: Array<{
    customer_id: number;
    name: string;
    email: string;
    lang_iso: string;
    date_add: string;
  }>;
}

export const emailManagementApi = {
  // Get all email types
  getTypes: async (): Promise<{ success: boolean; types: EmailTypeInfo[] }> => {
    const response = await api.get('/email-management/types');
    return response.data;
  },

  // Get email preview
  getPreview: async (emailType: string, lang: string = 'es', orderId?: number): Promise<EmailPreviewResponse> => {
    const response = await api.get(`/email-management/preview/${emailType}`, {
      params: { lang, order_id: orderId }
    });
    return response.data;
  },

  // Send test email
  sendTestEmail: async (emailType: string, toEmail: string, lang: string = 'es', orderId?: number): Promise<{
    success: boolean;
    message: string;
    message_id?: string;
  }> => {
    const response = await api.post('/email-management/send-test', {
      email_type: emailType,
      to_email: toEmail,
      lang,
      order_id: orderId
    });
    return response.data;
  },

  // Get stats summary for all email types
  getStatsSummary: async (days: number = 30): Promise<EmailStatsSummaryResponse> => {
    const response = await api.get('/email-management/stats/summary', {
      params: { days }
    });
    return response.data;
  },

  // Get stats for specific email type
  getTypeStats: async (emailType: string, days: number = 30): Promise<EmailTypeStatsResponse> => {
    const response = await api.get(`/email-management/stats/${emailType}`, {
      params: { days }
    });
    return response.data;
  },

  // Get welcome email local stats
  getWelcomeLocalStats: async (days: number = 30): Promise<WelcomeLocalStatsResponse> => {
    const response = await api.get('/email-management/stats/welcome/local', {
      params: { days }
    });
    return response.data;
  },
};

// ============================================================================
// SHIPPING - Gestión de envíos (alternativa a Outvio)
// ============================================================================

export interface ShippingAddress {
  name: string;
  company?: string;
  street: string;
  street2?: string;
  city: string;
  postal_code: string;
  state?: string;
  country_code: string;
  phone?: string;
  phone_mobile?: string;
  email?: string;
  /** false = no hay un movil valido para avisar por SMS; null = destino no evaluable */
  mobile_ok?: boolean | null;
}

export interface OrderProductDetail {
  id: number;
  name: string;
  reference?: string;
  quantity: number;
  price: number;
  total: number;
  image_url?: string;
}

export interface GroupedOrderInfo {
  parent_order_id: number;
  parent_order_reference: string;
  date_grouped?: string;
}

export interface ShippingOrder {
  id: number;
  reference: string;
  date_add: string;
  current_state: number;
  state_name: string;
  customer_name: string;
  customer_email: string;
  shipping_address: ShippingAddress;
  total: number;
  total_shipping: number;
  weight: number;
  items_count: number;
  carrier_id: number;
  carrier_name: string;
  payment_method: string;
  is_cod: boolean;
  cod_amount: number;
  gift_message?: string;
  dni?: string;
  vat_number?: string;
  total_paid_tax_incl?: number;
  total_paid_tax_excl?: number;
  shipment?: ShipmentData;
  suggested_carrier?: {
    carrier_code: string;
    service_type?: string;
    rule_matched?: string;
  };
  products?: OrderProductDetail[];
  service_point?: {
    name: string;
    address: string;
    city: string;
    postal_code: string;
    country: string;
  } | null;
  // Información de agrupación (envío gratuito por pedido anterior)
  is_grouped?: boolean;
  grouped_with?: GroupedOrderInfo;
}

export interface ShipmentData {
  id: number;
  order_id: number;
  order_reference: string;
  carrier_code: string;
  tracking_number?: string;
  status: string;
  weight: number;
  packages: number;
  rule_matched?: string;
  cod_amount_sent?: number;  // COD real enviado al transportista (para verificación)
  created_at: string;
  shipped_at?: string;
  delivered_at?: string;
  tracking_url?: string;
}

export interface ShippingRule {
  id: number;
  name: string;
  priority: number;
  is_active: boolean;
  conditions: Record<string, any>;
  carrier_code: string;
  service_type?: string;
}

export interface ShippingStats {
  by_status: Record<string, number>;
  by_carrier: Record<string, number>;
  today: number;
  total: number;
}

export interface TrackingEvent {
  status: string;
  status_date: string;
  location?: string;
  description?: string;
}

export interface ShippingNotification {
  id: number;
  type: string;
  email: string;
  sent_at: string;
  brevo_message_id?: string;
}

export interface ShipmentProduct {
  id: number;
  order_id: number;
  product_name: string;
  product_reference?: string;
  quantity: number;
  price: number;
  weight: number;
  image_url?: string;
  is_custom: boolean;
  is_removed: boolean;
  original_product_id?: number;
  created_at?: string;
}

export interface QueueProduct {
  id: number;
  name: string;
  reference?: string;
  quantity: number;
  price: number;
  total: number;
  image_url?: string;
}

export interface QueueItem {
  order_id: number;
  order_reference: string;
  customer_name: string;
  shipping_address: ShippingAddress;
  total: number;
  items_count: number;
  carrier_code: string;
  packages: number;
  weight: number;
  is_cod: boolean;  // Es contrareembolso
  cod_amount: number;  // Importe contrareembolso
  payment_method: string;  // Método de pago
  carrier_notes?: string;  // Notas editables para etiqueta transportista
  with_return?: boolean;  // Envío con retorno (recogida en destino)
  added_at: string;
  suggested_carrier?: {
    carrier_code: string;
    service_type?: string;
    rule_matched?: string;
  };
  products?: QueueProduct[];
  service_point?: {
    name: string;
    address: string;
    city: string;
    postal_code: string;
    country: string;
  } | null;
}

export interface ManualOrderProduct {
  id: number;
  product_name: string;
  product_reference?: string;
  quantity: number;
  price: number;
  weight: number;
  image_url?: string;
  created_at?: string;
}

export interface ManualOrder {
  id: number;
  reference: string;
  custom_reference?: string;  // Referencia del pedido (aparece en etiqueta transportista)
  recipient_name: string;
  recipient_email?: string;
  recipient_phone?: string;
  recipient_nif?: string;
  recipient_company?: string;
  address_street: string;
  address_city: string;
  address_postal_code: string;
  address_province?: string;
  address_country: string;
  warehouse: string;
  payment_method?: string;
  invoice_number?: string;
  content_description?: string;
  total_value: number;
  currency: string;
  carrier_code?: string;
  carrier_message?: string;
  weight: number;
  packages: number;
  is_cod?: boolean;
  cod_amount?: number;
  with_return?: boolean;
  service_point_id?: string;
  service_point_name?: string;
  service_point_address?: string;
  service_point_city?: string;
  service_point_postal_code?: string;
  service_point_country?: string;
  status: string;
  created_at?: string;
  updated_at?: string;
  products: ManualOrderProduct[];
}

export interface ManualOrderRequest {
  recipient_name: string;
  recipient_email?: string;
  recipient_phone?: string;
  recipient_nif?: string;
  recipient_company?: string;
  address_street: string;
  address_city: string;
  address_postal_code: string;
  address_province?: string;
  address_country?: string;
  custom_reference?: string;  // Referencia del pedido (aparece en etiqueta transportista)
  warehouse?: string;
  payment_method?: string;
  invoice_number?: string;
  content_description?: string;
  total_value?: number;
  currency?: string;
  carrier_code?: string;
  carrier_message?: string;
  weight?: number;
  packages?: number;
  is_cod?: boolean;
  cod_amount?: number;
  with_return?: boolean;
  service_point_id?: string;
  service_point_name?: string;
  service_point_address?: string;
  service_point_city?: string;
  service_point_postal_code?: string;
  service_point_country?: string;
}

export interface FactusolClient {
  code: number;
  name: string;
  nif?: string;
  email?: string;
  phone?: string;
  company?: string;
  address: string;
  city: string;
  postal_code: string;
  province?: string;
  /** null = Factusol no tiene país (PAICLI vacío) y no se pudo deducir del texto */
  country: string | null;
  country_source?: 'factusol' | 'inferred' | 'unknown';
}

export interface ReembolsoBatch {
  id: number;
  carrier: string;
  document_ref: string | null;
  settlement_date: string | null;
  email_date: string | null;
  email_subject: string | null;
  total_amount: number;
  line_count: number;
  matched_count: number;
  discrepancy_count: number;
  notfound_count: number;
  bank_collected: number;
  bank_collected_date: string | null;
  bank_collected_amount: number | null;
  bank_notes: string | null;
  raw_kind: string | null;
}

export interface ReembolsoLine {
  id: number;
  batch_id: number;
  carrier: string;
  order_reference: string | null;
  order_ref_int: number | null;
  tracking: string | null;
  recipient: string | null;
  settled_amount: number | null;
  settlement_date: string | null;
  matched_order_id: number | null;
  expected_amount: number | null;
  match_status: 'matched' | 'discrepancy' | 'not_found';
  paid_marked: number;
}

export interface Impago {
  order_id: number;
  order_reference: string;
  carrier: string;
  tracking: string | null;
  cod_amount: number;
  recipient: string | null;
  status: string;
  delivered_at: string;
}

export const reembolsos = {
  dashboard: async (): Promise<any> => {
    const r = await api.get('/reembolsos/dashboard');
    return r.data;
  },
  listBatches: async (carrier?: string, limit = 200): Promise<{ batches: ReembolsoBatch[] }> => {
    const r = await api.get('/reembolsos/batches', { params: { carrier, limit } });
    return r.data;
  },
  batchDetail: async (id: number): Promise<{ batch: ReembolsoBatch; lines: ReembolsoLine[] }> => {
    const r = await api.get(`/reembolsos/batches/${id}`);
    return r.data;
  },
  setBankCollected: async (id: number, body: { collected: boolean; amount?: number | null; date?: string | null; notes?: string | null }): Promise<any> => {
    const r = await api.post(`/reembolsos/batches/${id}/bank-collected`, body);
    return r.data;
  },
  setLinePaid: async (id: number, paid: boolean): Promise<any> => {
    const r = await api.put(`/reembolsos/lines/${id}/paid`, { paid });
    return r.data;
  },
  impagos: async (days = 15, max_days = 90): Promise<{ count: number; total: number; impagos: Impago[] }> => {
    const r = await api.get('/reembolsos/impagos', { params: { days, max_days } });
    return r.data;
  },
  ingest: async (days = 20, limit = 300): Promise<{ scanned: number; nuevas_liquidaciones: number }> => {
    const r = await api.post('/reembolsos/ingest', null, { params: { days, limit } });
    return r.data;
  },
};

export const shipping = {
  // ========================================
  // Pedidos
  // ========================================

  /**
   * Lista pedidos pendientes de envío
   */
  listOrders: async (params?: {
    status?: string;
    include_shipped?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{
    orders: ShippingOrder[];
    total: number;
    page: number;
    per_page: number;
  }> => {
    const response = await api.get('/shipping/orders', { params });
    return response.data;
  },

  /**
   * Lista pedidos que tienen envíos creados en el sistema.
   * Útil para la pestaña de Seguimiento.
   */
  listOrdersWithShipments: async (params?: {
    shipment_status?: string;
    exclude_cancelled?: boolean;
    limit?: number;
    search?: string;
  }): Promise<{
    orders: ShippingOrder[];
    total: number;
    page: number;
    per_page: number;
  }> => {
    const response = await api.get('/shipping/orders-with-shipments', { params });
    return response.data;
  },

  /**
   * Obtiene un pedido por ID
   */
  getOrder: async (orderId: number): Promise<ShippingOrder> => {
    const response = await api.get(`/shipping/orders/${orderId}`);
    return response.data;
  },

  /**
   * Actualiza los datos de un pedido
   */
  updateOrder: async (orderId: number, data: {
    name: string;
    company?: string;
    email: string;
    phone?: string;
    street: string;
    street2?: string;
    city: string;
    postal_code: string;
    country_code: string;
    carrier_message?: string;
    dni?: string;
  }): Promise<{ success: boolean; message: string }> => {
    const response = await api.put(`/shipping/orders/${orderId}`, data);
    return response.data;
  },

  // ========================================
  // Envíos
  // ========================================

  /**
   * Crea un envío para un pedido
   * @param test_mode - Si es true, simula el envío sin llamar a APIs reales
   */
  createShipment: async (data: {
    order_id: number;
    carrier_code?: string;
    service_type?: string;
    packages?: number;
    weight?: number;
    notes?: string;
    test_mode?: boolean;
    with_return?: boolean;
  }): Promise<ShipmentData> => {
    const response = await api.post('/shipping/shipments', data);
    return response.data;
  },

  /**
   * Crea envíos masivos
   * @param test_mode - Si es true, simula los envíos sin llamar a APIs reales
   */
  createShipmentsBulk: async (data: {
    order_ids: number[];
    auto_select_carrier?: boolean;
    test_mode?: boolean;
  }): Promise<{
    total: number;
    success: number;
    failed: number;
    shipments: Array<{ order_id: number; order_reference: string } & ShipmentData>;
    errors: Array<{ order_id: number; order_reference: string; error: string }>;
  }> => {
    const response = await api.post('/shipping/shipments/bulk', data);
    return response.data;
  },

  /**
   * Obtiene un envío por ID
   */
  getShipment: async (shipmentId: number): Promise<ShipmentData & {
    tracking_history: TrackingEvent[];
  }> => {
    const response = await api.get(`/shipping/shipments/${shipmentId}`);
    return response.data;
  },

  /**
   * Descarga la etiqueta de un envío
   */
  downloadLabel: async (shipmentId: number): Promise<Blob> => {
    const response = await api.get(`/shipping/shipments/${shipmentId}/label`, {
      responseType: 'blob'
    });
    return response.data;
  },

  /**
   * Obtiene las etiquetas combinadas de múltiples envíos en un único PDF
   * @param shipmentIds IDs de los envíos
   * @returns Blob del PDF combinado
   */
  getCombinedLabels: async (shipmentIds: number[]): Promise<Blob> => {
    const response = await api.post('/shipping/labels/combined', {
      shipment_ids: shipmentIds
    }, {
      responseType: 'blob'
    });
    return response.data;
  },

  /**
   * Cancela un envío
   * @param shipmentId ID del envío
   * @param revertToQueue Si true, revierte el pedido a la cola de empaquetado
   */
  cancelShipment: async (
    shipmentId: number,
    revertToQueue: boolean = false,
    force: boolean = false
  ): Promise<{
    message: string;
    carrier_code?: string;
    api_cancelled?: boolean;
    order_id?: number;
    reverted_to_queue?: boolean;
    warning?: string;
    forced?: boolean;
    is_test?: boolean;
  }> => {
    const response = await api.delete(`/shipping/shipments/${shipmentId}`, {
      params: { revert_to_queue: revertToQueue, force }
    });
    return response.data;
  },

  /**
   * Actualiza el estado de un envío
   */
  updateStatus: async (shipmentId: number, data: {
    new_status: string;
    customer_email?: string;
    customer_name?: string;
    send_notification?: boolean;
  }): Promise<{
    shipment_id: number;
    old_status: string;
    new_status: string;
    notification?: { success: boolean; message_id?: string };
  }> => {
    const response = await api.post(`/shipping/shipments/${shipmentId}/status`, data);
    return response.data;
  },

  // ========================================
  // Notificaciones
  // ========================================

  /**
   * Envía una notificación manual
   */
  sendNotification: async (shipmentId: number, data: {
    customer_email: string;
    customer_name: string;
    notification_type?: string;
  }): Promise<{
    success: boolean;
    message_id?: string;
    skipped?: boolean;
    reason?: string;
  }> => {
    const response = await api.post(`/shipping/shipments/${shipmentId}/notify`, data);
    return response.data;
  },

  /**
   * Obtiene el historial de notificaciones
   */
  getNotifications: async (shipmentId: number): Promise<{
    shipment_id: number;
    notifications: ShippingNotification[];
  }> => {
    const response = await api.get(`/shipping/shipments/${shipmentId}/notifications`);
    return response.data;
  },

  // ========================================
  // Reglas
  // ========================================

  /**
   * Lista reglas de selección de transportista
   */
  listRules: async (activeOnly: boolean = false): Promise<ShippingRule[]> => {
    const response = await api.get('/shipping/rules', {
      params: { active_only: activeOnly }
    });
    return response.data;
  },

  /**
   * Crea una nueva regla
   */
  createRule: async (data: Omit<ShippingRule, 'id'>): Promise<ShippingRule> => {
    const response = await api.post('/shipping/rules', data);
    return response.data;
  },

  /**
   * Actualiza una regla
   */
  updateRule: async (ruleId: number, data: Omit<ShippingRule, 'id'>): Promise<ShippingRule> => {
    const response = await api.put(`/shipping/rules/${ruleId}`, data);
    return response.data;
  },

  /**
   * Elimina una regla
   */
  deleteRule: async (ruleId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/shipping/rules/${ruleId}`);
    return response.data;
  },

  // ========================================
  // Cola de envío
  // ========================================

  /**
   * Lista pedidos en la cola de envío
   */
  listQueue: async (): Promise<{
    items: QueueItem[];
    total: number;
  }> => {
    const response = await api.get('/shipping/queue');
    return response.data;
  },

  /**
   * Añade pedidos a la cola de envío
   * @param order_ids - IDs de pedidos de PrestaShop
   * @param manual_order_ids - IDs de pedidos manuales (envíos manuales)
   */
  addToQueue: async (data: {
    order_ids?: number[];
    manual_order_ids?: number[];
    carrier_code?: string;
    packages?: number;
    weight?: number;
  }): Promise<{
    success: boolean;
    added: number;
    message: string;
  }> => {
    const response = await api.post('/shipping/queue', data);
    return response.data;
  },

  /**
   * Elimina un pedido de la cola
   */
  removeFromQueue: async (orderId: number): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.delete(`/shipping/queue/${orderId}`);
    return response.data;
  },

  /**
   * Elimina múltiples pedidos de la cola
   */
  removeMultipleFromQueue: async (orderIds: number[]): Promise<{
    success: boolean;
    removed: number;
    message: string;
  }> => {
    const response = await api.delete('/shipping/queue', {
      params: { order_ids: orderIds }
    });
    return response.data;
  },

  /**
   * Actualiza un item de la cola (transportista, bultos, peso, reembolso)
   */
  updateQueueItem: async (orderId: number, data: {
    carrier_code?: string;
    packages?: number;
    weight?: number;
    cod_amount?: number;  // Importe contrareembolso (0 = no es COD)
    carrier_notes?: string;  // Notas para etiqueta transportista
    with_return?: boolean;  // Envío con retorno (recogida en destino)
  }): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.put(`/shipping/queue/${orderId}`, data);
    return response.data;
  },

  /**
   * Obtiene los IDs de pedidos en la cola (separados por tipo)
   */
  getQueueOrderIds: async (): Promise<{
    order_ids: number[];  // IDs de pedidos de PrestaShop en cola
    manual_order_ids: number[];  // IDs de pedidos manuales en cola
    count: number;
  }> => {
    const response = await api.get('/shipping/queue/ids');
    return response.data;
  },

  // ========================================
  // Estadísticas y utilidades
  // ========================================

  /**
   * Obtiene estadísticas del sistema de shipping
   */
  getStats: async (): Promise<ShippingStats> => {
    const response = await api.get('/shipping/stats');
    return response.data;
  },

  /**
   * Prueba conexión con los transportistas
   */
  testConnections: async (): Promise<{
    mrw: { connected: boolean; franquicia?: string; environment?: string; error?: string };
    gls: { connected: boolean; sender_name?: string; error?: string };
    ctt: { connected: boolean; center_code?: string; environment?: string; error?: string };
    dhl: { connected: boolean; customer_code?: string; environment?: string; error?: string };
  }> => {
    const response = await api.get('/shipping/test/connection');
    return response.data;
  },

  /**
   * Cierre de día DHL - Genera listado y reporte PDF
   */
  dhlEndDay: async (): Promise<{
    success: boolean;
    shipment_count: number;
    shipments: Array<{ Origin: string; Customer: string; Year: string; Tracking: string }>;
    report_id: number | null;
  }> => {
    const response = await api.post('/shipping/dhl/endday');
    return response.data;
  },

  /**
   * Lista reportes de cierre de día DHL
   */
  dhlEndDayReports: async (): Promise<{
    reports: Array<{
      id: number;
      report_date: string;
      shipment_count: number;
      trackings: string[];
      created_at: string;
    }>;
  }> => {
    const response = await api.get('/shipping/dhl/endday/reports');
    return response.data;
  },

  /**
   * Descarga PDF de un reporte de cierre de día DHL
   */
  dhlEndDayReportPdf: async (reportId: number): Promise<Blob> => {
    const response = await api.get(`/shipping/dhl/endday/reports/${reportId}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Solicita recogida DHL en almacén JVS para el siguiente día laborable
   */
  dhlRequestPickup: async (): Promise<{
    success: boolean;
    pickup_id: number | null;
    pickup_date: string;
    message: string;
  }> => {
    const response = await api.post('/shipping/dhl/pickup');
    return response.data;
  },

  // ========================================
  // Productos del envío (gestión local, no modifica PrestaShop)
  // ========================================

  /**
   * Obtiene las modificaciones de productos para un pedido
   */
  getShipmentProducts: async (orderId: number): Promise<{
    order_id: number;
    modifications: ShipmentProduct[];
  }> => {
    const response = await api.get(`/shipping/orders/${orderId}/shipment-products`);
    return response.data;
  },

  /**
   * Añade un producto personalizado al envío
   */
  addShipmentProduct: async (orderId: number, data: {
    product_name: string;
    product_reference?: string;
    quantity?: number;
    price?: number;
    weight?: number;
    image_url?: string;
  }): Promise<ShipmentProduct> => {
    const response = await api.post(`/shipping/orders/${orderId}/shipment-products`, data);
    return response.data;
  },

  /**
   * Actualiza un producto personalizado del envío
   */
  updateShipmentProduct: async (orderId: number, productId: number, data: {
    product_name: string;
    product_reference?: string;
    quantity?: number;
    price?: number;
    weight?: number;
    image_url?: string;
  }): Promise<ShipmentProduct> => {
    const response = await api.put(`/shipping/orders/${orderId}/shipment-products/${productId}`, data);
    return response.data;
  },

  /**
   * Elimina un producto personalizado del envío
   */
  deleteShipmentProduct: async (orderId: number, productId: number): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.delete(`/shipping/orders/${orderId}/shipment-products/${productId}`);
    return response.data;
  },

  /**
   * Marca un producto de PrestaShop como excluido del envío
   */
  markProductRemoved: async (orderId: number, originalProductId: number): Promise<{
    success: boolean;
    message: string;
    modification_id: number;
  }> => {
    const response = await api.post(`/shipping/orders/${orderId}/shipment-products/mark-removed`, {
      original_product_id: originalProductId
    });
    return response.data;
  },

  /**
   * Restaura un producto de PrestaShop excluido
   */
  restoreProduct: async (orderId: number, originalProductId: number): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.post(`/shipping/orders/${orderId}/shipment-products/restore`, {
      original_product_id: originalProductId
    });
    return response.data;
  },

  /**
   * Modifica la cantidad de un producto de PrestaShop para el envío
   */
  updateProductQuantity: async (orderId: number, originalProductId: number, quantity: number): Promise<{
    success: boolean;
    message: string;
    modification_id: number;
  }> => {
    const response = await api.put(`/shipping/orders/${orderId}/shipment-products/quantity/${originalProductId}`, {
      quantity
    });
    return response.data;
  },

  /**
   * Elimina todas las modificaciones de productos de un pedido
   */
  clearShipmentProducts: async (orderId: number): Promise<{
    success: boolean;
    message: string;
    removed: number;
  }> => {
    const response = await api.delete(`/shipping/orders/${orderId}/shipment-products/clear`);
    return response.data;
  },

  // ========================================
  // Búsqueda de clientes (Factusol)
  // ========================================

  /**
   * Busca clientes en Factusol por nombre, NIF, email o teléfono
   */
  searchClients: async (query: string, limit: number = 20): Promise<FactusolClient[]> => {
    const response = await api.get('/shipping/clients/search', {
      params: { q: query, limit }
    });
    return response.data;
  },

  /**
   * Obtiene un cliente de Factusol por código
   */
  getClient: async (clientCode: number): Promise<FactusolClient> => {
    const response = await api.get(`/shipping/clients/${clientCode}`);
    return response.data;
  },

  // ========================================
  // Pedidos manuales (sin PrestaShop)
  // ========================================

  /**
   * Lista pedidos manuales
   */
  listManualOrders: async (params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    orders: ManualOrder[];
    total: number;
    page: number;
    per_page: number;
  }> => {
    const response = await api.get('/shipping/manual-orders', { params });
    return response.data;
  },

  /**
   * Obtiene un pedido manual por ID
   */
  getManualOrder: async (orderId: number): Promise<ManualOrder> => {
    const response = await api.get(`/shipping/manual-orders/${orderId}`);
    return response.data;
  },

  /**
   * Crea un nuevo pedido manual
   */
  createManualOrder: async (data: ManualOrderRequest): Promise<ManualOrder> => {
    const response = await api.post('/shipping/manual-orders', data);
    return response.data;
  },

  /**
   * Actualiza un pedido manual
   */
  updateManualOrder: async (orderId: number, data: ManualOrderRequest): Promise<ManualOrder> => {
    const response = await api.put(`/shipping/manual-orders/${orderId}`, data);
    return response.data;
  },

  /**
   * Elimina un pedido manual
   */
  deleteManualOrder: async (orderId: number): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.delete(`/shipping/manual-orders/${orderId}`);
    return response.data;
  },

  /**
   * Actualiza el estado de un pedido manual
   */
  updateManualOrderStatus: async (orderId: number, status: string): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.put(`/shipping/manual-orders/${orderId}/status`, null, {
      params: { status }
    });
    return response.data;
  },

  // ========================================
  // Productos de pedidos manuales
  // ========================================

  /**
   * Obtiene los productos de un pedido manual
   */
  getManualOrderProducts: async (orderId: number): Promise<{
    order_id: number;
    products: ManualOrderProduct[];
  }> => {
    const response = await api.get(`/shipping/manual-orders/${orderId}/products`);
    return response.data;
  },

  /**
   * Añade un producto a un pedido manual
   */
  addManualOrderProduct: async (orderId: number, data: {
    product_name: string;
    product_reference?: string;
    quantity?: number;
    price?: number;
    weight?: number;
    image_url?: string;
  }): Promise<{
    success: boolean;
    product_id: number;
    message: string;
  }> => {
    const response = await api.post(`/shipping/manual-orders/${orderId}/products`, data);
    return response.data;
  },

  /**
   * Actualiza un producto de un pedido manual
   */
  updateManualOrderProduct: async (orderId: number, productId: number, data: {
    product_name: string;
    product_reference?: string;
    quantity?: number;
    price?: number;
    weight?: number;
    image_url?: string;
  }): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.put(`/shipping/manual-orders/${orderId}/products/${productId}`, data);
    return response.data;
  },

  /**
   * Elimina un producto de un pedido manual
   */
  deleteManualOrderProduct: async (orderId: number, productId: number): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.delete(`/shipping/manual-orders/${orderId}/products/${productId}`);
    return response.data;
  },

  // ========================================================================
  // PACKAGES - Gestión de paquetes estilo Outvio
  // ========================================================================

  /**
   * Obtiene los tamaños de paquete disponibles
   */
  getPackageSizes: async (): Promise<{
    sizes: PackageSize[];
  }> => {
    const response = await api.get('/shipping/packages/sizes');
    return response.data;
  },

  /**
   * Obtiene todos los paquetes de un pedido con sus productos
   */
  getOrderPackages: async (orderId: number): Promise<PackagesResponse> => {
    const response = await api.get(`/shipping/orders/${orderId}/packages`);
    return response.data;
  },

  /**
   * Crea un nuevo paquete vacío para un pedido
   */
  createPackage: async (orderId: number, data: CreatePackageRequest): Promise<{
    success: boolean;
    package_id: number;
    message: string;
  }> => {
    const response = await api.post(`/shipping/orders/${orderId}/packages`, data);
    return response.data;
  },

  /**
   * Actualiza un paquete (tamaño, peso)
   */
  updatePackage: async (orderId: number, packageId: number, data: UpdatePackageRequest): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.put(`/shipping/orders/${orderId}/packages/${packageId}`, data);
    return response.data;
  },

  /**
   * Elimina un paquete y libera sus productos
   */
  deletePackage: async (orderId: number, packageId: number): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.delete(`/shipping/orders/${orderId}/packages/${packageId}`);
    return response.data;
  },

  /**
   * Añade un producto a un paquete
   */
  addItemToPackage: async (orderId: number, packageId: number, item: AddPackageItemRequest): Promise<{
    success: boolean;
    item_id: number;
    message: string;
  }> => {
    const response = await api.post(`/shipping/orders/${orderId}/packages/${packageId}/items`, item);
    return response.data;
  },

  /**
   * Elimina un producto de un paquete
   */
  removeItemFromPackage: async (orderId: number, packageId: number, itemId: number): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.delete(`/shipping/orders/${orderId}/packages/${packageId}/items/${itemId}`);
    return response.data;
  },

  /**
   * Desempaqueta todos los productos de un paquete
   */
  unpackPackage: async (orderId: number, packageId: number): Promise<{
    success: boolean;
    removed_items: number;
    message: string;
  }> => {
    const response = await api.post(`/shipping/orders/${orderId}/packages/${packageId}/unpack`);
    return response.data;
  },

  /**
   * Cierra un paquete añadiendo múltiples productos
   */
  closePackage: async (orderId: number, packageId: number, items: AddPackageItemRequest[]): Promise<{
    success: boolean;
    added_items: number;
    message: string;
  }> => {
    const response = await api.post(`/shipping/orders/${orderId}/packages/${packageId}/close`, items);
    return response.data;
  },

  // =========================================================================
  // TRACKING EVENTS
  // =========================================================================

  /**
   * Obtiene los eventos de tracking de un envío
   */
  getTrackingEvents: async (shipmentId: number): Promise<TrackingEventsResponse> => {
    const response = await api.get(`/shipping/shipments/${shipmentId}/tracking-events`);
    return response.data;
  },

  /**
   * Añade un evento de tracking a un envío
   */
  addTrackingEvent: async (shipmentId: number, event: AddTrackingEventRequest): Promise<TrackingEventData> => {
    const response = await api.post(`/shipping/shipments/${shipmentId}/tracking-events`, event);
    return response.data;
  },

  // =========================================================================
  // SHIPPING COSTS
  // =========================================================================

  /**
   * Lista costes de envío con filtros opcionales
   */
  getShippingCosts: async (params?: {
    carrier_code?: string;
    country_code?: string;
    active_only?: boolean;
  }): Promise<ShippingCostListResponse> => {
    const response = await api.get('/shipping/shipping-costs', { params });
    return response.data;
  },

  /**
   * Obtiene un coste de envío por ID
   */
  getShippingCost: async (costId: number): Promise<ShippingCostData> => {
    const response = await api.get(`/shipping/shipping-costs/${costId}`);
    return response.data;
  },

  /**
   * Crea un nuevo coste de envío
   */
  createShippingCost: async (cost: ShippingCostRequest): Promise<ShippingCostData> => {
    const response = await api.post('/shipping/shipping-costs', cost);
    return response.data;
  },

  /**
   * Actualiza un coste de envío
   */
  updateShippingCost: async (costId: number, cost: ShippingCostRequest): Promise<ShippingCostData> => {
    const response = await api.put(`/shipping/shipping-costs/${costId}`, cost);
    return response.data;
  },

  /**
   * Elimina un coste de envío
   */
  deleteShippingCost: async (costId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/shipping/shipping-costs/${costId}`);
    return response.data;
  },

  /**
   * Crea múltiples costes de envío
   */
  bulkCreateShippingCosts: async (costs: ShippingCostRequest[]): Promise<{
    success: boolean;
    created: number;
    total: number;
  }> => {
    const response = await api.post('/shipping/shipping-costs/bulk', { costs });
    return response.data;
  },

  /**
   * Calcula el coste de envío basado en la configuración
   */
  calculateShippingCost: async (params: {
    carrier_code: string;
    country_code: string;
    weight: number;
  }): Promise<{
    carrier_code: string;
    country_code: string;
    weight: number;
    cost: number | null;
    found: boolean;
  }> => {
    const response = await api.post('/shipping/shipping-costs/calculate', params);
    return response.data;
  },

  // =========================================================================
  // WEIGHT CONFIG
  // =========================================================================

  /**
   * Obtiene la configuración de cálculo de peso
   */
  getWeightConfig: async (): Promise<WeightConfigResponse> => {
    const response = await api.get('/shipping/weight-config');
    return response.data;
  },

  /**
   * Guarda la configuración de cálculo de peso
   */
  saveWeightConfig: async (config: WeightConfigRequest): Promise<WeightConfigResponse> => {
    const response = await api.post('/shipping/weight-config', config);
    return response.data;
  },

  // =========================================================================
  // PRINT CONFIG
  // =========================================================================

  /**
   * Obtiene la configuración de impresión de etiquetas
   */
  getPrintConfig: async (): Promise<PrintConfigResponse> => {
    const response = await api.get('/shipping/print-config');
    return response.data;
  },

  /**
   * Guarda la configuración de impresión de etiquetas
   */
  savePrintConfig: async (config: PrintConfigRequest): Promise<PrintConfigResponse> => {
    const response = await api.post('/shipping/print-config', config);
    return response.data;
  },

  /**
   * Obtiene los tamaños de papel disponibles
   */
  getPaperSizes: async (): Promise<{ sizes: PaperSize[] }> => {
    const response = await api.get('/shipping/print-config/paper-sizes');
    return response.data;
  },
};

// Interfaces para configuración de pesos
export interface WeightConfigResponse {
  mode: 'fixed' | 'calculated';
  fixed_weight: number;
  updated_at?: string;
}

export interface WeightConfigRequest {
  mode: 'fixed' | 'calculated';
  fixed_weight: number;
}

// Interfaces para configuración de impresión
export interface PrintConfigResponse {
  paper_size: string;
  copies: number;
  auto_print: boolean;
  updated_at?: string;
}

export interface PrintConfigRequest {
  paper_size: string;
  copies: number;
  auto_print: boolean;
}

export interface PaperSize {
  code: string;
  width: number;
  height: number;
  label: string;
}

// Interfaces para gestión de paquetes
export interface PackageSize {
  code: string;
  label: string;
  default_weight: number;
}

export interface PackageItem {
  id?: number;
  package_id: number;
  order_id: number;
  product_id: number;
  product_name: string;
  product_reference?: string;
  quantity: number;
  price: number;
  image_url?: string;
  created_at?: string;
}

export interface ShipmentPackage {
  id?: number;
  order_id: number;
  size_code: string;
  size_label: string;
  weight: number;
  position: number;
  created_at?: string;
  items: PackageItem[];
}

export interface PackagesResponse {
  order_id: number;
  packages: ShipmentPackage[];
  total_weight: number;
  total_items: number;
}

export interface CreatePackageRequest {
  size_code?: string;
  size_label?: string;
  weight?: number;
}

export interface UpdatePackageRequest {
  size_code?: string;
  size_label?: string;
  weight?: number;
}

export interface AddPackageItemRequest {
  product_id: number;
  product_name: string;
  product_reference?: string;
  quantity?: number;
  price?: number;
  image_url?: string;
}

// ============================================================================
// TRACKING EVENTS TYPES
// ============================================================================

export interface TrackingEventData {
  id: number;
  shipment_id: number;
  status: string;
  status_date?: string;
  location?: string;
  description?: string;
  created_at?: string;
}

export interface TrackingEventsResponse {
  shipment_id: number;
  events: TrackingEventData[];
}

export interface AddTrackingEventRequest {
  status: string;
  status_date?: string;
  location?: string;
  description?: string;
}

// ============================================================================
// SHIPPING COSTS TYPES
// ============================================================================

export interface ShippingCostData {
  id: number;
  carrier_code: string;
  country_code: string;
  weight_from: number;
  weight_to: number;
  cost: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ShippingCostListResponse {
  costs: ShippingCostData[];
  total: number;
}

export interface ShippingCostRequest {
  carrier_code: string;
  country_code: string;
  weight_from: number;
  weight_to: number;
  cost: number;
  is_active?: boolean;
}

// ============================================================================
// SHIPPING RULES V2 - Estilo Outvio
// ============================================================================

export interface RuleCondition {
  type: string;
  value: any;
  operator: 'AND' | 'OR';
}

export interface RuleAction {
  type: string;
  carrier_code?: string;
  service_type?: string;
  value?: any;
}

export interface ShippingRuleV2 {
  id: number;
  name: string;
  priority: number;
  is_active: boolean;
  conditions: RuleCondition[];
  actions: RuleAction[];
  created_at?: string;
  updated_at?: string;
}

export interface ShippingRuleV2Request {
  name: string;
  priority?: number;
  is_active?: boolean;
  conditions: RuleCondition[];
  actions: RuleAction[];
}

export interface ConditionTypeOption {
  value: string;
  label: string;
}

export interface ConditionTypeInfo {
  type: string;
  label: string;
  value_type: 'text' | 'number' | 'boolean' | 'select' | 'range';
  placeholder?: string | string[];
  options?: ConditionTypeOption[];
}

export interface CarrierService {
  code: string;
  name: string;
}

export interface CarrierInfo {
  name: string;
  services: CarrierService[];
}

export interface TestRuleResult {
  matched: boolean;
  rule_name?: string;
  rule_id?: number;
  carrier_code?: string;
  service_type?: string;
  all_rules_tested: number;
  order_reference?: string;
  order_data?: any;
}

export const shippingRulesV2 = {
  /**
   * Lista todas las reglas de envío v2
   */
  getRules: async (activeOnly: boolean = false): Promise<ShippingRuleV2[]> => {
    const response = await api.get('/shipping/rules-v2', {
      params: { active_only: activeOnly }
    });
    return response.data;
  },

  /**
   * Obtiene una regla por ID
   */
  getRule: async (ruleId: number): Promise<ShippingRuleV2> => {
    const response = await api.get(`/shipping/rules-v2/${ruleId}`);
    return response.data;
  },

  /**
   * Crea una nueva regla
   */
  createRule: async (data: ShippingRuleV2Request): Promise<ShippingRuleV2> => {
    const response = await api.post('/shipping/rules-v2', data);
    return response.data;
  },

  /**
   * Actualiza una regla existente
   */
  updateRule: async (ruleId: number, data: ShippingRuleV2Request): Promise<ShippingRuleV2> => {
    const response = await api.put(`/shipping/rules-v2/${ruleId}`, data);
    return response.data;
  },

  /**
   * Activa o desactiva una regla
   */
  toggleRule: async (ruleId: number, isActive: boolean): Promise<{ success: boolean; is_active: boolean }> => {
    const response = await api.patch(`/shipping/rules-v2/${ruleId}/toggle`, null, {
      params: { is_active: isActive }
    });
    return response.data;
  },

  /**
   * Elimina una regla
   */
  deleteRule: async (ruleId: number): Promise<{ success: boolean }> => {
    const response = await api.delete(`/shipping/rules-v2/${ruleId}`);
    return response.data;
  },

  /**
   * Reordena las reglas
   */
  reorderRules: async (ruleIds: number[]): Promise<{ success: boolean }> => {
    const response = await api.post('/shipping/rules-v2/reorder', { rule_ids: ruleIds });
    return response.data;
  },

  /**
   * Obtiene los tipos de condiciones disponibles
   */
  getConditionTypes: async (): Promise<{ condition_types: ConditionTypeInfo[] }> => {
    const response = await api.get('/shipping/rules-v2/condition-types');
    return response.data;
  },

  /**
   * Obtiene los transportistas y servicios disponibles
   */
  getCarriers: async (): Promise<{ carriers: Record<string, CarrierInfo> }> => {
    const response = await api.get('/shipping/rules-v2/carriers');
    return response.data;
  },

  /**
   * Prueba las reglas con datos simulados
   */
  testRules: async (data: {
    country_code?: string;
    postal_code?: string;
    carrier_name?: string;
    weight?: number;
    total?: number;
    is_cod?: boolean;
    items_count?: number;
  }): Promise<TestRuleResult> => {
    const response = await api.post('/shipping/rules-v2/test', data);
    return response.data;
  },

  /**
   * Prueba las reglas con un pedido real
   */
  testRulesWithOrder: async (orderId: number): Promise<TestRuleResult> => {
    const response = await api.post(`/shipping/rules-v2/test-with-order/${orderId}`);
    return response.data;
  },

  /**
   * Migra las reglas v1 a v2
   */
  migrateFromV1: async (): Promise<{ success: boolean; migrated: number }> => {
    const response = await api.post('/shipping/rules-v2/migrate-from-v1');
    return response.data;
  },
};

// =========================================================================
// eBay Marketplace API
// =========================================================================

export const ebayApi = {
  // Dashboard
  getDashboard: async () => {
    const response = await api.get('/ebay/dashboard');
    return response.data;
  },
  // Config
  getConfig: async () => {
    const response = await api.get('/ebay/config');
    return response.data;
  },
  updateConfig: async (key: string, value: string) => {
    const response = await api.post('/ebay/config', { key, value });
    return response.data;
  },
  // Selection
  calculateSelection: async (maxSlots?: number) => {
    const response = await api.post(`/ebay/selection/calculate?max_slots=${maxSlots || 1468}`);
    return response.data;
  },
  getSelectionSummary: async () => {
    const response = await api.get('/ebay/selection/summary');
    return response.data;
  },
  publishSelection: async (dryRun: boolean = true) => {
    const response = await api.post('/ebay/selection/publish', { dry_run: dryRun });
    return response.data;
  },
  // Sync
  syncStock: async () => {
    const response = await api.post('/ebay/sync/stock');
    return response.data;
  },
  syncPrices: async () => {
    const response = await api.post('/ebay/sync/prices');
    return response.data;
  },
  getSyncStatus: async () => {
    const response = await api.get('/ebay/sync/status');
    return response.data;
  },
  // Listings
  getListings: async (params?: { status?: string; limit?: number; offset?: number }) => {
    const response = await api.get('/ebay/listings', { params });
    return response.data;
  },
  getPreview: async (sku: string) => {
    const response = await api.get(`/ebay/listings/${sku}/preview`);
    return response.data;
  },
  publishProduct: async (sku: string) => {
    const response = await api.post(`/ebay/publish/${sku}`);
    return response.data;
  },
  // AI Content
  getAiStatus: async () => {
    const response = await api.get('/ebay/ai/status');
    return response.data;
  },
  getAiContent: async (limit?: number, offset?: number) => {
    const response = await api.get('/ebay/ai/content', { params: { limit: limit || 500, offset: offset || 0 } });
    return response.data;
  },
  getAiContentDetail: async (sku: string) => {
    const response = await api.get(`/ebay/ai/content/${sku}`);
    return response.data;
  },
  generateAi: async (sku: string, model?: string) => {
    const response = await api.post(`/ebay/ai/generate/${sku}`, { model: model || 'gemini-2.5-flash' });
    return response.data;
  },
  generateAllAi: async (model?: string) => {
    const response = await api.post('/ebay/ai/generate-all', { model: model || 'gemini-2.5-flash' });
    return response.data;
  },
  deleteAiContent: async (sku: string) => {
    const response = await api.delete(`/ebay/ai/content/${sku}`);
    return response.data;
  },
  // Sub-listings
  calculateAllSubListings: async () => {
    const response = await api.post('/ebay/sub-listings/calculate');
    return response.data;
  },
  calculateSubListings: async (sku: string) => {
    const response = await api.post(`/ebay/sub-listings/calculate/${sku}`);
    return response.data;
  },
  getSubListings: async () => {
    const response = await api.get('/ebay/sub-listings');
    return response.data;
  },
  getSubListingsForSku: async (sku: string) => {
    const response = await api.get(`/ebay/sub-listings/${sku}`);
    return response.data;
  },
  // Promoted Listings
  promotedSetup: async () => {
    const response = await api.post('/ebay/promoted/setup');
    return response.data;
  },
  promotedStatus: async () => {
    const response = await api.get('/ebay/promoted/status');
    return response.data;
  },
  promotedPause: async () => {
    const response = await api.post('/ebay/promoted/pause');
    return response.data;
  },
  promotedResume: async () => {
    const response = await api.post('/ebay/promoted/resume');
    return response.data;
  },
  promotedSync: async () => {
    const response = await api.post('/ebay/promoted/sync');
    return response.data;
  },
};

// ============================================================================
// MANUAL INVOICES API
// ============================================================================

export interface ManualInvoiceCustomer {
  code: string;
  name: string;
  nif?: string;
  email?: string;
  phone?: string;
  company?: string;
  address: string;
  city: string;
  postal_code: string;
  province?: string;
  country: string;
  tariff: number;
  is_intracom: boolean;
  payment_method?: string;
}

export interface ManualInvoiceProduct {
  reference: string;
  name: string;
  price_net: number;
  cost: number;
  iva_type: number;
  stock: number;
  weight: number;
  family?: string;
}

export interface ShippingOption {
  carrier_id: number;
  carrier_name: string;
  price_net: number;
  price_with_tax: number;
  zone_name: string;
  same_day_available: boolean;
  available: boolean;
  inactive_until?: string;
}

export interface ManualInvoiceCreateRequest {
  customer_code: string;
  reference?: string;
  payment_method: string;
  warehouse: string;
  items: {
    reference: string;
    description: string;
    quantity: number;
    price_net: number;
    iva_type: number;
    weight: number;
    cost: number;
  }[];
  shipping_cost_net: number;
  shipping_iva_type: number;
  carrier_code?: string;
  custom_shipping_address?: {
    name: string;
    street: string;
    city: string;
    postal_code: string;
    province?: string;
    country: string;
    phone?: string;
    email?: string;
  };
}

export const manualInvoices = {
  searchCustomers: async (query: string, limit: number = 10) => {
    const response = await api.get('/manual-invoices/customers/search', { params: { q: query, limit } });
    return response.data as ManualInvoiceCustomer[];
  },
  getCustomer: async (code: string) => {
    const response = await api.get(`/manual-invoices/customers/${code}`);
    return response.data as ManualInvoiceCustomer;
  },
  searchProducts: async (query: string, tariff: number = 1, limit: number = 20) => {
    const response = await api.get('/manual-invoices/products/search', { params: { q: query, tariff, limit } });
    return response.data as ManualInvoiceProduct[];
  },
  getPaymentMethods: async () => {
    const response = await api.get('/manual-invoices/payment-methods');
    return response.data as PaymentMethod[];
  },
  calculateShipping: async (params: { postal_code: string; country: string; weight: number; carrier_code?: string }) => {
    const response = await api.post('/manual-invoices/calculate-shipping', params);
    return response.data as ShippingOption[];
  },
  getNextNumber: async () => {
    const response = await api.get('/manual-invoices/next-number');
    return response.data as { number: string; reference: string };
  },
  create: async (invoice: ManualInvoiceCreateRequest) => {
    const response = await api.post('/manual-invoices/create', invoice);
    return response.data;
  },
};

// ============================================================================
// STOCK ALERTS / WAITLIST API
// ============================================================================

export interface WaitlistItem {
  id_product: number
  id_product_attribute: number
  reference: string | null
  name: string | null
  stock: number | null
  out_of_stock: number | null
  subscribers: number
  cover_image_id: number | null
  status: 'waiting' | 'ready_to_notify' | 'under_reserve' | 'orphan'
}

export interface WaitlistSummary {
  total_alerts: number
  total_products: number
  total_customers: number
  ready_to_notify: number
  waiting: number
  under_reserve: number
  orphans: number
  top_demand: Array<{ id_product: number; reference: string; name: string; subscribers: number }>
}

export interface WaitlistSubscriber {
  id_customer: number
  email: string
  id_lang: number
  id_shop: number
}

export interface WaitlistProductSearchResult {
  id_product: number
  reference: string
  name: string
  stock: number | null
  out_of_stock: number | null
  cover_image_id: number | null
}

export interface WaitlistAuditEntry {
  id: number
  action: string
  customer_email: string | null
  id_product: number | null
  id_product_attribute: number | null
  reference: string | null
  performed_by: string
  performed_at: string
  reason: string | null
  result: any
}

export const stockAlertsApi = {
  summary: async (): Promise<WaitlistSummary> => {
    const r = await api.get('/stock-alerts/summary')
    return r.data
  },

  listPending: async (params: {
    q?: string
    status?: string
    page?: number
    page_size?: number
  } = {}): Promise<{ items: WaitlistItem[]; total: number; page: number; page_size: number }> => {
    const r = await api.get('/stock-alerts/pending', { params })
    return r.data
  },

  getSubscribers: async (id_product: number, id_product_attribute = 0): Promise<{ items: WaitlistSubscriber[] }> => {
    const r = await api.get(`/stock-alerts/product/${id_product}/subscribers`, {
      params: { id_product_attribute },
    })
    return r.data
  },

  searchProducts: async (q: string): Promise<{ items: WaitlistProductSearchResult[] }> => {
    const r = await api.get('/stock-alerts/search-products', { params: { q } })
    return r.data
  },

  checkStock: async (id_product: number, id_product_attribute = 0) => {
    const r = await api.get('/stock-alerts/check-stock', {
      params: { id_product, id_product_attribute },
    })
    return r.data as {
      found: boolean
      has_stock: boolean
      quantity: number
      out_of_stock: number
      active: number
      reference: string
      name: string
    }
  },

  addManual: async (payload: {
    email: string
    id_product: number
    id_lang?: number
    id_product_attribute?: number
    reason?: string
    notify_immediately?: boolean
  }): Promise<{ created: boolean; notified?: number; reason?: string; error?: string }> => {
    const r = await api.post('/stock-alerts/manual', payload)
    return r.data
  },

  deleteAlert: async (params: {
    email: string
    id_product: number
    id_product_attribute?: number
    reason?: string
  }) => {
    const r = await api.delete('/stock-alerts/alert', { params })
    return r.data as { deleted: number }
  },

  notifyNow: async (id_product: number, reason?: string) => {
    const r = await api.post(`/stock-alerts/notify/${id_product}`, { reason })
    return r.data as { notified: number; errors?: number; cleaned?: number }
  },

  cleanupOrphans: async () => {
    const r = await api.post('/stock-alerts/cleanup-orphans')
    return r.data as { deleted: number }
  },

  audit: async (limit = 100, offset = 0): Promise<{ items: WaitlistAuditEntry[]; total: number }> => {
    const r = await api.get('/stock-alerts/audit', { params: { limit, offset } })
    return r.data
  },
}

// =============================================================================
// GOOGLE MERCHANT CENTER (own sync replacing gmerchantcenterpro)
// =============================================================================

export interface GMCStatus {
  connected: boolean;
  configured: boolean;
  merchant_id?: string;
  account_level_issues?: any[];
  destinations?: Array<{
    destination: string;
    country: string;
    active: number;
    pending: number;
    disapproved: number;
    expiring: number;
  }>;
  error?: string;
}

export interface GMCConfig {
  filter_active: number;
  filter_min_margin_percent: number;
  filter_require_factusol_stock: number;
  filter_min_clean_image_id: number;
  enabled_countries: string[];
  country_to_lang: Record<string, string>;
  country_to_currency: Record<string, string>;
  country_vat_percent: Record<string, number>;
  price_band_breaks: number[];
  margin_tier_breaks: number[];
  adscore_bucket_breaks: number[];
  mode: 'shadow' | 'canonical';
  shadow_offer_id_prefix: string;
  canonical_offer_id_prefix: string;
  min_parity_pct_for_cutover: number;
  last_full_sync_at: string | null;
  last_delta_sync_at: string | null;
  last_statuses_check_at: string | null;
  [key: string]: any;
}

export interface GMCEligibleResponse {
  total: number;
  limit: number;
  offset: number;
  items: Array<{
    id_product: number;
    reference: string;
    id_category_default: number;
    price_ex_vat: number;
    margin_percent: number;
    ad_score: number;
    stock_actual: number;
    clean_idi: number;
  }>;
  by_family: Record<string, number>;
}

export interface GMCSyncLogItem {
  id: number;
  run_id: string;
  started_at: string;
  ended_at: string | null;
  mode: string;
  scope: string;
  processed: number;
  ok: number;
  errors: number;
  stats_json: any;
}

export const googleMerchantApi = {
  // Status & Config
  getStatus: async (): Promise<GMCStatus> => {
    const r = await api.get('/google-merchant/status');
    return r.data;
  },

  getConfig: async (): Promise<{ config: GMCConfig; is_configured: boolean; merchant_id: string | null }> => {
    const r = await api.get('/google-merchant/config');
    return r.data;
  },

  updateConfig: async (updates: Partial<GMCConfig>): Promise<{ ok: boolean; results: Record<string, boolean>; config: GMCConfig }> => {
    const r = await api.post('/google-merchant/config', { updates });
    return r.data;
  },

  // Products
  listEligible: async (limit = 100, offset = 0): Promise<GMCEligibleResponse> => {
    const r = await api.get('/google-merchant/products/eligible', { params: { limit, offset } });
    return r.data;
  },

  previewProduct: async (idProduct: number, country = 'ES', lang = 'es', mode: 'shadow' | 'canonical' = 'shadow') => {
    const r = await api.get(`/google-merchant/products/${idProduct}/preview`, {
      params: { country, lang, mode },
    });
    return r.data;
  },

  // Sync
  syncDryRun: async (params: { mode?: 'shadow' | 'canonical'; scope?: 'full' | 'delta'; limit?: number; concurrency?: number }) => {
    const r = await api.post('/google-merchant/sync/dry-run', {
      mode: params.mode ?? 'shadow',
      scope: params.scope ?? 'full',
      limit: params.limit ?? null,
      concurrency: params.concurrency ?? 20,
    });
    return r.data;
  },

  syncRun: async (params: { mode?: 'shadow' | 'canonical'; scope?: 'full' | 'delta'; limit?: number; concurrency?: number }) => {
    const r = await api.post('/google-merchant/sync/run', {
      mode: params.mode ?? 'shadow',
      scope: params.scope ?? 'full',
      limit: params.limit ?? null,
      concurrency: params.concurrency ?? 20,
    });
    return r.data;
  },

  syncLog: async (limit = 20): Promise<{ items: GMCSyncLogItem[] }> => {
    const r = await api.get('/google-merchant/sync/log', { params: { limit } });
    return r.data;
  },

  // Statuses & diagnostics
  productStatuses: async (maxItems = 500) => {
    const r = await api.get('/google-merchant/productstatuses', { params: { max_items: maxItems } });
    return r.data;
  },

  diagnostics: async () => {
    const r = await api.get('/google-merchant/diagnostics');
    return r.data;
  },

  // Category map
  getCategoryMap: async () => {
    const r = await api.get('/google-merchant/category-map');
    return r.data as {
      mapping: Array<{ id_category: number; google_category_id: number | null; google_product_type: string | null; notes: string | null; category_name: string | null }>;
      unmapped: Array<{ id_category: number; name: string; product_count: number }>;
    };
  },

  upsertCategoryMap: async (row: { id_category: number; google_category_id?: number | null; google_product_type?: string | null; notes?: string | null }) => {
    const r = await api.post('/google-merchant/category-map', row);
    return r.data;
  },

  // Cutover
  cutover: async (confirm: boolean) => {
    const r = await api.post('/google-merchant/cutover', { confirm });
    return r.data;
  },
};

// ============================================================================
// PORTAL DE PROVEEDORES — gestión interna (staff)
// ============================================================================
// Pedidos de compra (RFQ) — staff
export const supplierPurchaseOrdersApi = {
  list: async (params: { supplier_id?: number; status?: string; page?: number } = {}) => {
    const r = await api.get('/supplier-portal-admin/purchase-orders', { params });
    return r.data;
  },
  get: async (id: number) => {
    const r = await api.get(`/supplier-portal-admin/purchase-orders/${id}`);
    return r.data;
  },
  seed: async (supplierId: number) => {
    const r = await api.get(`/supplier-portal-admin/purchase-orders/seed/${supplierId}`);
    return r.data;
  },
  create: async (data: { supplier_id: number; notes?: string; created_by?: number; lines: any[] }) => {
    const r = await api.post('/supplier-portal-admin/purchase-orders', data);
    return r.data;
  },
  createMulti: async (data: { supplier_ids: number[]; notes?: string; created_by?: number; send?: boolean; lines: any[] }) => {
    const r = await api.post('/supplier-portal-admin/purchase-orders/multi', data);
    return r.data;
  },
  send: async (id: number) => {
    const r = await api.post(`/supplier-portal-admin/purchase-orders/${id}/send`, {});
    return r.data;
  },
  markOrdered: async (id: number, factusol_order_number?: string) => {
    const r = await api.post(`/supplier-portal-admin/purchase-orders/${id}/mark-ordered`, { factusol_order_number });
    return r.data;
  },
  cancel: async (id: number) => {
    const r = await api.post(`/supplier-portal-admin/purchase-orders/${id}/cancel`, {});
    return r.data;
  },
  exportUrl: (id: number) => `/api/supplier-portal-admin/purchase-orders/${id}/export`,
  // Comparativa multi-proveedor
  listInquiryGroups: async () => {
    const r = await api.get('/supplier-portal-admin/inquiry-groups');
    return r.data;
  },
  getInquiryComparison: async (groupId: string) => {
    const r = await api.get(`/supplier-portal-admin/inquiry-groups/${groupId}`);
    return r.data;
  },
};

export const supplierPortalAdminApi = {
  // Tipo de cambio USD/EUR (BCE)
  getFx: async () => {
    const r = await api.get('/supplier-catalogs/fx/info');
    return r.data;
  },
  refreshFx: async () => {
    const r = await api.post('/supplier-catalogs/fx/refresh');
    return r.data;
  },
  getFxHistory: async (limit = 90) => {
    const r = await api.get('/supplier-catalogs/fx/history', { params: { limit } });
    return r.data;
  },
  // Alta de proveedor asiático (crea suppliers + cuenta en un paso)
  createPortalSupplier: async (data: {
    code: string; name: string; username: string; password?: string;
    company_name?: string; contact_name?: string; contact_email: string;
    contact_whatsapp?: string; quote_currency?: string;
  }) => {
    const r = await api.post('/supplier-catalogs/portal-suppliers', data);
    return r.data;
  },
  // Cuenta de acceso al portal
  getAccount: async (supplierId: number) => {
    const r = await api.get(`/supplier-catalogs/suppliers/${supplierId}/account`);
    return r.data;
  },
  createAccount: async (supplierId: number, data: any) => {
    const r = await api.post(`/supplier-catalogs/suppliers/${supplierId}/account`, data);
    return r.data;
  },
  updateAccount: async (supplierId: number, data: any) => {
    const r = await api.put(`/supplier-catalogs/suppliers/${supplierId}/account`, data);
    return r.data;
  },
  // Familias asignadas a un proveedor (admin, todas)
  supplierFamilies: async (supplierId: number) => {
    const r = await api.get(`/supplier-portal-admin/suppliers/${supplierId}/families`);
    return r.data;
  },
  // Desasignar productos (por familia y/o lista de ids)
  unassignProducts: async (supplierId: number, data: { family?: string; product_ids?: number[] }) => {
    const r = await api.post(`/supplier-portal-admin/suppliers/${supplierId}/unassign`, data);
    return r.data;
  },
  // Asignación de productos (Factusol -> supplier_products)
  assignProducts: async (
    supplierId: number,
    data: { codarts?: string[]; familia?: string; familias?: string[]; with_images?: boolean }
  ) => {
    const r = await api.post(`/supplier-catalogs/suppliers/${supplierId}/assign-products`, data);
    return r.data;
  },
  // Familias de Factusol (para el selector de asignación)
  getFamilies: async (): Promise<{ code: string; name: string }[]> => {
    const r = await api.get('/reports/supplier-orders/filters');
    return r.data?.families || [];
  },
  // Búsqueda de artículos en Factusol (CODART/REFART/DESART)
  searchFactusolProducts: async (search: string, limit = 25) => {
    const r = await api.get('/factusol/products', { params: { search, limit } });
    return r.data?.products || [];
  },
  // Cola de revisión de cotizaciones
  listSubmissions: async (params: { status?: string; supplier_id?: number; page?: number; page_size?: number }) => {
    const r = await api.get('/supplier-catalogs/quotes/submissions', { params });
    return r.data;
  },
  approveSubmission: async (id: number, data: { fx_rate?: number; reviewed_by?: number }) => {
    const r = await api.post(`/supplier-catalogs/quotes/submissions/${id}/approve`, data);
    return r.data;
  },
  rejectSubmission: async (id: number, data: { note?: string; reviewed_by?: number }) => {
    const r = await api.post(`/supplier-catalogs/quotes/submissions/${id}/reject`, data);
    return r.data;
  },
  bulkApprove: async (data: { ids: number[]; fx_rate?: number; reviewed_by?: number }) => {
    const r = await api.post('/supplier-catalogs/quotes/submissions/bulk-approve', data);
    return r.data;
  },
  // Propuestas de productos del proveedor
  listProposals: async (params: { status?: string; supplier_id?: number } = {}) => {
    const r = await api.get('/supplier-portal-admin/product-proposals', { params });
    return r.data;
  },
  acceptProposal: async (id: number, data: { fx_rate?: number; reviewed_by?: number }) => {
    const r = await api.post(`/supplier-portal-admin/product-proposals/${id}/accept`, data);
    return r.data;
  },
  rejectProposal: async (id: number, data: { note?: string; reviewed_by?: number }) => {
    const r = await api.post(`/supplier-portal-admin/product-proposals/${id}/reject`, data);
    return r.data;
  },
  proposalImageUrl: (id: number) => `/api/supplier-portal-admin/product-proposals/${id}/image`,
  // Mensajería staff <-> proveedor
  listMessages: async (params: { supplier_id: number; product_id?: number; order_id?: number; proposal_id?: number }) => {
    const r = await api.get('/supplier-portal-admin/messages', { params });
    return r.data;
  },
  postMessage: async (form: FormData) => {
    const r = await api.post('/supplier-portal-admin/messages', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  },
  attachmentUrl: (id: number) => `/api/supplier-portal-admin/attachments/${id}`,
  listConversations: async () => {
    const r = await api.get('/supplier-portal-admin/conversations');
    return r.data;
  },
  unreadCount: async (supplier_id?: number) => {
    const r = await api.get('/supplier-portal-admin/unread-count', { params: { supplier_id } });
    return r.data;
  },
  // Ranking de rendimiento
  getRanking: async () => {
    const r = await api.get('/supplier-portal-admin/metrics/ranking');
    return r.data;
  },
  // Evolución de precio por producto
  searchPriceEvolution: async (q: string) => {
    const r = await api.get('/supplier-portal-admin/price-evolution/search', { params: { q } });
    return r.data;
  },
  getPriceEvolution: async (reference: string) => {
    const r = await api.get('/supplier-portal-admin/price-evolution', { params: { reference } });
    return r.data;
  },
  // Documentos de tarifa (formato propio del proveedor)
  listDocuments: async (supplier_id: number) => {
    const r = await api.get('/supplier-portal-admin/documents', { params: { supplier_id } });
    return r.data;
  },
  uploadDocument: async (form: FormData) => {
    const r = await api.post('/supplier-portal-admin/documents', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  },
  documentDownloadUrl: (id: number) => `/api/supplier-portal-admin/documents/${id}/download`,
  deleteDocument: async (id: number) => {
    const r = await api.delete(`/supplier-portal-admin/documents/${id}`);
    return r.data;
  },
};

// ============================================================================
// B2B Lots / Outlet Pro
// ============================================================================
export interface B2BUnit {
  id: number;
  lot_id: number;
  unit_reference: string;
  sticker_id: string | null;
  model: string | null;
  specs: string | null;
  keyboard_layout: string | null;
  images_count: number;
  ps_product_id: number | null;
  unit_cost: number;
  unit_price: number;
  defects: string | null;
  charger_included: number | null;
  warranty_months: number | null;
  status: string;
  sold_order_id: number | null;
  sold_at: string | null;
}

export interface B2BLot {
  id: number;
  lot_code: string;
  name: string;
  model: string | null;
  specs: string | null;
  factusol_codart: string | null;
  ps_category_id: number | null;
  purchase_total_cost: number;
  units_total: number;
  units_sold: number;
  margin_eur: number;
  warranty_months: number;
  charger_included: number;
  keyboard_layout: string | null;
  status: string;
  created_at: string;
  units?: B2BUnit[];
  profit?: { units_total: number; units_sold: number; revenue: number; cost_sold: number; profit: number };
}

export const b2bLotsApi = {
  list: async (): Promise<{ success: boolean; lots: B2BLot[] }> => {
    const r = await api.get('/b2b/lots');
    return r.data;
  },
  get: async (lotId: number): Promise<{ success: boolean; lot: B2BLot }> => {
    const r = await api.get(`/b2b/lots/${lotId}`);
    return r.data;
  },
  create: async (data: {
    name: string; model?: string; specs?: string; lot_code?: string;
    purchase_total_cost?: number; margin_eur?: number; warranty_months?: number;
    charger_included?: boolean; keyboard_layout?: string;
  }): Promise<{ success: boolean; lot: B2BLot }> => {
    const r = await api.post('/b2b/lots', data);
    return r.data;
  },
  addUnit: async (lotId: number, data: {
    sticker_id: string; model?: string; specs?: string; keyboard_layout?: string; unit_cost?: number; unit_price?: number; defects?: string;
    charger_included?: boolean; warranty_months?: number;
  }, images: File[]): Promise<{ success: boolean; unit: B2BUnit }> => {
    const form = new FormData();
    form.append('sticker_id', data.sticker_id);
    if (data.model) form.append('model', data.model);
    if (data.specs) form.append('specs', data.specs);
    if (data.keyboard_layout) form.append('keyboard_layout', data.keyboard_layout);
    if (data.unit_cost != null) form.append('unit_cost', String(data.unit_cost));
    if (data.unit_price != null) form.append('unit_price', String(data.unit_price));
    if (data.defects) form.append('defects', data.defects);
    if (data.charger_included != null) form.append('charger_included', String(data.charger_included));
    if (data.warranty_months != null) form.append('warranty_months', String(data.warranty_months));
    (images || []).forEach((f) => form.append('images', f));
    const r = await api.post(`/b2b/lots/${lotId}/units`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  },
  updateUnit: async (unitId: number, data: {
    model?: string; specs?: string; keyboard_layout?: string; sticker_id?: string;
    unit_cost?: number; unit_price?: number; defects?: string;
    charger_included?: boolean; warranty_months?: number;
  }): Promise<{ success: boolean; unit: B2BUnit }> => {
    const r = await api.patch(`/b2b/units/${unitId}`, data);
    return r.data;
  },
  deleteUnit: async (unitId: number): Promise<{ success: boolean }> => {
    const r = await api.delete(`/b2b/units/${unitId}`);
    return r.data;
  },
  addUnitImages: async (unitId: number, images: File[]): Promise<{ success: boolean; unit: B2BUnit }> => {
    const form = new FormData();
    (images || []).forEach((f) => form.append('images', f));
    const r = await api.post(`/b2b/units/${unitId}/images`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  },
  deleteUnitImage: async (unitId: number, idx: number): Promise<{ success: boolean; unit: B2BUnit }> => {
    const r = await api.delete(`/b2b/units/${unitId}/images/${idx}`);
    return r.data;
  },
  // Devuelve un object URL (blob) con la foto en borrador; recuerda revocarlo al desmontar
  getUnitImage: async (unitId: number, idx: number): Promise<string> => {
    const r = await api.get(`/b2b/units/${unitId}/images/${idx}`, { responseType: 'blob' });
    return URL.createObjectURL(r.data as Blob);
  },
  publish: async (lotId: number): Promise<{ success: boolean; status: B2BPublishStatus }> => {
    const r = await api.post(`/b2b/lots/${lotId}/publish`);
    return r.data;
  },
  publishStatus: async (lotId: number): Promise<{ success: boolean; status: B2BPublishStatus }> => {
    const r = await api.get(`/b2b/lots/${lotId}/publish-status`);
    return r.data;
  },
};

export interface B2BPublishStatus {
  status: 'running' | 'done' | 'error' | null;
  done: number;
  total: number;
  error: string | null;
  lot_status: string;
}

// ============================================================================
// SEO · CANAL IA (ChatGPT, Perplexity, Gemini, Copilot, Claude)
// ============================================================================

export interface AiCitationItem {
  id_product: number;
  name: string;
  reference: string;
  mpn?: string;
  active?: boolean;
  bot_hits: number;
  ai_visits: number;
  ai_orders: number;
  ai_revenue: number;
  stock: number;
  price?: number;
  bots: string[];
  sources: string[];
  ai_readiness_score?: number | null;
}

export interface AiCitationsData {
  days: number;
  total_products: number;
  no_stock_count: number;
  totals?: { bot_hits: number; ai_visits: number; ai_orders: number; ai_revenue: number };
  items: AiCitationItem[];
}

export interface AiSummaryRow {
  yearweek: string;
  week_start: string;
  visits_logs: Record<string, number>;
  visits_connections: Record<string, number>;
  visits_total: number;
  orders: number;
  revenue: number;
  bot_hits_search: number;
  bot_hits_training: number;
}

export interface AiSummaryData {
  weeks: number;
  since: string;
  labels: Record<string, string>;
  totals_by_source: Record<string, number>;
  total_orders: number;
  total_revenue: number;
  rows: AiSummaryRow[];
}

export const seoAi = {
  getCitations: async (days: number = 30, limit: number = 100): Promise<{ success: boolean; data: AiCitationsData }> => {
    const response = await api.get('/seo-analytics/ai/citations', { params: { days, limit } });
    return response.data;
  },
  getSummary: async (weeks: number = 12): Promise<{ success: boolean; data: AiSummaryData }> => {
    const response = await api.get('/seo-analytics/ai/summary', { params: { weeks } });
    return response.data;
  },
};
